'use client';

import { useState, useEffect, useRef, useCallback, useMemo, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import toast from 'react-hot-toast';
import {
  Activity, BarChart3, Briefcase, Building2, Calendar, ChevronDown, ChevronUp,
  ChevronRight, ClipboardCheck, ClipboardList, Factory, FileText, GraduationCap, Hammer,
  HardHat, Heart, Mail, MapPin, MessageSquare, Phone, RefreshCw, Scale,
  ScanLine, Ship, Shield, ShoppingBag, Smartphone, Sparkles, SquareTerminal,
  Star, Stethoscope, Ticket, TrendingUp, Truck, UserCheck, Users,
  UtensilsCrossed, Video, Vote, Wrench, X, Zap,
  type LucideIcon,
} from 'lucide-react';
import { useNavigationRouter } from '@/hooks/useNavigationRouter';
import { useAuth } from '@/hooks/useAuth';
import { useNavigation } from '@/hooks/useNavigation';
import { usePathname } from 'next/navigation';
import ClientSelector from './ClientSelector';
import ThemeToggle from './ThemeToggle';
import NotificationIcon from './NotificationIcon';
import FavouritesBar from './FavouritesBar';
import { useFavourites } from '@/hooks/useFavourites';
import ManagedTasksModal, { type ManagedTasksMainTab } from './ManagedTasksModal';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { MessagesModal } from './messaging';
import UnifiedAssistantModal from './UnifiedAssistantModal';
import MyShellModal from './shell/MyShellModal';
import EmailMobileModal from '@/app/client-zone/modules/total-connect/components/EmailMobileModal';
import CalendarModal from './CalendarModal';
import { useEmailStore } from '@/app/client-zone/modules/total-connect/stores/emailStore';
import SupportModal, { type SupportModalHandle } from './SupportModal';
import { emitGuidedTourTrainingEvent, GUIDED_TOUR_DISMISS_REVEAL_EVENT } from '@/components/training/guided-tour';
import { CapacitorOTAUpdateDialog } from './CapacitorOTAUpdateDialog';

import BrandedLoadingSpinner from './shared/BrandedLoadingSpinner';
// TotalConnectRTC - The primary voice/video calling component with lifecycle integration
import TotalConnectRTC from './TotalConnectRTC';
import type { IncomingCall, PendingCallTarget, PendingGroupCall, SipCallData } from '@/components/call';
import {
  mergePushFlatSipData,
  type IncomingPushSipFlatFields,
} from '@/lib/incomingCallPushSipExtras';
import { useBadge } from '@/contexts/BadgeContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useModalState } from '@/contexts/ModalStateContext';
import { isTauriDesktop } from '@/lib/desktop/desktopUtils';
import { isAndroid, isAppShellRoute, isCapacitor, isIOS } from '@/lib/platformUtils';
import { useTotalConnectStandaloneWindow } from '@/hooks/useTotalConnectStandaloneWindow';
import {
  acknowledgeInstalledPwaDeploymentVersion,
  clearAcknowledgedInstalledPwaDeploymentVersion,
  fetchInstalledPwaDeploymentVersionInfo,
  readAcknowledgedInstalledPwaDeploymentVersion,
} from '@/lib/installedPwaDeployment';
import { isStandalonePwa } from '@/lib/pwaAppIdentity';
import { getContrastRatio, getRelativeLuminance, shouldUseLightText } from '@/lib/colorContrast';
import { useCapacitorOTAUpdate } from '@/hooks/useCapacitorOTAUpdate';
import { checkForInstalledPwaUpdateAndRefresh, clearInstalledPwaCaches, clearAllAppData, recoverServiceWorkerAfterCacheReset } from '@/lib/serviceWorkerRegistration';
import { frontendLogger } from '@/lib/frontendLoggingService';
import { useMasterSyncNetworkStatus } from '@/lib/masterSyncNetworkStatus';
import { useSyncStatus } from '@/contexts/SyncStatusContext';
import { wsClient } from '@/lib/websocket';
import { addDeclinedIncomingCallIds } from '@/components/total-connect-rtc/wsInboundEnvelope';
import { useSession } from '@/components/SessionProvider';
import { notify } from '@/lib/notifications';
import haptics from '@/lib/haptics';
import MasterSyncModal, { useMasterSyncModal } from '@/components/offline/MasterSyncModal';
import { permissionCache } from '@/lib/permissionCache';
import DiagnosticModal from '@/components/diagnostics/DiagnosticModal';
import ClearCacheModal from '@/components/ClearCacheModal';
import MyDevicesModal from '@/components/shared/MyDevicesModal';
import ProfileModal from '@/components/shared/ProfileModal';
import IamConsoleModal from '@/components/shared/IamConsoleModal';
import EmployeeSelfServiceModal from '@/components/shared/EmployeeSelfServiceModal';
import type { WorkforcePortalTab } from '@/app/client-zone/dashboard/components/dashboards/customer-portals/WorkforceSelfServicePortal';
import type { WorkforcePortalSection } from '@/app/client-zone/dashboard/components/dashboards/WorkforcePortalDashboard';
import { DEFAULT_APP_INSTALL_CTA, resolveAppInstallCta, type AppInstallCta } from '@/lib/appInstallCta';
import type { DemoSessionInfo } from '@/lib/demo/types';
import type { NavigationItem, NavigationResponse } from '@/lib/navigationService';
import { authService } from '@/lib/authService';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { emit } from '@/lib/typedEventBus';
import { useOptionalWorkItemOpener } from '@/hooks/useWorkItemOpener';
import { useOpenTeamMember } from '@/hooks/useOpenTeamMember';
import {
  clearOperationsDemoClientContext,
  fetchDemoSessionResponse,
  OPERATIONS_DEMO_EXIT_REQUESTED_EVENT,
  isOperationsDemoOverrideEnabled,
  isTotalFinanceRoute,
  reconcileOperationsDemoClientContext,
  SMART_TASK_DEMO_MODULE_KEY,
  shouldTrackOperationsDemoSession,
  TOTAL_FINANCE_DEMO_MODULE_KEY,
} from '@/lib/demo/clientContext';
import { SHELL_TOGGLE_BUTTON_CLASS, SHELL_TOGGLE_ICON_CLASS, SHELL_TOGGLE_REVEAL_BUTTON_CLASS } from '@/components/shared/shellToggleButtonStyles';

type MenuSectionKey = 'modules' | 'my-corner' | 'my-workspace' | 'session-app';

type MenuSectionVariant = 'desktop' | 'mobile';

type HeaderHapticPattern = 'menuToggle' | 'accordionToggle' | 'menuAction' | 'warning' | 'success';

function getMenuItemClasses(variant: MenuSectionVariant) {
  return {
    base: variant === 'desktop'
      ? 'flex items-center w-full px-4 py-2 theme-text-primary hover:bg-slate-100 hover:text-[#047cde] dark:hover:bg-slate-700/50 dark:hover:text-sky-300 text-left transition-colors duration-150 ease-in-out'
      : 'theme-text-secondary flex items-center py-3 w-full text-left transition-colors',
    justified: variant === 'desktop'
      ? 'flex items-center justify-between w-full px-4 py-2 theme-text-primary hover:bg-slate-100 hover:text-[#047cde] dark:hover:bg-slate-700/50 dark:hover:text-sky-300 text-left transition-colors duration-150 ease-in-out'
      : 'theme-text-secondary flex items-center justify-between py-3 w-full text-left transition-colors',
    subtle: variant === 'desktop'
      ? 'text-xs font-semibold text-slate-400 dark:text-slate-500'
      : 'text-xs font-semibold text-slate-400',
    themeToggle: variant === 'desktop'
      ? 'px-4 py-2 theme-text-primary hover:bg-slate-100 hover:text-[#047cde] dark:hover:bg-slate-700/50 dark:hover:text-sky-300 transition-colors duration-150 ease-in-out'
      : 'py-3 theme-text-secondary hover-theme-primary transition-colors',
  };
}

type HeaderIncomingCall = IncomingCall & {
  autoAnswer?: boolean;
};

type SearchResult = {
  id: string | number;
  type: string;
  title: string;
  subtitle?: string;
  description?: string;
  route: string;
  module: string;
  icon?: string;
  share_token?: string | null;
  metadata?: Record<string, unknown>;
};

type PublicBranding = {
  logo_url: string;
  header_color: string | null;
  primary_color: string;
  secondary_color: string;
};

type WhitelabelConfig = {
  client_name?: string;
  logo_url?: string;
  favicon_url?: string;
  primary_color?: string | null;
  secondary_color?: string | null;
  header_color?: string | null;
  return_url?: string | null;
};

type HeaderClientInfo = NonNullable<NavigationResponse['data']>['client_info'] & {
  whitelabel_enabled?: boolean;
  primary_color?: string | null;
  secondary_color?: string | null;
  whitelabel_header_color?: string | null;
};

type IncomingCallNotificationPayload = IncomingPushSipFlatFields & {
  caller_id?: string | number;
  caller_name?: string;
  caller_avatar?: string;
  client_id?: string | number;
  clientId?: string | number;
  call_type?: string;
  timestamp?: string;
  is_sip_call?: boolean | string;
  sip_data?: Partial<SipCallData>;
};

type PendingNavigationPayload = {
  type: 'message' | 'email';
  threadId?: string;
  emailId?: string;
};

type PendingEmailOpenPayload = {
  emailId?: string;
  threadId?: string;
  subject?: string;
  folder: string;
  timestamp: number;
};

type SharedContentPayload = {
  type: 'text' | 'files';
  text: string;
  files: Array<{ name: string; mimeType: string; size: number; data: string }>;
  timestamp?: number;
};

type HeaderWindow = Window & typeof globalThis & {
  __pwaHeaderDiagnostics?: {
    showStandalonePwaControls: boolean;
    showAppLikeMenuLayout: boolean;
    showClearCacheAction: boolean;
    showMobileSyncBadge: boolean;
    isMobileApp: boolean;
    isMenuOpen: boolean;
    pathname: string | null;
  };
  __setColdStartDiagnosticsEnabled?: (enabled: boolean, options?: { keepLogs?: boolean }) => void;
  __coldStartDiagnosticsEnabled?: boolean;
  __hideCallConnectingUI?: () => void;
  __markIncomingCallListenerReady?: () => void;
  __pendingCallAnswer?: IncomingCallNotificationPayload | null;
  __pendingNavigation?: PendingNavigationPayload | null;
  __hideNavigationUI?: () => void;
  __getPendingSharedContent?: () => SharedContentPayload | null;
  __clearPendingSharedContent?: () => void;
  __hideShareUI?: () => void;
  __incomingCallListenerReady?: boolean;
  __markNavigationListenerReady?: () => void;
  __navigationListenerReady?: boolean;
};

const MODULE_MAP: Record<string, string> = {
  'root': 'Root Admin',
  'total-automation': 'Automation',
  'total-finance': 'Accounts',
  'total-attendance': 'Attendance',
  'total-client-portal': 'Client Portal',
  'total-communications': 'Communications',
  'total-connect': 'Connect',
  'total-crm': 'CRM',
  'total-emergency-alert': 'Emergency Alert',
  'total-iot-control': 'IoT Control',
  'total-operations': 'Operations',
  'total-productivity': 'Productivity',
  'total-support': 'Support',
  'total-workforce': 'Workforce',
};

const DEV_SYNC_BADGE_OVERRIDE_STORAGE_KEY = 'dev_mobile_sync_badge';
const DEV_SYNC_BADGE_OVERRIDE_QUERY_PARAM = 'devMobileSyncBadge';
const COLDSTART_LOGS_ENABLED_STORAGE_KEY = 'ta_coldstart_logs_enabled';
const COLDSTART_LOGS_STORAGE_KEY = 'ta_coldstart_logs';
const COLDSTART_LOGS_TOGGLED_EVENT = 'ta-coldstart-logs-toggled';
const SMART_TASK_NAV_OVERRIDE_KEY = 'smart_task_navigation_override';
const SMART_TASK_NAV_OVERRIDE_CHANGED_EVENT = 'smart-task-navigation-override-changed';
const TRAINING_MODE_ENTER_MIN_LOADING_MS = 6000;
const CAPACITOR_FOOTER_HOME_TAPPED_EVENT = 'capacitor-footer-home-tapped';
const INSTALLED_PWA_UPDATE_TOAST_ID = 'ta-installed-pwa-update-toast';

/** Lucide icons — soft halo + directional edge in the same blue family as Modules */
const HEADER_QUICK_ACTION_SVG_OUTLINE =
  '[filter:drop-shadow(0_0_0.65px_rgba(4,124,222,0.5))_drop-shadow(0.55px_0_0_rgba(4,124,222,0.45))_drop-shadow(-0.55px_0_0_rgba(4,124,222,0.45))_drop-shadow(0_0.55px_0_rgba(4,124,222,0.45))_drop-shadow(0_-0.55px_0_rgba(4,124,222,0.45))]';

const HEADER_QUICK_ACTION_BADGE_CLASS = 'bg-red-500 text-white';
const HEADER_QUICK_ACTION_ICON_CLASS = `${HEADER_QUICK_ACTION_SVG_OUTLINE} text-[#047cde] dark:text-sky-300`;

const HEADER_MODULES_TRIGGER_CLASS =
  'inline-flex items-center gap-2 rounded-full border border-[#047cde]/45 bg-white/95 px-3 h-9 shadow-sm backdrop-blur-sm text-sm font-semibold text-[#047cde] transition-colors hover:bg-[#047cde] hover:text-white dark:border-sky-400/35 dark:bg-slate-950/85 dark:text-sky-300 dark:hover:bg-[#047cde] dark:hover:text-white';

// ta-theme-scope marks panels that render inside <header> but must keep the
// document theme text colors (globals.css resets the whitelabel-contrast vars).
const HEADER_MODULES_PANEL_CLASS =
  'ta-theme-scope absolute top-full left-1/2 mt-2 w-72 -translate-x-1/2 rounded-xl border border-slate-200/60 bg-white/98 py-2 shadow-xl z-[84] max-h-96 overflow-y-auto dark:border-slate-700/80 dark:bg-slate-900/96 dark:shadow-2xl';

const HEADER_PUBLIC_MENU_PANEL_CLASS =
  'ta-theme-scope absolute top-full right-0 mt-2 w-56 rounded-xl border border-slate-200/60 bg-white/98 py-2 shadow-xl z-50 dark:border-slate-700/80 dark:bg-slate-900/96 dark:shadow-2xl';

const HEADER_PUBLIC_AUTH_GROUP_CLASS =
  'flex items-center gap-2 rounded-full border border-slate-200/70 bg-white/95 px-2 py-1 shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/88';

const HEADER_PUBLIC_PRIMARY_AUTH_BUTTON_CLASS =
  'inline-flex items-center gap-2 rounded-full border border-transparent bg-gradient-to-r from-[#047cde] to-sky-500 h-8 px-3 text-xs font-semibold text-white shadow-sm transition-all hover:opacity-90 hover:shadow-md dark:from-sky-500 dark:to-[#047cde]';

const HEADER_PUBLIC_SECONDARY_AUTH_BUTTON_CLASS =
  'inline-flex items-center gap-2 rounded-full border border-[#047cde]/45 bg-white h-8 px-3 text-xs font-semibold text-[#047cde] shadow-sm transition-colors hover:bg-[#047cde] hover:text-white dark:border-sky-400/35 dark:bg-slate-950/85 dark:text-sky-300 dark:hover:bg-[#047cde] dark:hover:text-white';

const HEADER_MODULES_GROUP_CLASS =
  'border-t theme-border';

const HEADER_MODULES_GROUP_HEADER_CLASS =
  'px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider dark:bg-slate-800/90 dark:text-slate-300';

const HEADER_MODULES_ITEM_CLASS =
  'w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-900 transition-colors duration-150 ease-in-out hover:bg-slate-100 hover:text-[#047cde] dark:text-slate-100 dark:hover:bg-slate-700/50 dark:hover:text-sky-300';

const HEADER_PUBLIC_MENU_ITEM_CLASS =
  'flex items-center gap-2 px-3 py-2 text-sm text-slate-900 transition-colors duration-150 ease-in-out hover:bg-slate-100 hover:text-[#047cde] dark:text-slate-100 dark:hover:bg-slate-700/50 dark:hover:text-sky-300';

/** Top-level public nav — mega-menu triggers and direct links share one style.
 *  Text color rides on --theme-text-secondary (overridden on hover/active,
 *  since unlayered .theme-text-secondary beats Tailwind color utilities). */
const HEADER_PUBLIC_NAV_ITEM_CLASS =
  'inline-flex items-center gap-1.5 rounded-full px-3 h-9 text-sm font-semibold theme-text-secondary transition-colors hover:bg-slate-100 hover:[--theme-text-secondary:#0f172a] dark:hover:bg-slate-700/50 dark:hover:[--theme-text-secondary:#7dd3fc]';

/** Active nav item — menu open, or the current route. */
const HEADER_PUBLIC_NAV_ITEM_ACTIVE_CLASS =
  'bg-slate-100 [--theme-text-secondary:#047cde] dark:bg-slate-700/60 dark:[--theme-text-secondary:#7dd3fc]';

/** Mega-menu panel — full-width strip attached under the header (portaled
 *  into <header>, which is sticky/positioned). */
const HEADER_PUBLIC_MEGA_PANEL_CLASS =
  'ta-theme-scope absolute inset-x-0 top-full border-b border-slate-200/60 bg-white/98 shadow-2xl z-[84] dark:border-slate-700/80 dark:bg-slate-900/96';



const HEADER_MODULES_ACTIVE_ITEM_CLASS =
  'w-full flex items-center gap-2 px-3 py-2 text-sm text-left bg-blue-50 font-medium text-[#047cde] transition-colors duration-150 ease-in-out hover:bg-blue-100 dark:bg-sky-950/45 dark:text-sky-300 dark:hover:bg-sky-900/60';

const HEADER_MODULES_ICON_CLASS = 'text-[12px] text-[#047cde] dark:text-sky-300';

const HEADER_MODULES_DANGER_ITEM_CLASS =
  'flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-600 transition-colors duration-150 ease-in-out hover:bg-red-100 hover:text-red-700 dark:text-red-300 dark:hover:bg-red-950/50 dark:hover:text-red-200';

const HEADER_MODULES_BROWSE_ITEM_CLASS =
  'flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-900 transition-colors duration-150 ease-in-out hover:bg-slate-100 hover:text-[#047cde] dark:text-slate-100 dark:hover:bg-slate-700/50 dark:hover:text-sky-300';

/** Accordion section headers in the modules dropdown — same row style as the user-menu accordion. */
const HEADER_MODULES_NAV_ACCORDION_TOGGLE_CLASS =
  'flex items-center justify-between w-full px-4 py-2 theme-text-primary hover:bg-slate-100 hover:text-[#047cde] dark:hover:bg-slate-700/50 dark:hover:text-sky-300 text-left transition-colors duration-150 ease-in-out';

/** Preview-pane cards for rail entries that aren't catalog items
 *  (All Products / All Industries / custom-industry CTA). */
const PUBLIC_NAV_META_PREVIEWS: Record<string, NavPreviewItem> = {
  '/products': {
    href: '/products',
    label: 'All Products',
    faIcon: 'fa-th',
    subtitle: 'The full suite',
    description:
      'Communications, CRM, finance, workforce, operations and support — every module on one platform, sharing one login and one data model.',
  },
  '/products/industries': {
    href: '/products/industries',
    label: 'All Industries',
    faIcon: 'fa-th',
    subtitle: 'Browse every solution',
    description:
      'Pre-configured implementations for your industry — workflows, dashboards and templates already built on the platform. Get operational in minutes, not months.',
  },
  '/contact?subject=custom-industry': {
    href: '/contact?subject=custom-industry',
    label: "Don't see your industry?",
    faIcon: 'fa-plus',
    subtitle: 'Custom builds',
    description:
      'The universal work-item system adapts to almost any operation — talk to our team about a solution built around your workflows.',
  },
};

/** Signup paths surfaced by the Get Started mega menu — the same four
 *  choices the /auth/signup-options page presents (kept for mobile). */
const GET_STARTED_NAV_ITEMS: NavPreviewItem[] = [
  {
    href: '/auth/new-client-signup',
    label: 'New Company / Client',
    faIcon: 'fa-building',
    subtitle: 'Start a free trial',
    description:
      'Set up your business on Total Access — 14-day free trial, guided onboarding, no credit card required.',
    bullets: ['Pick your modules', 'Go live in 48 hours', 'Cancel anytime'],
  },
  {
    href: '/auth/signup',
    label: 'Join a Team',
    faIcon: 'fa-users',
    subtitle: 'Invited to a workspace',
    description:
      'Create your user account to join an existing organisation on Total Access.',
  },
  {
    href: '/auth/accountant-signup',
    label: 'Accountant / Bookkeeper',
    faIcon: 'fa-calculator',
    subtitle: 'Practice account',
    description:
      'A multi-client workspace for accountants and bookkeepers managing books on Total Access.',
  },
  {
    href: '/auth/new-client-signup?accountType=reseller',
    label: 'Reseller / Channel Partner',
    faIcon: 'fa-store',
    subtitle: 'Partner program',
    description:
      'Resell and support Total Access solutions for your own clients under the partner program.',
  },
];

const MODULES_RESOURCES_ACCORDION_GROUP_KEY = '__resources';
const MODULES_NAVIGATION_ACCORDION_GROUP_KEY = '__navigation';

/** Individual product pages surfaced as children of the Products nav item. */
/** Product flyout fallback + icon source — the live list comes from the
 *  modules catalog (/api/v1/modules/public-catalog) so every active module
 *  is linked at /products/<module_key> without a hardcoded nav. */
const PRODUCTS_CHILDREN: Array<{ href: string; label: string; icon: string; description?: string }> = [
  { href: '/products/total-attendance', label: 'Total Attendance', icon: 'fa-clock' },
  { href: '/products/total-automation', label: 'Total Automation', icon: 'fa-robot' },
  { href: '/products/total-connect', label: 'Total Connect', icon: 'fa-comments' },
  { href: '/products/total-cloud-directory', label: 'Total Cloud Directory', icon: 'fa-address-book' },
  { href: '/products/total-crm', label: 'Total CRM', icon: 'fa-handshake' },
  { href: '/products/total-finance', label: 'Total Finance', icon: 'fa-chart-line' },
  { href: '/products/total-forms', label: 'Total Forms', icon: 'fa-clipboard-list' },
  { href: '/products/total-integration', label: 'Total Integration', icon: 'fa-plug' },
  { href: '/products/total-operations', label: 'Total Operations', icon: 'fa-cogs' },
  { href: '/products/total-pabx', label: 'Total PABX', icon: 'fa-phone' },
  { href: '/products/total-support', label: 'Total Support', icon: 'fa-headset' },
  { href: '/products/total-vision', label: 'Total Vision', icon: 'fa-eye' },
  { href: '/products/total-workforce', label: 'Total Workforce', icon: 'fa-users' },
  { href: '/products/work-tracker', label: 'Work Tracker', icon: 'fa-tasks' },
];

type SitePageNavLink = { page_key: string; page_name: string; route: string };

type IndustrySolutionNavItem = {
  href: string;
  label: string;
  industry?: string;
  icon?: string;
  color?: string;
  featured?: boolean;
  description?: string;
  features?: string[];
};

/** Row in a two-pane nav menu — left rail entry + right preview pane. */
type NavPreviewItem = {
  href: string;
  label: string;
  subtitle?: string;
  description?: string;
  /** Lucide icon name (industry solutions). */
  icon?: string;
  /** Font Awesome class (platform modules / resource links). */
  faIcon?: string;
  /** Emoji icon (authenticated module items). */
  emoji?: string;
  color?: string;
  /** Quick-link rows shown under the preview description. */
  bullets?: string[];
  /** Authenticated module key — used for current-route emphasis/disable. */
  moduleKey?: string;
  disabled?: boolean;
  /** Custom activation (module nav does handleModuleNavigation, not router.push). */
  onSelect?: () => void;
};

/** Left-rail row of the two-pane menu — hover previews, click navigates. */
function PublicNavRailItem({
  item,
  active,
  onHover,
  onNavigate,
}: {
  item: NavPreviewItem;
  active: boolean;
  onHover: (href: string) => void;
  onNavigate: () => void;
}) {
  const className = `flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ease-in-out ${
    active
      ? 'bg-slate-100 font-medium text-[#047cde] dark:bg-slate-700/60 dark:text-sky-300'
      : 'text-slate-800 hover:bg-slate-100 hover:text-[#047cde] dark:text-slate-100 dark:hover:bg-slate-700/50 dark:hover:text-sky-300'
  }${item.disabled ? ' cursor-default opacity-50' : ''}`;
  const inner = (
    <>
      <span className="flex min-w-0 items-center gap-2.5">
        {item.icon ? (
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
            style={{ backgroundColor: `${item.color ?? '#047cde'}1f` }}
          >
            {renderIndustryGlyph(item.icon, 'h-3.5 w-3.5', { color: item.color ?? '#047cde' })}
          </span>
        ) : item.emoji ? (
          <span className="w-4 text-center text-base leading-none">{item.emoji}</span>
        ) : (
          <i className={`fa ${item.faIcon ?? 'fa-file-alt'} w-4 text-center text-[12px] text-[#047cde] dark:text-sky-300`}></i>
        )}
        <span className="truncate">{item.label}</span>
      </span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-40" />
    </>
  );
  if (item.onSelect) {
    return (
      <button
        type="button"
        className={className}
        role="menuitem"
        disabled={item.disabled}
        onMouseEnter={() => onHover(item.href)}
        onClick={() => {
          if (item.disabled) return;
          item.onSelect?.();
          onNavigate();
        }}
      >
        {inner}
      </button>
    );
  }
  const external = item.href.startsWith('http');
  if (external) {
    return (
      <a href={item.href} className={className} role="menuitem" onMouseEnter={() => onHover(item.href)} onClick={onNavigate}>
        {inner}
      </a>
    );
  }
  return (
    <Link href={item.href} className={className} role="menuitem" onMouseEnter={() => onHover(item.href)} onClick={onNavigate}>
      {inner}
    </Link>
  );
}

/** Right preview pane of the two-pane menu — the hovered rail item rendered
 *  as a product card: icon, name, description, Explore link + quick links. */
function PublicNavPreview({ item, onNavigate }: { item: NavPreviewItem; onNavigate: () => void }) {
  const external = item.href.startsWith('http');
  const explore = (
    <>
      <span>Explore {item.label}</span>
      <i className="fa fa-arrow-right text-[11px]"></i>
    </>
  );
  const exploreClass =
    'mt-2 inline-flex items-center gap-2 text-sm font-semibold text-[#047cde] underline underline-offset-2 hover:text-sky-600 dark:text-sky-300 dark:hover:text-sky-200';
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3">
        {item.icon ? (
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
            style={{ backgroundColor: `${item.color ?? '#047cde'}1f` }}
          >
            {renderIndustryGlyph(item.icon, 'h-5 w-5', { color: item.color ?? '#047cde' })}
          </span>
        ) : item.emoji ? (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#047cde]/10 text-2xl leading-none">
            {item.emoji}
          </span>
        ) : (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#047cde]/10">
            <i className={`fa ${item.faIcon ?? 'fa-file-alt'} text-[16px] text-[#047cde] dark:text-sky-300`}></i>
          </span>
        )}
        <div className="min-w-0">
          <div className="truncate text-lg font-bold text-slate-900 dark:text-white">{item.label}</div>
          {item.subtitle ? (
            <div className="truncate text-xs text-slate-500 dark:text-slate-400">{item.subtitle}</div>
          ) : null}
        </div>
      </div>
      {item.description ? (
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          {item.description}
        </p>
      ) : null}
      {item.onSelect ? (
        <button
          type="button"
          className={exploreClass}
          onClick={() => {
            item.onSelect?.();
            onNavigate();
          }}
        >
          {explore}
        </button>
      ) : external ? (
        <a href={item.href} className={exploreClass} onClick={onNavigate}>
          {explore}
        </a>
      ) : (
        <Link href={item.href} className={exploreClass} onClick={onNavigate}>
          {explore}
        </Link>
      )}
      {item.bullets && item.bullets.length > 0 ? (
        <div className="mt-4 space-y-0.5 border-t border-slate-100 pt-3 dark:border-slate-800">
          {item.bullets.map((bullet) =>
            external ? (
              <a
                key={bullet}
                href={item.href}
                onClick={onNavigate}
                className="block rounded-md px-1 py-1.5 text-sm font-semibold text-slate-800 transition-colors hover:text-[#047cde] dark:text-slate-200 dark:hover:text-sky-300"
              >
                {bullet}
              </a>
            ) : (
              <Link
                key={bullet}
                href={item.href}
                onClick={onNavigate}
                className="block rounded-md px-1 py-1.5 text-sm font-semibold text-slate-800 transition-colors hover:text-[#047cde] dark:text-slate-200 dark:hover:text-sky-300"
              >
                {bullet}
              </Link>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Desktop public mega menus — only one panel open at a time. */
type PublicMenuKey = 'products' | 'industries' | 'resources' | 'get-started';

/** Cap on industry items in compact/accordion menus — the full list lives on /products/industries. */
const INDUSTRY_MENU_MAX_ITEMS = 10;

/** Lucide lookup for industry-solution catalog icons (names come from the
 *  industry_solutions table). Explicit map so the header never pulls the
 *  full lucide barrel into every page's bundle. */
const INDUSTRY_ICON_MAP: Record<string, LucideIcon> = {
  activity: Activity,
  barchart3: BarChart3,
  briefcase: Briefcase,
  building2: Building2,
  calendar: Calendar,
  clipboardcheck: ClipboardCheck,
  clipboardlist: ClipboardList,
  factory: Factory,
  filetext: FileText,
  graduationcap: GraduationCap,
  hammer: Hammer,
  hardhat: HardHat,
  heart: Heart,
  mappin: MapPin,
  scale: Scale,
  scanline: ScanLine,
  ship: Ship,
  shield: Shield,
  shoppingbag: ShoppingBag,
  smartphone: Smartphone,
  sparkles: Sparkles,
  stethoscope: Stethoscope,
  ticket: Ticket,
  trendingup: TrendingUp,
  truck: Truck,
  usercheck: UserCheck,
  users: Users,
  utensilscrossed: UtensilsCrossed,
  video: Video,
  vote: Vote,
  wrench: Wrench,
  zap: Zap,
};

/** Renders the lucide glyph for an industry-solution icon name (not a
 *  component — returns JSX so it can be called during render). */
function renderIndustryGlyph(name: string | undefined, className: string, style?: CSSProperties): ReactNode {
  const Cmp = INDUSTRY_ICON_MAP[(name ?? '').trim().toLowerCase()] ?? Building2;
  return <Cmp className={className} style={style} />;
}

/**
 * Routes already hard-coded in the public menus — published site pages pointing
 * at these are skipped so dynamically discovered pages never duplicate items.
 */
const KNOWN_MENU_ROUTES = new Set<string>([
  '/', '/about', '/products', '/products/industries', '/contact', '/pricing',
  '/support', '/resources', '/case-studies', '/legal', '/encryption-info',
]);

function modulesNavAccordionCollapsedKey(scope: 'desktop' | 'mobile', key: string) {
  return `${scope}::${key}`;
}

function canUseBrowserSyncBadgeOverride(): boolean {
  if (typeof window === 'undefined' || isCapacitor()) {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  return (
    process.env.NODE_ENV !== 'production'
    || hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname.includes('dev-next')
    || hostname.startsWith('dev-') || hostname === 'dev.totalaccess.co.za'
  );
}

function canUseColdStartLogToggle(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  return (
    process.env.NODE_ENV !== 'production'
    || hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname.includes('dev-next')
    || hostname.includes('dev-staging')
    || hostname.startsWith('dev-') || hostname === 'dev.totalaccess.co.za'
    || hostname.includes('dev-app') || hostname === 'dev.totalaccess.co.za'
  );
}

function getColdStartLogsEnabled(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    return localStorage.getItem(COLDSTART_LOGS_ENABLED_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

async function ensureMinimumTrainingModeLoadingTime(
  startedAt: number,
  mode: 'enter' | 'exit',
): Promise<void> {
  const minimumDurationMs = mode === 'enter' ? TRAINING_MODE_ENTER_MIN_LOADING_MS : 0;
  const remainingMs = minimumDurationMs - (Date.now() - startedAt);

  if (remainingMs <= 0) {
    return;
  }

  await new Promise((resolve) => setTimeout(resolve, remainingMs));
}

export default function Header() {
  const router = useNavigationRouter();
  const workItemOpener = useOptionalWorkItemOpener();
  const openTeamMember = useOpenTeamMember();
  const { user, isAuthenticated, logout, isRoot } = useAuth();
  const { navigationGroups, loading: navLoading, refreshNavigation, user: navUser, userClients, navigationData, isUserLevelOnly, userRole, isAdmin } = useNavigation();
  const { currentLogo, headerColor: themeHeaderColor, clientLogo: themeClientLogo, applyClientFavicon } = useTheme();
  const { badgeCounts, setMissedCallsCount } = useBadge();
  /** Standalone Total Connect Tauri window mounts its own TotalConnectRTC in the suite page */
  const { isStandalone: isTotalConnectStandaloneWindow } = useTotalConnectStandaloneWindow();
  const { toggleModal, activeModal, openModal } = useModalState();
  const taskCount = badgeCounts.tasksDueTodayCount;
  const { barVisible: favouritesBarVisible, toggleBarVisible: toggleFavouritesBar } = useFavourites();

  // Session lock control — allows manual lock from header button / Ctrl+L
  const session = useSession();

  // Sync status for LOCAL-FIRST offline architecture (now with MasterSync integration)
  const {
    status: syncStatus,
    stats: syncStats,
    progress: syncProgress,
    totalPending,
    triggerSync,
    cancelSync,
  } = useSyncStatus();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModulesOpen, setIsModulesOpen] = useState(false);
  /** Authenticated mega menu — which of the three panes is showing. */
  const [authMenuKey, setAuthMenuKey] = useState<'modules' | 'resources' | 'menu'>('modules');
  const [isPublicNavOpen, setIsPublicNavOpen] = useState(false);
  /** Desktop mega menu — which top-level panel is open (one at a time). */
  const [publicMenu, setPublicMenu] = useState<PublicMenuKey | null>(null);
  /** md–lg compact public menu — which accordion section is expanded. */
  const [compactNavSection, setCompactNavSection] = useState<'products' | 'industries' | 'resources' | null>(null);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isMobileIndustriesOpen, setIsMobileIndustriesOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);
  /** Published site pages with routes — drives the semi-dynamic public nav. */
  const [sitePageLinks, setSitePageLinks] = useState<SitePageNavLink[]>([]);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [openMenuSection, setOpenMenuSection] = useState<MenuSectionKey | null>(null);
  /** Per modules dropdown: accordion rows start collapsed; only one section is open at a time (`true` = expanded). */
  const [modulesNavAccordionExpanded, setModulesNavAccordionExpanded] = useState<Record<string, boolean>>({});
  const [isClientSelectorOpen, setIsClientSelectorOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isDesktopApp, setIsDesktopApp] = useState(false);
  // Initialize isMobileApp immediately to avoid filter timing issues
  const [isMobileApp, setIsMobileApp] = useState(() => {
    if (typeof window !== 'undefined') {
      return isCapacitor();
    }
    return false;
  });
  const [showBrowserSyncBadgeOverride, setShowBrowserSyncBadgeOverride] = useState(false);
  const [showColdStartLogToggle, setShowColdStartLogToggle] = useState(false);
  const [coldStartLogsEnabled, setColdStartLogsEnabled] = useState(true);
  const [showDiagnosticModal, setShowDiagnosticModal] = useState(false);
  const [showClearCacheModal, setShowClearCacheModal] = useState(false);
  const [showMyDevicesModal, setShowMyDevicesModal] = useState(false);
   const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileInitialTab, setProfileInitialTab] = useState<'personal' | 'security' | 'notifications' | 'terms' | 'overview'>('personal');
  const [showIamModal, setShowIamModal] = useState(false);
  // Pill navigation request inside the IAM console (e.g. guided tours deep
  // linking to a specific hub tab via 'iam-console-open' detail.tab).
  const [iamNavRequest, setIamNavRequest] = useState<{ tab: string; nonce: number } | null>(null);
  const [showEssModal, setShowEssModal] = useState(false);
  const [essInitialTab, setEssInitialTab] = useState<WorkforcePortalTab | undefined>(undefined);
  const [essInitialSection, setEssInitialSection] = useState<WorkforcePortalSection | undefined>(undefined);

  // Dispatch tour training events when the user menu or profile/support/diagnostics
  // modals open, so the platform-basics guided tour can verify these interactions.
  useEffect(() => {
    if (isUserMenuOpen) {
      emitGuidedTourTrainingEvent({ type: 'user_menu_opened' });
    }
  }, [isUserMenuOpen]);

  useEffect(() => {
    if (showProfileModal) {
      emitGuidedTourTrainingEvent({ type: 'profile_modal_opened' });
    }
  }, [showProfileModal]);

  useEffect(() => {
    if (showIamModal) {
      emitGuidedTourTrainingEvent({ type: 'iam_modal_opened' });
    }
  }, [showIamModal]);

  // Allow any surface to open the IAM console modal (e.g. legacy
  // /client-zone/admin-hub?tab=iam|users|onboarding|people deep links).
  useEffect(() => {
    const openIamConsole = (event: Event) => {
      setShowIamModal(true);
      const tab = (event as CustomEvent<{ tab?: string }>).detail?.tab;
      if (tab) setIamNavRequest({ tab, nonce: Date.now() });
    };
    window.addEventListener('iam-console-open', openIamConsole);
    return () => window.removeEventListener('iam-console-open', openIamConsole);
  }, []);

  // Allow any surface to open the Employee Self-Service portal modal (e.g. the
  // ESS tile on UnifiedDashboard) with an optional deep-linked tab/section.
  useEffect(() => {
    const openEssPortal = (event: Event) => {
      const detail = (event as CustomEvent<{
        tab?: WorkforcePortalTab;
        section?: WorkforcePortalSection;
      }>).detail;
      setEssInitialTab(detail?.tab);
      setEssInitialSection(detail?.section);
      setShowEssModal(true);
    };
    window.addEventListener('ess-portal-open', openEssPortal);
    return () => window.removeEventListener('ess-portal-open', openEssPortal);
  }, []);

  useEffect(() => {
    if (showMyDevicesModal) {
      emitGuidedTourTrainingEvent({ type: 'my_devices_modal_opened' });
    }
  }, [showMyDevicesModal]);


  useEffect(() => {
    if (showDiagnosticModal) {
      emitGuidedTourTrainingEvent({ type: 'diagnostics_modal_opened' });
    }
  }, [showDiagnosticModal]);

  useEffect(() => {
    if (showEssModal) {
      emitGuidedTourTrainingEvent({ type: 'ess_portal_modal_opened' });
    }
  }, [showEssModal]);

  // Emit a training event when the My Corner accordion section expands so the
  // platform-basics guided tour can verify + auto-advance the my-corner step.
  useEffect(() => {
    if (openMenuSection === 'my-corner') {
      emitGuidedTourTrainingEvent({ type: 'my_corner_section_opened' });
    }
  }, [openMenuSection]);

  const [installContext, setInstallContext] = useState<AppInstallCta>(DEFAULT_APP_INSTALL_CTA);
  const [isResponsiveMobileViewport, setIsResponsiveMobileViewport] = useState(false);
  const [isMdDesktopViewport, setIsMdDesktopViewport] = useState(false);
  const [isDesktopEmailSlideOpen, setIsDesktopEmailSlideOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchScope, setSearchScope] = useState<'module' | 'all'>('module');
  const [searchTypeFilter, setSearchTypeFilter] = useState<string | null>(null);
  const [managedTasksInitialTab, setManagedTasksInitialTab] = useState<ManagedTasksMainTab>('tasks');
  const [currentModule, setCurrentModule] = useState<{ key: string; label: string } | null>(null);
  const [pwaVersionInfo, setPwaVersionInfo] = useState<{
    version: string;
    deployedAt: string;
    loading: boolean;
  }>({
    version: 'Loading...',
    deployedAt: 'Loading...',
    loading: false,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const publicNavRef = useRef<HTMLDivElement>(null);
  /** Public auth CTAs (Get Started / Sign In) — the Get Started trigger lives
   *  outside publicNavRef, so outside-click must consider it too. */
  const publicAuthRef = useRef<HTMLDivElement>(null);
  const publicMenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const supportModalRef = useRef<SupportModalHandle | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const headerElementRef = useRef<HTMLElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  // When the middleware rewrites root "/" to "/client-zone/dashboard" (Capacitor apps
  // and authenticated users on dedicated app hosts), usePathname() returns "/". Treat
  // "/" as client-zone so the header renders the correct navigation, logo, and colors.
  const isInClientZone = pathname?.startsWith('/client-zone') || (pathname === '/' && isAuthenticated);

  // Close any open user-menu modal when the guided tour advances past a step
  // that revealed a modal (reveal_target). This lets the next tour step
  // re-guide the user back to the dropdown without a stale modal covering it.
  useEffect(() => {
    const handleDismissReveal = () => {
      setShowProfileModal(false);
      setShowEssModal(false);
      setShowMyDevicesModal(false);
      setShowDiagnosticModal(false);
      setShowClearCacheModal(false);
      supportModalRef.current?.close?.();
      setIsUserMenuOpen(false);
      setOpenMenuSection(null);
    };
    window.addEventListener(GUIDED_TOUR_DISMISS_REVEAL_EVENT, handleDismissReveal as EventListener);
    return () => {
      window.removeEventListener(GUIDED_TOUR_DISMISS_REVEAL_EVENT, handleDismissReveal as EventListener);
    };
  }, []);
  const isAuthPage = pathname?.startsWith('/auth/');
  const isLoginPage = pathname === '/auth/login';
  const shouldTrackDemoSession = shouldTrackOperationsDemoSession(pathname);
  const [showHeader, setShowHeader] = useState(true);
  /** When true, portal chrome (e.g. Total Finance mobile shell) owns full viewport — hide app header + top safe-area strip. */
  const [immersedClientZoneModule, setImmersedClientZoneModule] = useState(false);
  const lastScrollY = useRef(0);
  const isOnDashboard = pathname === '/client-zone/dashboard' || pathname === '/client-zone/portal' || pathname === '/client-zone' || pathname === '/';

  // OTA Update and Cache state for mobile menu
  const {
    checkForUpdates,
    downloadAndInstall,
    isChecking,
    updateInfo,
    progress,
    skipVersion,
    currentVersion,
  } = useCapacitorOTAUpdate(false);
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | 'available' | 'up-to-date' | 'error'>('idle');
  const [showAppVersionDialog, setShowAppVersionDialog] = useState(false);
  const [cacheStatus, setCacheStatus] = useState<'idle' | 'clearing' | 'success' | 'error'>('idle');
  const [pwaUpdateStatus, setPwaUpdateStatus] = useState<'idle' | 'checking' | 'refreshing' | 'updated' | 'up-to-date' | 'error'>('idle');
  const showStandalonePwaControls = mounted && !isMobileApp && !isDesktopApp && isResponsiveMobileViewport;
  const showAppLikeMenuLayout = isMobileApp || showStandalonePwaControls;
  const showClearCacheAction = mounted && typeof window !== 'undefined';
  const installedPwa = mounted && isStandalonePwa();
  const hideHeaderForCapacitorLogin = showAppLikeMenuLayout && isLoginPage;
  const desktopHeaderToggleEnabled = mounted && isAuthenticated && !showAppLikeMenuLayout;
  const desktopHeaderHidden = desktopHeaderToggleEnabled && !showHeader;
  const appVariationBadge = mounted
    ? isMobileApp
      ? isAndroid()
        ? { kind: 'android' as const, label: 'Android' }
        : isIOS()
          ? { kind: 'ios' as const, label: 'IOS' }
          : null
      : installedPwa
        ? { kind: 'pwa' as const, label: 'PWA' }
        : { kind: 'portal' as const, label: 'Portal' }
    : null;

  // Network and GPS status for mobile
  const networkStatus = useMasterSyncNetworkStatus();
  const [gpsEnabled, setGpsEnabled] = useState<boolean | null>(null);
  const emailWarmupPromiseRef = useRef<Promise<void> | null>(null);
  const showMobileSyncBadge = isMobileApp || showStandalonePwaControls || showBrowserSyncBadgeOverride;
  const masterSyncModal = useMasterSyncModal({
    mounted,
    isAuthenticated,
    isOnDashboard,
    networkStatus,
    userId: user?.id,
    autoPreparationClientId: Number(navigationData?.client_info?.id || 0),
  });

  /** Pill label is only Online / Offline / Syncing — details appear in MasterSync modal. */
  const isOfflinePreparing = Boolean(
    masterSyncModal.canPrepareOffline && masterSyncModal.status === 'preparing',
  );
  const isUploadSyncing = networkStatus === 'syncing';
  const mobileBadgeSyncActive = isOfflinePreparing || isUploadSyncing;
  const mobileSyncBadgeLabel = mobileBadgeSyncActive
    ? 'Syncing'
    : networkStatus === 'online'
      ? 'Online'
      : 'Offline';

  const triggerHeaderHaptic = useCallback(async (pattern: HeaderHapticPattern) => {
    if (!isCapacitor()) {
      return;
    }

    switch (pattern) {
      case 'menuToggle':
        await haptics.buttonTap();
        return;
      case 'accordionToggle':
        await haptics.toggle();
        return;
      case 'menuAction':
        await haptics.medium();
        return;
      case 'warning':
        await haptics.warning();
        return;
      case 'success':
        await haptics.success();
        return;
    }
  }, []);

  useEffect(() => {
    let active = true;

    // Check for diagnostics query parameter to auto-open modal
    if (active && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (url.searchParams.get('diagnostics') === 'true') {
        setShowDiagnosticModal(true);
        // Clean up the URL
        url.searchParams.delete('diagnostics');
        window.history.replaceState({}, '', url.toString());
      }
    }

    // Listen for custom event to open My Devices modal
    const handleOpenMyDevices = () => {
      setShowMyDevicesModal(true);
    };

    window.addEventListener('open-my-devices-modal', handleOpenMyDevices);

    return () => {
      active = false;
      window.removeEventListener('open-my-devices-modal', handleOpenMyDevices);
    };
  }, []);

  useEffect(() => {
    setInstallContext(resolveAppInstallCta(window.navigator));
  }, []);

  // updateStatus derives only from this instance's own check state — the
  // service broadcasts progress to every subscriber (lifecycle, layout, other
  // hooks), so a wedged or in-flight background check must not disable the
  // version button.
  useEffect(() => {
    if (isChecking) {
      setUpdateStatus('checking');
      return;
    }

    if (updateInfo?.checkFailed) {
      setUpdateStatus('error');
      return;
    }

    if (updateInfo?.updateAvailable) {
      setUpdateStatus('available');
      return;
    }

    if (updateInfo) {
      setUpdateStatus('up-to-date');
    }
  }, [isChecking, updateInfo]);

  const openAppVersionDialog = useCallback(async (forceCheck: boolean = false) => {
    setShowAppVersionDialog(true);

    if (!forceCheck && updateInfo && updateStatus !== 'error') {
      return;
    }

    setUpdateStatus('checking');

    try {
      const result = await checkForUpdates(true);

      if (result?.checkFailed) {
        setUpdateStatus('error');
      } else if (result?.updateAvailable) {
        setUpdateStatus('available');
      } else if (result) {
        setUpdateStatus('up-to-date');
      } else {
        setUpdateStatus('idle');
      }
    } catch (error) {
      console.error('[Header] App version check failed:', error);
      setUpdateStatus('error');
    }
  }, [checkForUpdates, updateInfo, updateStatus]);

  const handleSkipAppVersion = useCallback(() => {
    skipVersion();
    setShowAppVersionDialog(false);
  }, [skipVersion]);

  const closeMobileMenuWithHaptic = useCallback(() => {
    void triggerHeaderHaptic('menuAction');
    setIsMenuOpen(false);
    setOpenMenuSection(null);
    setIsMobileProductsOpen(false);
    setIsMobileIndustriesOpen(false);
    setIsMobileResourcesOpen(false);
  }, [triggerHeaderHaptic]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateResponsiveMobileViewport = () => {
      setIsResponsiveMobileViewport(window.matchMedia('(max-width: 1023.98px)').matches);
    };

    updateResponsiveMobileViewport();
    window.addEventListener('resize', updateResponsiveMobileViewport);
    window.addEventListener('orientationchange', updateResponsiveMobileViewport);

    return () => {
      window.removeEventListener('resize', updateResponsiveMobileViewport);
      window.removeEventListener('orientationchange', updateResponsiveMobileViewport);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mq = window.matchMedia('(min-width: 768px)');
    const syncMd = () => setIsMdDesktopViewport(mq.matches);
    syncMd();
    mq.addEventListener('change', syncMd);
    return () => mq.removeEventListener('change', syncMd);
  }, []);

  const emailExperienceUsesMobileModal = isCapacitor() || !isMdDesktopViewport;

  useEffect(() => {
    if (emailExperienceUsesMobileModal && isDesktopEmailSlideOpen) {
      setIsDesktopEmailSlideOpen(false);
    }
  }, [emailExperienceUsesMobileModal, isDesktopEmailSlideOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const headerWindow = window as HeaderWindow;

    headerWindow.__pwaHeaderDiagnostics = {
      showStandalonePwaControls,
      showAppLikeMenuLayout,
      showClearCacheAction,
      showMobileSyncBadge,
      isMobileApp,
      isMenuOpen,
      pathname,
    };
    window.dispatchEvent(new CustomEvent('pwa-diagnostics-updated'));
  }, [isMenuOpen, isMobileApp, pathname, showAppLikeMenuLayout, showClearCacheAction, showMobileSyncBadge, showStandalonePwaControls]);

  useEffect(() => {
    if (!desktopHeaderToggleEnabled) {
      setShowHeader(true);
    }
  }, [desktopHeaderToggleEnabled]);

  useEffect(() => {
    if (!isMenuOpen) {
      setOpenMenuSection(null);
    }
  }, [isMenuOpen]);

  // Operations module access and call modal state (Capacitor only)
  const [, setHasOperationsAccess] = useState(false);
  const [, setHasSmartTaskDashboardAccess] = useState(false);
  const [hasConnectAccess, setHasConnectAccess] = useState(false);
  const [hasEssAccess, setHasEssAccess] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);

  const warmEmailModal = useCallback(() => {
    if (!hasConnectAccess) {
      return Promise.resolve();
    }

    if (emailWarmupPromiseRef.current) {
      return emailWarmupPromiseRef.current;
    }

    emailWarmupPromiseRef.current = (async () => {
      try {
        const emailStore = useEmailStore.getState();
        await emailStore.hydrateFromIndexedDB();

        const hasAccount = await emailStore.checkEmailAccount();
        if (!hasAccount) {
          return;
        }

        await Promise.allSettled([
          emailStore.fetchFolders(),
          emailStore.fetchThreads({ folder: emailStore.selectedFolder || 'INBOX' }),
        ]);
      } catch (error) {
        console.warn('[Header] Email modal warmup failed:', error);
      } finally {
        emailWarmupPromiseRef.current = null;
      }
    })();

    return emailWarmupPromiseRef.current;
  }, [hasConnectAccess]);

  // Pending thread ID for Messages modal (from FCM notification deep link)
  const [pendingMessagesThreadId, setPendingMessagesThreadId] = useState<string | null>(null);
  const [pendingMessagesDirectUserId, setPendingMessagesDirectUserId] = useState<number | null>(null);

  // Pending email ID for Email modal (from FCM notification deep link)
  const [pendingEmailId, setPendingEmailId] = useState<string | null>(null);

  // Pending shared content for Messages modal (from Android share intent)
  const [pendingSharedContent, setPendingSharedContent] = useState<{
    type: 'text' | 'files';
    text: string;
    files: Array<{ name: string; mimeType: string; size: number; data: string }>;
  } | null>(null);

  // User feature settings (from Total Operations module)
  const [userFeatureSettings, setUserFeatureSettings] = useState<{
    message_centre: boolean;
    voice_calls: boolean;
    video_calls: boolean;
    my_tasks: boolean;
    total_assistant: boolean;
    job_threads?: boolean;
    client_thread?: boolean;
    schedule_thread?: boolean;
    show_when_online?: boolean;
    smart_task_field_worker?: boolean;
    show_last_known_location?: boolean;
  } | null>(null);
  const [hideTotalOperationsNavigation, setHideTotalOperationsNavigation] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SMART_TASK_NAV_OVERRIDE_KEY) === 'true';
  });
  const [demoSession, setDemoSession] = useState<DemoSessionInfo | null>(null);
  const [, setCanEnterOperationsDemo] = useState(false);
  const [, setIsDemoSessionLoading] = useState(false);
  const [demoActionLoading, setDemoActionLoading] = useState<'enter' | 'exit' | null>(null);
  const [demoLoadingProgressActive, setDemoLoadingProgressActive] = useState(false);

  // Feature settings keys type
  type FeatureKey = 'message_centre' | 'voice_calls' | 'video_calls' | 'my_tasks' | 'total_assistant';

  const shouldHideNavigationModule = useCallback((moduleKey?: string | null, userRole?: string | null) => {
    if (!hideTotalOperationsNavigation || moduleKey !== 'total-operations') {
      return false;
    }
    // Match navigationService.stripTotalOperationsForSmartTaskUsers: a
    // privileged module_access role keeps the navigation item.
    return !['owner', 'admin', 'supervisor', 'manager', 'root'].includes((userRole || '').toLowerCase());
  }, [hideTotalOperationsNavigation]);

  const closeMessagesModal = useCallback(() => {
    if (activeModal === 'messages') {
      toggleModal('messages');
    }
    setPendingMessagesThreadId(null);
    setPendingSharedContent(null);
    window.dispatchEvent(new CustomEvent('capacitor-footer-modal-closed', { detail: { tab: 'messages' } }));
  }, [activeModal, toggleModal]);

  const closeEmailModal = useCallback(() => {
    setIsDesktopEmailSlideOpen(false);
    if (activeModal === 'email') {
      toggleModal('email');
    }
    setPendingEmailId(null);
    window.dispatchEvent(new CustomEvent('capacitor-footer-modal-closed', { detail: { tab: 'email' } }));
  }, [activeModal, toggleModal]);

  const closeCalendarModal = useCallback(() => {
    if (activeModal === 'calendar') {
      toggleModal('calendar');
    }
    window.dispatchEvent(new CustomEvent('capacitor-footer-modal-closed', { detail: { tab: 'calendar' } }));
  }, [activeModal, toggleModal]);

  const toggleEmailExperience = useCallback(() => {
    void warmEmailModal();
    if (!isCapacitor() && isMdDesktopViewport) {
      setIsDesktopEmailSlideOpen((open) => !open);
      return;
    }
    toggleModal('email');
  }, [warmEmailModal, isMdDesktopViewport, toggleModal]);

  const openEmailExperience = useCallback(() => {
    void warmEmailModal();
    if (!isCapacitor() && isMdDesktopViewport) {
      setIsDesktopEmailSlideOpen(true);
      return;
    }
    openModal('email');
  }, [warmEmailModal, isMdDesktopViewport, openModal]);

  // Set mounted state after hydration to prevent hydration mismatches
  useEffect(() => {
    setMounted(true);
    setIsDesktopApp(isTauriDesktop());
    const capacitorApp = isCapacitor();
    setIsMobileApp(capacitorApp);

    if (typeof window === 'undefined' || capacitorApp) {
      setShowBrowserSyncBadgeOverride(false);
      return;
    }

    if (!canUseBrowserSyncBadgeOverride()) {
      setShowBrowserSyncBadgeOverride(false);
      return;
    }

    try {
      const url = new URL(window.location.href);
      const overrideParam = url.searchParams.get(DEV_SYNC_BADGE_OVERRIDE_QUERY_PARAM);

      if (overrideParam === '1' || overrideParam === 'true') {
        localStorage.setItem(DEV_SYNC_BADGE_OVERRIDE_STORAGE_KEY, '1');
        setShowBrowserSyncBadgeOverride(true);
        return;
      }

      if (overrideParam === '0' || overrideParam === 'false') {
        localStorage.removeItem(DEV_SYNC_BADGE_OVERRIDE_STORAGE_KEY);
        setShowBrowserSyncBadgeOverride(false);
        return;
      }

      setShowBrowserSyncBadgeOverride(
        localStorage.getItem(DEV_SYNC_BADGE_OVERRIDE_STORAGE_KEY) === '1'
      );
    } catch {
      setShowBrowserSyncBadgeOverride(false);
    }
  }, []);

  // Public site pages (published, with routes) — feeds the semi-dynamic nav.
  // New pages added to sitePages.yaml appear in the menus automatically.
  useEffect(() => {
    if (isCapacitor()) return;
    let cancelled = false;
    fetch('/api/v1/site-pages')
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled || !result?.success) return;
        const pages = Array.isArray(result.data?.pages) ? result.data.pages : [];
        setSitePageLinks(
          pages
            .filter(
              (p: { route?: string | null; is_published?: boolean }) =>
                typeof p.route === 'string' && !!p.route && p.is_published !== false
            )
            .map((p: { page_key: string; page_name: string; route: string }) => ({
              page_key: p.page_key,
              page_name: p.page_name,
              route: p.route as string,
            }))
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  /** Products dropdown — dynamically defined from the modules catalog so
   *  every active module is listed at /products/<module_key>. Seeded with
   *  the shipped list until the catalog loads. */
  const [productLinks, setProductLinks] = useState<Array<{ href: string; label: string; icon: string; category?: string; description?: string }>>(PRODUCTS_CHILDREN);
  /** Two-pane nav — which rail item is previewed (href); null = default. */
  const [navPreviewHref, setNavPreviewHref] = useState<string | null>(null);
  /** Current nav spotlight — the featured card atop the Products flyout,
   *  rotated by the MyShell scheduled task via /api/v1/nav/spotlight. */
  const [spotlight, setSpotlight] = useState<{ kind: string; href: string; label: string } | null>(null);

  useEffect(() => {
    if (isCapacitor()) return;
    let cancelled = false;
    fetch('/api/v1/nav/spotlight')
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled || !result?.success) return;
        setSpotlight(result.data?.spotlight ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isCapacitor()) return;
    let cancelled = false;
    fetch('/api/v1/modules/public-catalog')
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled || !result?.success) return;
        const mods = Array.isArray(result.data?.modules) ? result.data.modules : [];
        if (mods.length === 0) return;
        const icons = new Map(PRODUCTS_CHILDREN.map((c) => [c.href, c.icon]));
        setProductLinks(
          mods.map((m: {
            module_key: string;
            module_name: string;
            category?: string | null;
            description?: string | null;
            marketing_content?: { hero?: { tagline?: string; description?: string } } | null;
          }) => ({
            href: `/products/${m.module_key}`,
            label: m.module_name,
            icon: icons.get(`/products/${m.module_key}`) ?? 'fa-box-open',
            category: m.category ?? undefined,
            description:
              m.marketing_content?.hero?.tagline ??
              m.marketing_content?.hero?.description ??
              m.description ??
              undefined,
          }))
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  /** Product flyout = module catalog slugs + published site pages under /products/ (industry-* keys are excluded — they appear in the Industries flyout). */
  const productMenuChildren = useMemo(() => {
    const known = new Set(productLinks.map((c) => c.href));
    const extras = sitePageLinks
      .filter(
        (p) =>
          p.route.startsWith('/products/') &&
          !p.page_key.startsWith('industry-') &&
          !KNOWN_MENU_ROUTES.has(p.route) &&
          !known.has(p.route)
      )
      .map((p) => ({ href: p.route, label: p.page_name, icon: 'fa-file-alt', category: undefined as string | undefined, description: undefined as string | undefined }));
    return [...productLinks, ...extras];
  }, [sitePageLinks, productLinks]);

  /** Zoho-style grouped flyout — modules bucketed by normalized category,
   *  extras (site pages without a module) attached to their own group. */
  const productMenuGroups = useMemo(() => {
    const norm = (c?: string) => (c ?? '').trim().toLowerCase();
    const GROUP_LABEL: Record<string, string> = {
      communication: 'Communications',
      sales: 'Sales & CRM',
      finance: 'Finance',
      'total workforce': 'Workforce',
      hr: 'Workforce',
      productivity: 'Productivity',
      'security & identity': 'Security & Identity',
      'total operations': 'Operations & Platform',
      'total business operations': 'Operations & Platform',
      other: 'Operations & Platform',
    };
    const ORDER = ['Communications', 'Sales & CRM', 'Finance', 'Workforce', 'Productivity', 'Security & Identity', 'Operations & Platform'];
    const buckets = new Map<string, typeof productMenuChildren>();
    for (const item of productMenuChildren) {
      const label = GROUP_LABEL[norm(item.category)] ?? norm(item.category) ?? '';
      const key = label && item.category ? label : 'More Products';
      const arr = buckets.get(key) ?? [];
      arr.push(item);
      buckets.set(key, arr);
    }
    const ordered = ORDER.filter((g) => buckets.has(g)).map((g) => ({ label: g, items: buckets.get(g)! }));
    for (const [k, v] of buckets) {
      if (!ORDER.includes(k)) ordered.push({ label: k, items: v });
    }
    return ordered;
  }, [productMenuChildren]);

  /** Public industry-solutions catalog — feeds the Industries mega menu and
   *  the Products panel's featured-solutions rail. Published industry-* site
   *  pages fill any gaps when the catalog lacks a row. */
  const [industrySolutions, setIndustrySolutions] = useState<IndustrySolutionNavItem[]>([]);

  useEffect(() => {
    if (isCapacitor()) return;
    let cancelled = false;
    fetch('/api/v1/industry-solutions/catalog')
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled || !result?.success) return;
        const sols = Array.isArray(result.data?.solutions) ? result.data.solutions : [];
        if (sols.length === 0) return;
        setIndustrySolutions(
          sols.map((s: {
            solution_key: string;
            name: string;
            industry?: string | null;
            icon?: string;
            color?: string;
            is_featured?: boolean;
            description?: string | null;
            features?: unknown;
          }) => ({
            href: `/products/${s.solution_key}`,
            label: s.name,
            industry: s.industry ?? '',
            icon: s.icon,
            color: s.color,
            featured: Boolean(s.is_featured),
            description: s.description ?? undefined,
            features: Array.isArray(s.features)
              ? s.features
                  .slice(0, 3)
                  .map((f) => (typeof f === 'string' ? f : (f as { title?: string; name?: string })?.title ?? (f as { name?: string })?.name ?? ''))
                  .filter(Boolean)
              : [],
          }))
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  /** Escape closes the open public/authenticated mega menu. */
  useEffect(() => {
    if (publicMenu === null && !isModulesOpen && !isUserMenuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPublicMenu(null);
        setIsModulesOpen(false);
        setIsUserMenuOpen(false);
        setOpenMenuSection(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [publicMenu, isModulesOpen, isUserMenuOpen]);

  /** Mega-menu hover intent — panels open on trigger hover and close shortly
   *  after the pointer leaves the whole nav cluster (bridge-free, unlike the
   *  old nested flyouts). */
  const cancelPublicMenuClose = () => {
    if (publicMenuCloseTimerRef.current) {
      clearTimeout(publicMenuCloseTimerRef.current);
      publicMenuCloseTimerRef.current = null;
    }
  };
  const openPublicMenu = (key: PublicMenuKey) => {
    cancelPublicMenuClose();
    setPublicMenu(key);
    setNavPreviewHref(null);
  };
  const closePublicMenu = () => setPublicMenu(null);
  const togglePublicMenu = (key: PublicMenuKey) => {
    setPublicMenu((prev) => (prev === key ? null : key));
    setNavPreviewHref(null);
  };

  /** Authenticated mega menus (Modules / Resources / Menu) — click toggles a
   *  pane open/closed; while a pane is open, hovering another trigger
   *  switches to it. isModulesOpen = "an auth panel is open",
   *  authMenuKey = which pane. */
  const openAuthMenu = (key: 'modules' | 'resources' | 'menu') => {
    cancelPublicMenuClose();
    setAuthMenuKey(key);
    setIsModulesOpen(true);
    setNavPreviewHref(null);
  };
  const toggleAuthMenu = (key: 'modules' | 'resources' | 'menu') => {
    setIsModulesOpen((prev) => !(prev && authMenuKey === key));
    setAuthMenuKey(key);
    setNavPreviewHref(null);
  };
  const hoverAuthMenu = (key: 'modules' | 'resources' | 'menu') => {
    if (isModulesOpen) openAuthMenu(key);
  };

  /** User dropdown mega menu — click toggles; openMenuSection drives
   *  which section renders in the right pane. */
  const openUserMenu = () => {
    cancelPublicMenuClose();
    setIsUserMenuOpen(true);
    setOpenMenuSection((prev) => prev ?? 'my-corner');
  };
  const toggleUserMenu = () => {
    if (isUserMenuOpen) {
      setIsUserMenuOpen(false);
      setOpenMenuSection(null);
    } else {
      openUserMenu();
    }
  };
  const schedulePublicMenuClose = () => {
    cancelPublicMenuClose();
    publicMenuCloseTimerRef.current = setTimeout(() => setPublicMenu(null), 180);
  };

  /** Industries menu entries — catalog solutions unioned with published
   *  industry-* site pages; catalog order (sort_order, name) is preserved. */
  const industryMenuEntries = useMemo<IndustrySolutionNavItem[]>(() => {
    const byHref = new Map<string, IndustrySolutionNavItem>();
    for (const s of industrySolutions) byHref.set(s.href, s);
    for (const p of sitePageLinks) {
      if (!p.page_key.startsWith('industry-')) continue;
      const href = `/products/${p.page_key.slice('industry-'.length)}`;
      if (!byHref.has(href)) {
        byHref.set(href, {
          href,
          label: p.page_name.replace(/^Industry Solution:\s*/i, ''),
          industry: '',
          icon: 'Building2',
        });
      }
    }
    return [...byHref.values()];
  }, [industrySolutions, sitePageLinks]);

  /** Published site pages on other routes not already hard-coded into the menus. */
  const otherSitePageLinks = useMemo(
    () =>
      sitePageLinks.filter(
        (p) =>
          !p.route.startsWith('/products') &&
          !p.route.startsWith('/client-zone') &&
          !KNOWN_MENU_ROUTES.has(p.route)
      ),
    [sitePageLinks]
  );

  /** Two-pane menu item lists — one shape for all three panels. */
  const productNavItems = useMemo<NavPreviewItem[]>(
    () =>
      productMenuChildren.map((c) => ({
        href: c.href,
        label: c.label,
        faIcon: c.icon,
        description: c.description,
      })),
    [productMenuChildren]
  );

  const industryNavItems = useMemo<NavPreviewItem[]>(
    () =>
      industryMenuEntries.map((s) => ({
        href: s.href,
        label: s.label,
        subtitle: s.industry || undefined,
        icon: s.icon,
        color: s.color,
        description: s.description,
        bullets: s.features,
      })),
    [industryMenuEntries]
  );

  const resourceNavItems = useMemo<NavPreviewItem[]>(() => {
    const base: NavPreviewItem[] = [
      {
        href: '/resources',
        label: 'Resources',
        faIcon: 'fa-book-open',
        description: 'Guides, videos and documentation for getting the most out of the Total Access platform.',
      },
      {
        href: '/case-studies',
        label: 'Case Studies',
        faIcon: 'fa-briefcase',
        description: 'How South African businesses streamline operations on Total Access.',
      },
      {
        href: '/support',
        label: 'Support',
        faIcon: 'fa-life-ring',
        description: 'Help centre, system status, and ways to reach the team during local business hours.',
      },
      {
        href: '/legal',
        label: 'Legal',
        faIcon: 'fa-balance-scale',
        description: 'Terms of service, privacy policy, and POPIA compliance information.',
      },
      {
        href:
          process.env.NODE_ENV === 'production'
            ? 'https://developer.totalaccess.co.za/'
            : 'https://dev-developer.totalaccess.co.za',
        label: 'Developer Docs',
        faIcon: 'fa-code',
        description: 'API references, integration guides, and platform technical documentation.',
      },
    ];
    for (const page of otherSitePageLinks) {
      base.splice(base.length - 1, 0, {
        href: page.route,
        label: page.page_name,
        faIcon: 'fa-file-alt',
        description: 'Published page on the Total Access website.',
      });
    }
    return base;
  }, [otherSitePageLinks]);

  /** The item rendered in the right preview pane for the open menu. */
  const activeNavPreview = useMemo<NavPreviewItem | null>(() => {
    const list =
      publicMenu === 'products'
        ? productNavItems
        : publicMenu === 'industries'
          ? industryNavItems
          : publicMenu === 'resources'
            ? resourceNavItems
            : publicMenu === 'get-started'
              ? GET_STARTED_NAV_ITEMS
              : [];
    if (list.length === 0) return null;
    const hovered =
      list.find((i) => i.href === navPreviewHref) ??
      (navPreviewHref ? PUBLIC_NAV_META_PREVIEWS[navPreviewHref] : undefined);
    if (hovered) return hovered;
    if (publicMenu === 'products' && spotlight) {
      return {
        href: spotlight.href,
        label: spotlight.label,
        subtitle: `Featured ${spotlight.kind}`,
        faIcon: 'fa-star',
        description: 'Hand-picked this week — see what is new on the platform.',
      };
    }
    return list[0];
  }, [publicMenu, navPreviewHref, productNavItems, industryNavItems, resourceNavItems, spotlight]);


  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncColdStartToggle = () => {
      const canShowToggle = canUseColdStartLogToggle();
      setShowColdStartLogToggle(canShowToggle);
      setColdStartLogsEnabled(canShowToggle ? getColdStartLogsEnabled() : false);
    };

    syncColdStartToggle();
    window.addEventListener('storage', syncColdStartToggle);
    window.addEventListener(COLDSTART_LOGS_TOGGLED_EVENT, syncColdStartToggle as EventListener);

    return () => {
      window.removeEventListener('storage', syncColdStartToggle);
      window.removeEventListener(COLDSTART_LOGS_TOGGLED_EVENT, syncColdStartToggle as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!mounted || !installedPwa) {
      return;
    }

    let isCancelled = false;

    const loadPwaVersionInfo = async () => {
      setPwaVersionInfo((prev) => ({
        ...prev,
        loading: true,
      }));

      try {
        const deploymentInfo = await fetchInstalledPwaDeploymentVersionInfo();

        if (isCancelled) {
          return;
        }

        setPwaVersionInfo({
          version: deploymentInfo.version,
          deployedAt: deploymentInfo.deployedAtLabel,
          loading: false,
        });
      } catch (error) {
        if (!isSilentFetchError(error)) console.error('[Header] Failed to load PWA version info:', error);

        if (isCancelled) {
          return;
        }

        setPwaVersionInfo({
          version: 'Unavailable',
          deployedAt: 'Unavailable',
          loading: false,
        });
      }
    };

    loadPwaVersionInfo();

    return () => {
      isCancelled = true;
    };
  }, [installedPwa, mounted]);

  const runInstalledPwaRefresh = useCallback(async ({
    closeMenu = true,
    acknowledgedVersion,
  }: {
    closeMenu?: boolean;
    acknowledgedVersion?: string;
  } = {}) => {
    const versionToAcknowledge = acknowledgedVersion && acknowledgedVersion !== 'Unavailable'
      ? acknowledgedVersion
      : undefined;

    setPwaUpdateStatus('checking');
    if (closeMenu) {
      setIsMenuOpen(false);
    }

    toast.dismiss(INSTALLED_PWA_UPDATE_TOAST_ID);

    const { showSplash, hideSplash } = await import('@/lib/splashScreenService');
    showSplash({
      message: 'Checking for updates...',
      subMessage: 'Refreshing the installed web app if a newer build is available',
    });

    try {
      if (versionToAcknowledge) {
        acknowledgeInstalledPwaDeploymentVersion(versionToAcknowledge);
      }

      const refreshResult = await checkForInstalledPwaUpdateAndRefresh();

      setPwaUpdateStatus(refreshResult.updateFound ? 'updated' : 'refreshing');

      window.setTimeout(() => {
        window.location.href = '/client-zone/dashboard';
      }, refreshResult.updateFound ? 400 : 900);
    } catch (error) {
      console.error('[Header] Installed PWA update check failed:', error);
      if (versionToAcknowledge) {
        clearAcknowledgedInstalledPwaDeploymentVersion(versionToAcknowledge);
      }
      setPwaUpdateStatus('error');
      hideSplash({ force: true });
      window.setTimeout(() => {
        setPwaUpdateStatus('idle');
      }, 3000);
    }
  }, []);

  useEffect(() => {
    if (!mounted || !installedPwa || pwaVersionInfo.loading || isInClientZone) {
      toast.dismiss(INSTALLED_PWA_UPDATE_TOAST_ID);
      return;
    }

    const deployedVersion = pwaVersionInfo.version;
    if (!deployedVersion || deployedVersion === 'Unavailable' || typeof window === 'undefined') {
      return;
    }

    const acknowledgedVersion = readAcknowledgedInstalledPwaDeploymentVersion();

    if (!acknowledgedVersion) {
      acknowledgeInstalledPwaDeploymentVersion(deployedVersion);
      toast.dismiss(INSTALLED_PWA_UPDATE_TOAST_ID);
      return;
    }

    if (acknowledgedVersion === deployedVersion || pwaUpdateStatus === 'checking' || pwaUpdateStatus === 'refreshing') {
      toast.dismiss(INSTALLED_PWA_UPDATE_TOAST_ID);
      return;
    }

    toast.custom((toastInstance) => (
      <div
        className={`${toastInstance.visible ? 'animate-enter' : 'animate-leave'} pointer-events-auto w-full max-w-md overflow-hidden rounded-2xl border border-blue-200 bg-slate-950 text-white shadow-2xl ring-1 ring-black/20`}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-blue-600/90">
              <RefreshCw className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">New web app build available</p>
              <p className="mt-1 text-sm text-slate-300">
                TotalAccess has a newer installed PWA build ready. Refresh now to load the latest Smart Actions and fixes.
              </p>
              <p className="mt-2 text-xs text-slate-400">
                Latest deploy v{deployedVersion} • {pwaVersionInfo.deployedAt}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                acknowledgeInstalledPwaDeploymentVersion(deployedVersion);
                toast.dismiss(toastInstance.id);
              }}
              className="rounded-full p-1.5 text-slate-400 transition hover:bg-white/10 hover:text-white"
              aria-label="Dismiss update prompt"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                toast.dismiss(toastInstance.id);
                void runInstalledPwaRefresh({
                  closeMenu: false,
                  acknowledgedVersion: deployedVersion,
                });
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-400"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh now
            </button>
            <button
              type="button"
              onClick={() => {
                acknowledgeInstalledPwaDeploymentVersion(deployedVersion);
                toast.dismiss(toastInstance.id);
              }}
              className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition hover:bg-white/5"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    ), {
      id: INSTALLED_PWA_UPDATE_TOAST_ID,
      duration: Infinity,
      position: 'top-center',
    });

    return () => {
      toast.dismiss(INSTALLED_PWA_UPDATE_TOAST_ID);
    };
  }, [
    installedPwa,
    isInClientZone,
    mounted,
    pwaUpdateStatus,
    pwaVersionInfo.deployedAt,
    pwaVersionInfo.loading,
    pwaVersionInfo.version,
    runInstalledPwaRefresh,
  ]);

  useEffect(() => {
    const syncNavigationOverride = () => {
      if (typeof window === 'undefined') return;
      setHideTotalOperationsNavigation(localStorage.getItem(SMART_TASK_NAV_OVERRIDE_KEY) === 'true');
    };

    syncNavigationOverride();
    window.addEventListener('storage', syncNavigationOverride);
    window.addEventListener(SMART_TASK_NAV_OVERRIDE_CHANGED_EVENT, syncNavigationOverride as EventListener);

    return () => {
      window.removeEventListener('storage', syncNavigationOverride);
      window.removeEventListener(SMART_TASK_NAV_OVERRIDE_CHANGED_EVENT, syncNavigationOverride as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!pathname) {
      setCurrentModule(null);
      return;
    }

    // Check if user is in client-zone modules
    const modulesMatch = pathname.match(/\/client-zone\/modules\/([^\/]+)/);
    if (modulesMatch) {
      const moduleKey = modulesMatch[1];
      const moduleLabel = MODULE_MAP[moduleKey];
      if (moduleLabel) {
        setCurrentModule({ key: moduleKey, label: moduleLabel });
        return;
      }
    }

    // Not in a module
    setCurrentModule(null);
  }, [pathname]);

  // Prevent body scroll when mobile menu is open (Capacitor only)
  useEffect(() => {
    if (!showMobileSyncBadge) return;

    if (isMenuOpen) {
      // Prevent body scrolling when menu is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      // Restore body scrolling when menu is closed
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isMenuOpen, showMobileSyncBadge]);

  // Check if user has operations module access (all platforms)
  useEffect(() => {
    if (!isAuthenticated || !navigationData) {
      setHasOperationsAccess(false);
      setHasSmartTaskDashboardAccess(false);
      setHasEssAccess(false);
      return;
    }

    // Check navigation groups for total-operations module
    const hasAccess = Object.values(navigationGroups).some(group =>
      group.items?.some((item: NavigationItem) => item.module_key === 'total-operations')
    );
    setHasOperationsAccess(hasAccess);

    // Check navigation groups for total-connect module (for email icon)
    const hasConnect = Object.values(navigationGroups).some(group =>
      group.items?.some((item: NavigationItem) => item.module_key === 'total-connect')
    );
    setHasConnectAccess(hasConnect);

    // Employee Self-Service — same gate as the dashboard ESS tile:
    // total-workforce or total-attendance, or historical (ex-employee) access.
    const hasEss = Object.values(navigationGroups).some(group =>
      group.items?.some((item: NavigationItem) =>
        item.module_key === 'total-workforce' || item.module_key === 'total-attendance'
      )
    );
    setHasEssAccess(hasEss || !!navigationData?.historical_access);
  }, [isAuthenticated, navigationData, navigationGroups]);

  // Key the feature-settings effect on the active client id (a stable primitive),
  // NOT the navigationData object — navigationData gets a new reference on every
  // background refresh, and this effect calls refreshNavigation() when the stored
  // SmartTask override flag disagrees with /operations/my-features. Depending on
  // the object reference turned that into a self-feeding loop: refresh → new
  // navigationData → effect refires → mismatch → refresh → ...
  const navigationClientId = navigationData?.client_info?.id;

  // Fetch user feature settings when authenticated and inside the app shell —
  // feature settings only drive app navigation, so public pages skip the call.
  useEffect(() => {
    if (!isAuthenticated || !mounted || !isAppShellRoute(pathname)) {
      setUserFeatureSettings(null);
      setHasSmartTaskDashboardAccess(false);
      setHideTotalOperationsNavigation(false);
      return;
    }

    const fetchFeatureSettings = async () => {
      try {
        const token = localStorage.getItem('token') || (() => {
          const authTokens = localStorage.getItem('auth_tokens');
          if (authTokens) return JSON.parse(authTokens).access_token;
          return null;
        })();

        if (!token) {
          setHasSmartTaskDashboardAccess(false);
          return;
        }

        const response = await fetch('/api/v1/operations/my-features', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.feature_settings) {
            // API returns feature_settings as nested object
            setUserFeatureSettings(data.feature_settings);

            const nextHasSmartTaskDashboardAccess = Boolean(
              data.hide_total_operations_navigation
            );
            setHasSmartTaskDashboardAccess(nextHasSmartTaskDashboardAccess);

            const nextHideTotalOperationsNavigation = Boolean(data.hide_total_operations_navigation);
            setHideTotalOperationsNavigation(nextHideTotalOperationsNavigation);

            const storedOverride = localStorage.getItem(SMART_TASK_NAV_OVERRIDE_KEY) === 'true';
            if (storedOverride !== nextHideTotalOperationsNavigation) {
              localStorage.setItem(SMART_TASK_NAV_OVERRIDE_KEY, nextHideTotalOperationsNavigation ? 'true' : 'false');
              window.dispatchEvent(new CustomEvent(SMART_TASK_NAV_OVERRIDE_CHANGED_EVENT, {
                detail: { hideTotalOperationsNavigation: nextHideTotalOperationsNavigation }
              }));
              refreshNavigation().catch((error) => {
                if (!isSilentFetchError(error)) console.error('[Header] Failed to refresh navigation after Smart Task override change:', error);
              });
            }
          } else if (data.success && !data.has_access) {
            // User doesn't have operations module access - show all icons by default
            setUserFeatureSettings(null);
            setHasSmartTaskDashboardAccess(false);
            setHideTotalOperationsNavigation(false);
            localStorage.setItem(SMART_TASK_NAV_OVERRIDE_KEY, 'false');
            window.dispatchEvent(new CustomEvent(SMART_TASK_NAV_OVERRIDE_CHANGED_EVENT, {
              detail: { hideTotalOperationsNavigation: false }
            }));
          }
        }
      } catch (error) {
        if (!isSilentFetchError(error)) console.error('[Header] Failed to fetch user feature settings:', error);
        setHasSmartTaskDashboardAccess(false);
      }
    };

    fetchFeatureSettings();
  }, [isAuthenticated, mounted, pathname, navigationClientId, refreshNavigation]);

  // Note: Missed calls count is now managed by BadgeContext

  // Incoming call state for operations calls (from push notifications)
  const [incomingOperationsCall, setIncomingOperationsCall] = useState<HeaderIncomingCall | null>(null);

  // Pending call target for auto-starting calls from Total Connect
  const [pendingCallTarget, setPendingCallTarget] = useState<PendingCallTarget | null>(null);

  // Pending group call for auto-starting group/team calls
  const [pendingGroupCall, setPendingGroupCall] = useState<PendingGroupCall | null>(null);

  const closeCallsModal = useCallback(() => {
    setIsCallModalOpen(false);
    setIncomingOperationsCall(null);
    setPendingCallTarget(null);
    setPendingGroupCall(null);
    window.dispatchEvent(new CustomEvent('capacitor-footer-modal-closed', { detail: { tab: 'calls' } }));
  }, []);

  const ensureCallModalOpenForColdStart = useCallback(() => {
    setIsCallModalOpen(true);
  }, []);

  const returnToDashboardFromConnect = useCallback((tab: 'messages' | 'email' | 'calendar' | 'calls') => {
    switch (tab) {
      case 'messages':
        closeMessagesModal();
        break;
      case 'email':
        closeEmailModal();
        break;
      case 'calendar':
        closeCalendarModal();
        break;
      case 'calls':
        closeCallsModal();
        break;
    }

    window.dispatchEvent(new CustomEvent('capacitor-footer-exit-connect-mode'));

    if (isOnDashboard) {
      window.scrollTo(0, 0);
      window.dispatchEvent(new CustomEvent('capacitor-footer-dashboard'));
      return;
    }

    router.push('/client-zone/dashboard');
  }, [closeCalendarModal, closeCallsModal, closeEmailModal, closeMessagesModal, isOnDashboard, router]);

  const parseCallClientId = useCallback((value: unknown): number | undefined => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, []);

  // Listen for incoming call events from push notifications (all platforms)
  // Note: We listen even without hasOperationsAccess because users can still RECEIVE calls
  useEffect(() => {
    // Helper: reconstruct SIP data from FCM flat fields or nested sip_data
    const extractSipData = (data: IncomingCallNotificationPayload) => {
      const isSipCall = data.is_sip_call === true || data.is_sip_call === 'true' ||
        data.call_type === 'sip_inbound' || data.call_id?.startsWith('sip-inbound-');
      if (!isSipCall) return { isSipCall: false, sipData: undefined };
      // FCM flattens sip_data into sip_* string fields; WebSocket sends nested sip_data object
      const nested: Partial<SipCallData> =
        data.sip_data &&
          typeof data.sip_data === 'object' &&
          !Array.isArray(data.sip_data)
          ? data.sip_data
          : {};
      return {
        isSipCall: true,
        sipData: mergePushFlatSipData(nested, data),
      };
    };

    const handleIncomingCall = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      console.log('[Header] Incoming call event:', data);

      const { isSipCall, sipData } = extractSipData(data);

      // Set incoming call data and open modal
      setIncomingOperationsCall({
        call_id: data.call_id,
        caller_id: parseInt(data.caller_id),
        caller_name: data.caller_name || 'Unknown',
        caller_avatar: data.caller_avatar,
        call_type: data.call_type || 'voice',
        client_id: parseCallClientId(data.clientId ?? data.client_id),
        timestamp: data.timestamp || new Date().toISOString(),
        isSipCall,
        caller_number: sipData?.caller_number,
        sipData,
      });
      setIsCallModalOpen(true);
    };

    const handleIncomingCallTapped = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      console.log('[Header] Incoming call notification tapped:', data);

      const { isSipCall, sipData } = extractSipData(data);

      // Set incoming call data and open modal
      setIncomingOperationsCall({
        call_id: data.call_id,
        caller_id: parseInt(data.caller_id),
        caller_name: data.caller_name || 'Unknown',
        caller_avatar: data.caller_avatar,
        call_type: data.call_type || 'voice',
        client_id: parseCallClientId(data.clientId ?? data.client_id),
        timestamp: data.timestamp || new Date().toISOString(),
        isSipCall,
        caller_number: sipData?.caller_number,
        sipData,
      });
      setIsCallModalOpen(true);

      // Hide the fast-track call connecting UI if it was shown during cold start
      if (typeof window !== 'undefined') {
        (window as HeaderWindow).__hideCallConnectingUI?.();
      }
    };

    // Handle when user taps "Answer" directly from notification action button
    const handleIncomingCallAnswered = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      console.log('[Header] Incoming call answered from notification:', data);

      // Clear the pending call answer from storage
      try {
        sessionStorage.removeItem('pending_call_answer');
      } catch (e) { }

      const { isSipCall, sipData } = extractSipData(data);

      // Set incoming call data with autoAnswer flag - modal will auto-answer
      setIncomingOperationsCall({
        call_id: data.call_id,
        caller_id: parseInt(data.caller_id),
        caller_name: data.caller_name || 'Unknown',
        caller_avatar: data.caller_avatar,
        call_type: data.call_type || 'voice',
        client_id: parseCallClientId(data.clientId ?? data.client_id),
        timestamp: data.timestamp || new Date().toISOString(),
        autoAnswer: true, // Auto-answer when modal opens
        isSipCall,
        caller_number: sipData?.caller_number,
        sipData,
      });
      setIsCallModalOpen(true);

      // Hide the fast-track call connecting UI if it was shown during cold start
      if (typeof window !== 'undefined') {
        (window as HeaderWindow).__hideCallConnectingUI?.();
      }
    };

    // Handle when user taps "Decline" directly from notification action button
    const handleIncomingCallDeclined = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      console.log('[Header] Incoming call declined from notification:', data);

      const d = data as Record<string, unknown>;
      const cid = d.call_id != null ? String(d.call_id) : '';
      const lid = d.call_log_id ?? d.sip_call_log_id;
      addDeclinedIncomingCallIds([cid || null, lid != null ? String(lid) : null]);

      // Close modal if open
      setIncomingOperationsCall(null);
      setIsCallModalOpen(false);

      // Try to send reject via the shared WebSocket client.
      // The modal's signaling may not be active, so ensure the shared client is connected first.
      try {
        await wsClient.connect();

        const detail = data as Record<string, unknown>;
        const callId = String(detail.call_id ?? '');
        const callLogId = detail.call_log_id ?? detail.sip_call_log_id;
        const ct = detail.call_type;
        const isSipFlag =
          detail.is_sip_call === true ||
          detail.is_sip_call === 'true';
        const isSipInbound =
          isSipFlag ||
          ct === 'sip_inbound' ||
          callId.startsWith('sip-inbound-');

        const payload = isSipInbound
          ? {
            type: 'reject_sip_call' as const,
            call_id: callId,
            ...(callLogId ? { call_log_id: String(callLogId) } : {}),
          }
          : { type: 'reject_operations_call' as const, call_id: callId };

        const sent = wsClient.send(payload);

        if (!sent) {
          throw new Error('Shared WebSocket client was not ready to send call rejection');
        }

        console.log('[Header] Sent call rejection via shared WebSocket client', payload.type);
      } catch (e) {
        console.error('[Header] Error sending call rejection:', e);
      }
    };

    const handleCallDismissed = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      console.log('[Header] Call dismissed event:', data);

      // Close modal if it's for this call
      if (incomingOperationsCall?.call_id === data.call_id) {
        setIncomingOperationsCall(null);
        setIsCallModalOpen(false);
      }
    };

    // Handle openCallModal event from EmbeddedCalls (Total Connect)
    const handleOpenCallModal = (event: Event) => {
      const customEvent = event as CustomEvent;
      const detail = customEvent.detail;
      console.log('[Header] openCallModal event received:', detail);

      // Clear any previous incoming call state
      setIncomingOperationsCall(null);

      // If detail contains targetUser and autoStart, store it for auto-starting
      if (detail?.targetUser && detail?.autoStart) {
        console.log('[Header] Setting pending call target for auto-start:', detail.targetUser);
        setPendingCallTarget({
          user: detail.targetUser,
          callType: detail.callType || 'voice'
        });
      } else {
        // Clear any previous pending target
        setPendingCallTarget(null);
      }

      setIsCallModalOpen(true);
    };

    // Handle openGroupCallModal event from MessagesModalNew (team/group calls)
    const handleOpenGroupCallModal = (event: Event) => {
      const customEvent = event as CustomEvent;
      const detail = customEvent.detail;
      console.log('[Header] openGroupCallModal event received:', detail);

      // Clear any previous call states
      setIncomingOperationsCall(null);
      setPendingCallTarget(null);

      // Set pending group call (or meeting)
      if (detail?.threadId && detail?.participantIds?.length > 0) {
        console.log('[Header] Setting pending group call:', detail);
        setPendingGroupCall({
          threadId: detail.threadId,
          threadName: detail.threadName || 'Group Call',
          callType: detail.callType || 'voice',
          participantIds: detail.participantIds,
          isMeeting: detail.isMeeting || false,
          initiatorId: typeof detail.initiatorId === 'number' ? detail.initiatorId : undefined,
          callId: typeof detail.callId === 'string' ? detail.callId : undefined,
        });
        setIsCallModalOpen(true);
      }
    };

    window.addEventListener('incomingCall', handleIncomingCall);
    window.addEventListener('incomingCallTapped', handleIncomingCallTapped);
    window.addEventListener('incomingCallAnswered', handleIncomingCallAnswered);
    window.addEventListener('incomingCallDeclined', handleIncomingCallDeclined);
    window.addEventListener('callDismissed', handleCallDismissed);
    window.addEventListener('openCallModal', handleOpenCallModal);
    window.addEventListener('openGroupCallModal', handleOpenGroupCallModal);

    // Mark listeners as ready and replay any queued events (from native layer)
    // This handles the case where native Android dispatches events before React mounts
    if (typeof window !== 'undefined') {
      (window as HeaderWindow).__markIncomingCallListenerReady?.();
    }

    // Check for pending call answer stored by native layer during cold start
    // This handles the case where user tapped "Answer" but app had to fully reload
    // Check if TotalConnectRTC is already handling a cold-start call
    try {
      // This flag survives component remounts and prevents duplicate handling
      const handlingFlag = localStorage.getItem('coldstart_call_handling');
      if (handlingFlag) {
        const { callId, timestamp } = JSON.parse(handlingFlag);
        const age = Date.now() - timestamp;
        if (age < 30000) {
          console.log('[Header] 📞 TotalConnectRTC already handling cold-start call:', callId);
          // Don't process here - TotalConnectRTC is handling it
          return;
        } else {
          // Stale flag, clear it
          localStorage.removeItem('coldstart_call_handling');
        }
      }

      // Skip if TotalConnectRTC's cold-start handler will handle it
      const windowPending = typeof window !== 'undefined' ? (window as HeaderWindow).__pendingCallAnswer : null;
      if (windowPending) {
        console.log('[Header] 📞 window.__pendingCallAnswer exists, TotalConnectRTC will handle it');
        // Don't process here - let TotalConnectRTC handle it
      } else {
        const pendingAnswerJson = sessionStorage.getItem('pending_call_answer');
        if (pendingAnswerJson) {
          const pendingAnswer = JSON.parse(pendingAnswerJson);
          const age = Date.now() - (pendingAnswer.timestamp || 0);

          if (age < 30000) { // Within 30 seconds
            console.log('[Header] 📞 Found pending call answer from cold start:', pendingAnswer);
            sessionStorage.removeItem('pending_call_answer');

            // Also clear window.__pendingCallAnswer to prevent duplicate handling
            if (typeof window !== 'undefined') {
              (window as HeaderWindow).__pendingCallAnswer = null;
            }

            // Open call modal with auto-answer
            const { isSipCall: pendingSipCall, sipData: pendingSipData } = extractSipData(pendingAnswer);
            setIncomingOperationsCall({
              call_id: pendingAnswer.callId,
              caller_id: parseInt(pendingAnswer.callerId) || 0,
              caller_name: pendingAnswer.callerName || 'Unknown',
              caller_avatar: pendingAnswer.callerAvatar,
              call_type: pendingAnswer.callType || 'voice',
              client_id: parseCallClientId(pendingAnswer.clientId ?? pendingAnswer.client_id),
              timestamp: new Date().toISOString(),
              autoAnswer: true,
              isSipCall: pendingSipCall || pendingAnswer.callId?.startsWith('sip-inbound-'),
              caller_number: pendingSipData?.caller_number,
              sipData: pendingSipData || pendingAnswer.sipData,
            });
            setIsCallModalOpen(true);

            // Hide the fast-track UI
            (window as HeaderWindow).__hideCallConnectingUI?.();
          } else {
            console.log('[Header] 📞 Pending call answer expired, clearing');
            sessionStorage.removeItem('pending_call_answer');
          }
        }
      }
    } catch (e) {
      console.error('[Header] Error checking for pending call answer:', e);
    }

    // Check for pending email/message navigation from cold start
    // Similar to call handling, use persistent localStorage flag to prevent duplicate handling
    try {
      // Skip if another instance is already handling navigation
      const handlingFlag = localStorage.getItem('coldstart_navigation_handling');
      if (handlingFlag) {
        const { type, id, timestamp } = JSON.parse(handlingFlag);
        const age = Date.now() - timestamp;
        if (age < 30000) {
          console.log(`[Header] 🔄 Another instance already handling ${type} navigation:`, id);
          return;
        } else {
          // Stale flag, clear it
          localStorage.removeItem('coldstart_navigation_handling');
        }
      }

      // Check window.__pendingNavigation set by capacitor-startup.js
      const windowPending = typeof window !== 'undefined' ? (window as HeaderWindow).__pendingNavigation : null;
      if (windowPending) {
        console.log('[Header] 🔄 Found pending navigation from cold start:', windowPending);

        // Set persistent flag to prevent duplicate handling
        localStorage.setItem('coldstart_navigation_handling', JSON.stringify({
          type: windowPending.type,
          id: windowPending.threadId || windowPending.emailId,
          timestamp: Date.now()
        }));

        // Clear the window flag
        (window as HeaderWindow).__pendingNavigation = null;

        // Handle based on type
        if (windowPending.type === 'message' && windowPending.threadId) {
          console.log('[Header] 💬 Opening messages modal for thread:', windowPending.threadId);
          setPendingMessagesThreadId(windowPending.threadId);
          toggleModal('messages');

          // Clear pending data
          sessionStorage.removeItem('pending_message_navigation');
          localStorage.removeItem('deeplink_pending_message_navigation');

          // Clear handling flag after successful handling
          setTimeout(() => {
            localStorage.removeItem('coldstart_navigation_handling');
          }, 1000);

        } else if (windowPending.type === 'email' && windowPending.emailId) {
          console.log('[Header] 📧 Opening email modal for email:', windowPending.emailId);
          setPendingEmailId(windowPending.emailId);

          // Store pending_email_open so EnhancedEmailModule can open the specific thread
          // (it reads this key on init, not pending_email_navigation)
          try {
            sessionStorage.setItem('pending_email_open', JSON.stringify({
              emailId: windowPending.emailId,
              threadId: windowPending.threadId || '',
              folder: 'INBOX',
              timestamp: Date.now()
            }));
          } catch (_e) {}

          openEmailExperience();

          // Clear pending data
          sessionStorage.removeItem('pending_email_navigation');
          localStorage.removeItem('deeplink_pending_email_navigation');

          // Clear handling flag after successful handling
          setTimeout(() => {
            localStorage.removeItem('coldstart_navigation_handling');
          }, 1000);
        }

        // Hide the navigation UI
        if (typeof window !== 'undefined') {
          (window as HeaderWindow).__hideNavigationUI?.();
        }
      }
    } catch (e) {
      console.error('[Header] Error checking for pending navigation:', e);
      localStorage.removeItem('coldstart_navigation_handling');
    }

    // Check for pending shared content from cold start (Android share intent)
    try {
      // Check window.__pendingSharedContent set by capacitor-startup.js
      const windowShare = typeof window !== 'undefined' ? (window as HeaderWindow).__getPendingSharedContent?.() : null;
      if (windowShare && (windowShare.type === 'text' || windowShare.type === 'files')) {
        console.log('[Header] 📤 Found pending shared content from cold start:', windowShare.type);

        // Store the shared content to pass to MessagesModal
        setPendingSharedContent(windowShare);

        // Open messages modal
        if (activeModal !== 'messages') {
          toggleModal('messages');
        }

        // Clear the pending content from capacitor-startup.js
        (window as HeaderWindow).__clearPendingSharedContent?.();

        // Hide the share loading UI
        (window as HeaderWindow).__hideShareUI?.();
      } else {
        // Also check localStorage/sessionStorage directly (backup)
        const pendingShareJson = sessionStorage.getItem('pendingSharedContent') || localStorage.getItem('coldstart_pending_share');
        if (pendingShareJson) {
          try {
            const shareData = JSON.parse(pendingShareJson);
            const age = Date.now() - (shareData.timestamp || Date.now());
            if (age < 120000 && (shareData.type === 'text' || shareData.type === 'files')) { // 2 minutes
              console.log('[Header] 📤 Found pending shared content from storage:', shareData.type);

              // Store the shared content to pass to MessagesModal
              setPendingSharedContent(shareData);

              // Open messages modal
              if (activeModal !== 'messages') {
                toggleModal('messages');
              }

              // Clear storage
              sessionStorage.removeItem('pendingSharedContent');
              localStorage.removeItem('coldstart_pending_share');

              // Hide the share loading UI
              (window as HeaderWindow).__hideShareUI?.();
            }
          } catch (parseErr) {
            console.error('[Header] Error parsing pending share data:', parseErr);
            sessionStorage.removeItem('pendingSharedContent');
            localStorage.removeItem('coldstart_pending_share');
          }
        }
      }
    } catch (e) {
      console.error('[Header] Error checking for pending shared content:', e);
    }

    return () => {
      window.removeEventListener('incomingCall', handleIncomingCall);
      window.removeEventListener('incomingCallTapped', handleIncomingCallTapped);
      window.removeEventListener('incomingCallAnswered', handleIncomingCallAnswered);
      window.removeEventListener('incomingCallDeclined', handleIncomingCallDeclined);
      window.removeEventListener('callDismissed', handleCallDismissed);
      window.removeEventListener('openCallModal', handleOpenCallModal);
      window.removeEventListener('openGroupCallModal', handleOpenGroupCallModal);
      // Mark as not ready on unmount so events queue again
      if (typeof window !== 'undefined') {
        (window as HeaderWindow).__incomingCallListenerReady = false;
      }
    };
  }, [activeModal, incomingOperationsCall, openEmailExperience, parseCallClientId, toggleModal]);

  // Check for pending incoming call from deep link (Capacitor)
  // capacitor-startup.js stores call data in sessionStorage and redirects to dashboard
  useEffect(() => {
    if (!mounted) return;

    try {
      const pendingCallJson = sessionStorage.getItem('pendingIncomingCall');
      if (pendingCallJson) {
        const pendingCall = JSON.parse(pendingCallJson);
        console.log('[Header] 📞 Found pending incoming call from deep link:', pendingCall);

        // Clear it immediately to prevent re-processing
        sessionStorage.removeItem('pendingIncomingCall');

        // Check if call is still recent (within last 60 seconds)
        const callTimestamp = new Date(pendingCall.timestamp).getTime();
        const now = Date.now();
        const ageSeconds = (now - callTimestamp) / 1000;

        if (ageSeconds > 60) {
          console.log('[Header] 📞 Pending call is too old, ignoring:', ageSeconds, 'seconds');
          return;
        }

        // Open the call modal with the pending call data
        setIncomingOperationsCall({
          call_id: pendingCall.call_id,
          caller_id: parseInt(pendingCall.caller_id) || 0,
          caller_name: pendingCall.caller_name || 'Unknown',
          caller_avatar: pendingCall.caller_avatar,
          call_type: pendingCall.call_type || 'voice',
          client_id: parseCallClientId(pendingCall.clientId ?? pendingCall.client_id),
          timestamp: pendingCall.timestamp
        });
        setIsCallModalOpen(true);

        console.log('[Header] 📞 Opened call modal for deep link call');
      }
    } catch (e) {
      console.error('[Header] Error checking for pending incoming call:', e);
    }
  }, [mounted, parseCallClientId]);

  // Listen for toggleModal and openMessagesModal events (from FCM notification deep links)
  // These events are dispatched by capacitorDeviceService and CapacitorStartupSuccess
  useEffect(() => {
    if (!mounted) return;

    // Handle toggleModal event - opens the specified modal
    const handleToggleModal = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { modalName } = customEvent.detail || {};
      console.log('[Header] 📬 toggleModal event received:', modalName);

      if (modalName === 'email') {
        toggleEmailExperience();
        return;
      }

      if (
        modalName === 'messages' ||
        modalName === 'tasks' ||
        modalName === 'assistant' ||
        modalName === 'calendar' ||
        modalName === 'myshell'
      ) {
        toggleModal(modalName);
      }
    };

    // Handle openMessagesModal event - opens messages modal and navigates to specific thread
    const handleOpenMessagesModal = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { threadId, userId } = customEvent.detail || {};
      console.log('[Header] 📬 openMessagesModal event received, threadId:', threadId, 'userId:', userId);

      if (threadId) {
        // Store the thread ID to pass to MessagesModal
        setPendingMessagesThreadId(threadId);

        // Clear the pending thread ID after a delay to allow MessagesModal to use it
        // but prevent it from being used again on re-renders
        setTimeout(() => {
          setPendingMessagesThreadId(null);
        }, 2000);
      }

      if (typeof userId === 'number' && userId > 0) {
        // Direct-message deep link: let MessagesModal resolve the direct thread
        setPendingMessagesDirectUserId(userId);
        setTimeout(() => {
          setPendingMessagesDirectUserId(null);
        }, 2000);
      }

      // Ensure messages modal is open
      if (activeModal !== 'messages') {
        toggleModal('messages');
      }
    };

    // Handle sharedContent event - opens messages modal with shared files/text
    const handleSharedContent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const shareData = customEvent.detail;
      console.log('[Header] 📤 sharedContent event received:', shareData);

      if (shareData && (shareData.type === 'text' || shareData.type === 'files')) {
        // Store the shared content to pass to MessagesModal
        setPendingSharedContent(shareData);

        // Ensure messages modal is open
        if (activeModal !== 'messages') {
          toggleModal('messages');
        }
      }
    };

    const normalizeOptionalPositiveNumber = (value: unknown): number | undefined => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    };

    const storePendingEmailOpen = (payload: {
      emailId?: string;
      messageId?: string;
      threadId?: string;
      subject?: string;
      folder?: string;
      accountId?: number | string;
      clientId?: number | string;
    }) => {
      const resolvedEmailId = payload.emailId || payload.messageId;
      if (!resolvedEmailId && !payload.threadId) {
        return;
      }

      sessionStorage.setItem('pending_email_open', JSON.stringify({
        emailId: resolvedEmailId || '',
        threadId: payload.threadId || '',
        subject: payload.subject || '',
        folder: payload.folder || 'INBOX',
        accountId: normalizeOptionalPositiveNumber(payload.accountId),
        clientId: normalizeOptionalPositiveNumber(payload.clientId),
        timestamp: Date.now()
      }));
    };

    // Legacy event name `openSmartConnect` — opens email (deep links / push; component SmartConnect removed)
    const handleOpenSmartConnect = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { tab, emailId, messageId, threadId, folder, accountId, clientId, subject } = customEvent.detail || {};
      console.log('[Header] 📧 openSmartConnect event received:', { tab, emailId: emailId || messageId, threadId, folder, accountId });

      storePendingEmailOpen({ emailId, messageId, threadId, folder, accountId, clientId, subject });

      openEmailExperience();
    };

    // Handle new_email push notification event (from FCM)
    const handleNewEmailNotification = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { email_count, sender_name, subject, message_id, thread_id, folder, account_id, client_id } = customEvent.detail || {};
      console.log('[Header] 📧 new_email event received:', { email_count, sender_name, subject, message_id, account_id });

      storePendingEmailOpen({
        messageId: message_id,
        threadId: thread_id,
        folder,
        accountId: account_id,
        clientId: client_id,
        subject,
      });

      // Open email modal when user taps the notification
      openEmailExperience();
    };

    // Handle openEmailModal event (from cold start navigation)
    const handleOpenEmailModal = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { emailId, messageId, threadId, folder, accountId, clientId, subject } = customEvent.detail || {};
      const resolvedEmailId = emailId || messageId;
      console.log('[Header] 📧 openEmailModal event received:', { emailId: resolvedEmailId, threadId, folder, accountId });

      // Store email context for EmailModal to use
      storePendingEmailOpen({
        emailId: resolvedEmailId,
        threadId,
        folder,
        accountId,
        clientId,
        subject,
      });

      // Open email modal / desktop slide
      openEmailExperience();

      // Hide navigation loading UI
      if (typeof window !== 'undefined') {
        (window as HeaderWindow).__hideNavigationUI?.();
      }
    };

    // Handle new_message push notification event (from FCM)
    const handleNewMessageNotification = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { thread_id, sender_name } = customEvent.detail || {};
      console.log('[Header] 💬 new_message event received:', { thread_id, sender_name });

      // Store thread ID and open messages modal
      if (thread_id) {
        setPendingMessagesThreadId(thread_id);
        setTimeout(() => {
          setPendingMessagesThreadId(null);
        }, 2000);
      }

      // Open messages modal
      if (activeModal !== 'messages') {
        toggleModal('messages');
      }

      // Hide navigation loading UI
      if (typeof window !== 'undefined') {
        (window as HeaderWindow).__hideNavigationUI?.();
      }
    };

    // Handle deepLinkEmail event (from Android MainActivity)
    const handleDeepLinkEmail = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { emailId, messageId, threadId, subject, folder, accountId, clientId } = customEvent.detail || {};
      const resolvedEmailId = emailId || messageId;
      console.log('[Header] 📧 deepLinkEmail event received:', { emailId: resolvedEmailId, threadId, subject, accountId });

      // Store email context for EmailModal to use
      storePendingEmailOpen({
        emailId: resolvedEmailId,
        threadId,
        subject,
        folder,
        accountId,
        clientId,
      });

      // Open email modal / desktop slide
      openEmailExperience();

      // Hide navigation loading UI
      if (typeof window !== 'undefined') {
        (window as HeaderWindow).__hideNavigationUI?.();
      }
    };

    // Handle deepLinkMessage event (from Android MainActivity)
    const handleDeepLinkMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { threadId, senderName } = customEvent.detail || {};
      console.log('[Header] 💬 deepLinkMessage event received:', { threadId, senderName });

      // Store thread ID and open messages modal
      if (threadId) {
        setPendingMessagesThreadId(threadId);
        setTimeout(() => {
          setPendingMessagesThreadId(null);
        }, 2000);
      }

      // Open messages modal
      if (activeModal !== 'messages') {
        toggleModal('messages');
      }

      // Hide navigation loading UI
      if (typeof window !== 'undefined') {
        (window as HeaderWindow).__hideNavigationUI?.();
      }
    };

    // Handle CapacitorFooter connect-mode tab events
    // When user taps a connect tab (Calls, Email, Messages, Calendar), open/close the corresponding modal
    const handleCapacitorConnectTab = (event: Event) => {
      const { tab, action } = (event as CustomEvent).detail || {};
      console.log('[Header] 📱 capacitor-footer-connect-tab:', tab, action);

      if (action === 'open') {
        if (tab === 'calls') {
          setIsCallModalOpen(true);
        } else if (tab === 'email') {
          toggleEmailExperience();
        } else if (tab === 'messages' || tab === 'calendar') {
          toggleModal(tab);
        }
      } else if (action === 'close') {
        if (tab === 'calls') {
          setIsCallModalOpen(false);
          // Also dispatch modal-closed event so CapacitorFooter can sync state
          window.dispatchEvent(new CustomEvent('capacitor-footer-modal-closed', { detail: { tab: 'calls' } }));
        } else if (tab === 'email' || tab === 'messages' || tab === 'calendar' || tab === 'assistant') {
          if (tab === 'email') {
            setIsDesktopEmailSlideOpen(false);
            if (activeModal === 'email') {
              toggleModal('email');
            }
          } else if (activeModal === tab) {
            toggleModal(tab);
          }
        }
      }
    };

    window.addEventListener('toggleModal', handleToggleModal);
    window.addEventListener('openMessagesModal', handleOpenMessagesModal);
    window.addEventListener('sharedContent', handleSharedContent);
    window.addEventListener('openSmartConnect', handleOpenSmartConnect);
    window.addEventListener('new_email', handleNewEmailNotification);
    window.addEventListener('openEmailModal', handleOpenEmailModal);
    window.addEventListener('new_message', handleNewMessageNotification);
    window.addEventListener('deepLinkEmail', handleDeepLinkEmail);
    window.addEventListener('deepLinkMessage', handleDeepLinkMessage);
    window.addEventListener('capacitor-footer-connect-tab', handleCapacitorConnectTab);

    // Mark navigation listeners as ready and replay any queued events
    // This handles cold start scenarios where events were dispatched before React mounted
    if (typeof window !== 'undefined') {
      (window as HeaderWindow).__markNavigationListenerReady?.();
    }

    return () => {
      window.removeEventListener('toggleModal', handleToggleModal);
      window.removeEventListener('openMessagesModal', handleOpenMessagesModal);
      window.removeEventListener('sharedContent', handleSharedContent);
      window.removeEventListener('openSmartConnect', handleOpenSmartConnect);
      window.removeEventListener('new_email', handleNewEmailNotification);
      window.removeEventListener('openEmailModal', handleOpenEmailModal);
      window.removeEventListener('new_message', handleNewMessageNotification);
      window.removeEventListener('deepLinkEmail', handleDeepLinkEmail);
      window.removeEventListener('deepLinkMessage', handleDeepLinkMessage);
      window.removeEventListener('capacitor-footer-connect-tab', handleCapacitorConnectTab);
      // Mark as not ready on unmount so events queue again
      if (typeof window !== 'undefined') {
        (window as HeaderWindow).__navigationListenerReady = false;
      }
    };
  }, [activeModal, mounted, openEmailExperience, showAppLikeMenuLayout, toggleEmailExperience, toggleModal]);

  // Track GPS status for app-like mobile sessions
  useEffect(() => {
    if (!showAppLikeMenuLayout) return;

    let permissionChangeHandler: (() => void) | null = null;
    let permissionStatus: PermissionStatus | null = null;

    const checkGPSStatus = async () => {
      try {
        // Check if Geolocation is available
        if (!('geolocation' in navigator)) {
          setGpsEnabled(false);
          return;
        }

        // Use Permissions API to check status WITHOUT requesting location
        // This avoids the "Only request geolocation in response to user gesture" violation
        if ('permissions' in navigator) {
          try {
            permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

            // Set GPS status based on permission state
            // 'granted' = GPS available, 'denied' = blocked, 'prompt' = not yet asked
            setGpsEnabled(permissionStatus.state === 'granted');

            // Listen for permission changes
            permissionChangeHandler = () => {
              if (permissionStatus) {
                setGpsEnabled(permissionStatus.state === 'granted');
              }
            };
            permissionStatus.addEventListener('change', permissionChangeHandler);
          } catch (e) {
            // Permissions API not fully supported, assume GPS is available
            console.log('[Header] Permissions API not available for geolocation');
            setGpsEnabled(true);
          }
        } else {
          // No Permissions API, assume GPS is available (will be checked when actually needed)
          setGpsEnabled(true);
        }
      } catch (error) {
        console.warn('[Header] GPS status check failed:', error);
        setGpsEnabled(null);
      }
    };

    checkGPSStatus();

    // Re-check periodically
    const intervalId = setInterval(checkGPSStatus, 60000); // Check every minute

    return () => {
      clearInterval(intervalId);
      if (permissionStatus && permissionChangeHandler) {
        permissionStatus.removeEventListener('change', permissionChangeHandler);
      }
    };
  }, [showAppLikeMenuLayout]);

  // Hide header on scroll down, show on scroll up (mobile only)
  useEffect(() => {
    if (!showAppLikeMenuLayout) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Don't hide if at top
      if (currentScrollY < 10) {
        setShowHeader(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      // Scrolling down - hide header
      if (currentScrollY > lastScrollY.current && currentScrollY > 76) {
        setShowHeader(false);
      }
      // Scrolling up - show header
      else if (currentScrollY < lastScrollY.current) {
        setShowHeader(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showAppLikeMenuLayout]);

  useEffect(() => {
    if (!showAppLikeMenuLayout && !desktopHeaderToggleEnabled) {
      return;
    }

    const handleShowHeader = () => {
      setShowHeader(true);
    };

    window.addEventListener('showHeader', handleShowHeader);

    return () => {
      window.removeEventListener('showHeader', handleShowHeader);
    };
  }, [desktopHeaderToggleEnabled, showAppLikeMenuLayout]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const evt = 'client-zone-immersive-header';
    const handler = (e: Event) => {
      setImmersedClientZoneModule((e as CustomEvent<{ hidden?: boolean }>).detail?.hidden === true);
    };

    window.addEventListener(evt, handler as EventListener);
    return () => {
      window.removeEventListener(evt, handler as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!showAppLikeMenuLayout) {
      return;
    }

    const handleFooterHomeTapped = () => {
      setIsMenuOpen(false);
      setIsModulesOpen(false);
      setIsPublicNavOpen(false);
      setIsUserMenuOpen(false);
      setIsClientSelectorOpen(false);
      setIsSearchOpen(false);
    };

    window.addEventListener(CAPACITOR_FOOTER_HOME_TAPPED_EVENT, handleFooterHomeTapped);

    return () => {
      window.removeEventListener(CAPACITOR_FOOTER_HOME_TAPPED_EVENT, handleFooterHomeTapped);
    };
  }, [showAppLikeMenuLayout]);

  // Broadcast header visibility state
  useEffect(() => {
    if (!showAppLikeMenuLayout && !desktopHeaderToggleEnabled) return;

    const event = new CustomEvent('headerVisibilityChange', {
      detail: { visible: showHeader }
    });
    window.dispatchEvent(event);
  }, [desktopHeaderToggleEnabled, showAppLikeMenuLayout, showHeader]);

  // Close search bar when a modal opens
  useEffect(() => {
    if (activeModal) {
      setIsSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      setSearchError(null);
      setSearchTypeFilter(null);
    }
  }, [activeModal]);

  // Also close search bar when any modal/drawer opens (for modals not using ModalStateContext)
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            // Check for common modal/drawer/dialog patterns
            if (
              node.classList?.contains('modal') ||
              node.classList?.contains('drawer') ||
              node.classList?.contains('dialog') ||
              node.getAttribute('role') === 'dialog' ||
              node.getAttribute('aria-modal') === 'true' ||
              node.querySelector('[role="dialog"]') ||
              node.querySelector('[aria-modal="true"]')
            ) {
              setIsSearchOpen(false);
              setSearchQuery('');
              setSearchResults([]);
              setSearchError(null);
              setSearchTypeFilter(null);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, []);

  // Check if we're on a main platform domain (allows public page access even when authenticated)
  const [isMainPlatformDomain, setIsMainPlatformDomain] = useState(false);
  const [isDeveloperPortalDomain, setIsDeveloperPortalDomain] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const mainDomains = [
        'totalaccess.co.za',
        'www.totalaccess.co.za',
        'dev-next.totalaccess.co.za',
        'dev.totalaccess.co.za',
        'dev-staging.totalaccess.co.za',
        'staging.totalaccess.co.za',
        'localhost'
      ];
      setIsMainPlatformDomain(mainDomains.includes(hostname) || hostname.includes('localhost'));
      const developerDomains = [
        'developer.totalaccess.co.za',
        'dev-developer.totalaccess.co.za',
      ];
      setIsDeveloperPortalDomain(developerDomains.includes(hostname));
    }
  }, []);

  // White-label configuration state
  const [isWhiteLabelPort, setIsWhiteLabelPort] = useState(false);
  const [productionWhitelabelConfig, setProductionWhitelabelConfig] = useState<WhitelabelConfig | null>(null);
  const [loadingWhitelabel, setLoadingWhitelabel] = useState(true);

  // Public branding from client_id 1 (for public site and fallbacks)
  const [publicBranding, setPublicBranding] = useState<PublicBranding | null>(null);

  // Fetch public branding from client_id 1 for public site and fallbacks
  useEffect(() => {
    const fetchPublicBranding = async () => {
      try {
        const response = await fetch('/api/v1/public-branding');
        if (response.ok) {
          const { data } = await response.json();
          setPublicBranding(data);
        }
      } catch (error: unknown) {
        if (!isSilentFetchError(error)) console.error('[Header] Failed to fetch public branding:', error);
      }
    };

    fetchPublicBranding();
  }, []);

  // Fetch white-label configuration on mount for production domains
  useEffect(() => {
    const fetchWhitelabelConfig = async () => {
      if (typeof window === 'undefined') return;

      const hostname = window.location.hostname;
      const port = window.location.port;

      // Never enable white-label mode for localhost (development)
      const isLocalhost = hostname.includes('localhost') || hostname.includes('127.0.0.1');

      // Check if running on port 5006 (old white-label test port - deprecated after Turbopack)
      const isTestPort = port === '5006';

      // White-label should NEVER be active on localhost, regardless of port
      setIsWhiteLabelPort(false); // Always false - whitelabel detection is only for production

      // Non-whitelabel domains (main platform + dev environments)
      const NON_WHITELABEL_DOMAINS = [
        'www.totalaccess.co.za',
        'system.totalaccess.co.za',
        'totalaccess.co.za',
        'dev-next.totalaccess.co.za',
        'dev.totalaccess.co.za',
        'dev-staging.totalaccess.co.za',
        'staging.totalaccess.co.za'
      ];

      // For production domains (not localhost, not main platform domains)
      const isProductionDomain = !isLocalhost &&
        !NON_WHITELABEL_DOMAINS.includes(hostname);

      if (isProductionDomain) {
        try {
          const response = await fetch(`/api/v1/whitelabel?domain=${hostname}`);
          const data = await response.json();

          if (data.success && data.isWhiteLabel) {
            setProductionWhitelabelConfig(data.data);
          }
        } catch (error) {
          if (!isSilentFetchError(error)) console.error('[Header] Failed to fetch whitelabel config:', error);
        }
      }

      setLoadingWhitelabel(false);
    };

    fetchWhitelabelConfig();
  }, []);

  // Custom whitelabel domain: API returns favicon_url (see /api/v1/whitelabel) — apply like main-domain client branding
  useEffect(() => {
    const url = productionWhitelabelConfig?.favicon_url;
    if (typeof url === 'string' && url.length > 0) {
      applyClientFavicon(url);
    }
  }, [productionWhitelabelConfig, applyClientFavicon]);

  // Get client info from navigationData (same as dashboard)
  const clientInfo = navigationData?.client_info;
  const navigationClientInfo = clientInfo as HeaderClientInfo | undefined;
  const currentClientId = Number(clientInfo?.id || 0);

  // White-label detection:
  // 1. Port 5006 (dev/staging test) - use navigationData client_info
  // 2. Production custom domain - use API-fetched config
  const isWhiteLabel = isWhiteLabelPort
    ? (navigationClientInfo?.whitelabel_enabled === true)
    : (productionWhitelabelConfig !== null);

  // White-label logo from client settings
  // Priority: 1) ThemeContext (instant preview), 2) Production whitelabel config, 3) Dev whitelabel port, 4) Client info
  const whitelabelLogo = themeClientLogo
    || productionWhitelabelConfig?.logo_url
    || (isWhiteLabelPort ? clientInfo?.logo_url : null)
    || clientInfo?.logo_url;

  const whitelabelTitle = isWhiteLabelPort
    ? clientInfo?.name
    : productionWhitelabelConfig?.client_name;

  const whitelabelPrimaryColor = isWhiteLabelPort
    ? navigationClientInfo?.primary_color
    : productionWhitelabelConfig?.primary_color;

  const whitelabelSecondaryColor = isWhiteLabelPort
    ? navigationClientInfo?.secondary_color
    : productionWhitelabelConfig?.secondary_color;

  // Header background color from client settings
  // Priority for client-zone: 1) ThemeContext (instant preview), 2) Production whitelabel config, 3) Client info, 4) Public branding (client_id 1 fallback)
  // Priority for public site: Use public branding (client_id 1)
  const whitelabelHeaderColor = isInClientZone
    ? (themeHeaderColor
      || productionWhitelabelConfig?.header_color
      || (isWhiteLabelPort ? navigationClientInfo?.whitelabel_header_color : null)
      || navigationClientInfo?.whitelabel_header_color
      || publicBranding?.header_color)  // Fallback to client_id 1 if client doesn't define it
    : publicBranding?.header_color;  // Public site uses client_id 1 header color

  // Whitelabel header contrast: the header color is client-defined, so the
  // light/dark theme cannot guarantee readable icons/text (e.g. dark theme
  // text on a client's dark header). Scope-override --theme-text-* vars on
  // <header> via WCAG luminance — every theme-text-* icon/button follows
  // automatically. Dropdown/menu panels keep opaque themed backgrounds, so
  // globals.css restores document text vars inside [data-header-custom]
  // .theme-surface / .ta-theme-scope descendants.
  const headerContrastStyle = useMemo((): React.CSSProperties | undefined => {
    if (!whitelabelHeaderColor) return undefined;
    const vars: Record<string, string> = { backgroundColor: whitelabelHeaderColor };
    if (getRelativeLuminance(whitelabelHeaderColor) == null) {
      return vars as React.CSSProperties;
    }
    const lightText = shouldUseLightText(whitelabelHeaderColor);
    vars['--theme-text-primary'] = lightText ? '#f9fafb' : '#111827';
    vars['--theme-text-secondary'] = lightText ? 'rgba(255,255,255,0.8)' : 'rgba(17,24,39,0.72)';
    vars['--theme-border'] = lightText ? 'rgba(255,255,255,0.3)' : 'rgba(17,24,39,0.22)';
    // --theme-surface-dark is used as the hover background on bar controls —
    // make it a translucent overlay that works on any header color.
    vars['--theme-surface-dark'] = lightText ? 'rgba(255,255,255,0.14)' : 'rgba(17,24,39,0.08)';
    const accent = whitelabelPrimaryColor || '#00affa';
    // Original accent is stashed so menu panels inside the header can restore
    // it (--theme-primary can't be "un-overridden" once scoped on <header>).
    vars['--ta-accent-orig'] = accent;
    const accentRatio = getContrastRatio(whitelabelHeaderColor, accent);
    if (accentRatio != null && accentRatio < 2) {
      vars['--theme-primary'] = lightText ? '#ffffff' : '#111827';
    }
    return vars as React.CSSProperties;
  }, [whitelabelHeaderColor, whitelabelPrimaryColor]);

  // Logo for public site: Use publicBranding logo from client_id 1
  const publicSiteLogo = publicBranding?.logo_url || currentLogo;

  // Return URL for white-label "Back to Website" functionality
  const whitelabelReturnUrl = productionWhitelabelConfig?.return_url;

  // Determine if user has multiple clients (for showing/hiding client switcher)
  // Only calculate after mount to prevent hydration mismatch
  // ROOT USER always sees the client switcher to access any client
  const hasMultipleClients = mounted && (isRoot || (userClients?.length || 0) > 1);

  // Check if user has a valid client (not the placeholder 99999)
  // Users without a client are assigned to placeholder client_id 99999
  // Fallback: navigation payload sometimes omits client_info for the primary client (e.g. id 1) until refresh — use JWT user.client_id
  const resolvedClientId = Number(
    clientInfo?.id ?? (user as { client_id?: number | string } | null)?.client_id ?? 0,
  );
  const hasValidClient = mounted && resolvedClientId > 0 && resolvedClientId !== 99999;
  const normalizedPathname = pathname ? (pathname.replace(/\/+$/, '') || '/') : '';
  const isOperationsDashboardPage = normalizedPathname === '/client-zone' || normalizedPathname === '/client-zone/dashboard';
  const isTotalFinanceLandingPage = isTotalFinanceRoute(normalizedPathname);
  const operationsDemoModuleKey = isOperationsDashboardPage
    ? SMART_TASK_DEMO_MODULE_KEY
    : isTotalFinanceLandingPage
      ? TOTAL_FINANCE_DEMO_MODULE_KEY
      : 'total-operations';
  const isClientSwitchBlockedByDemo = Boolean(demoSession?.is_demo_mode);
  const isTrainingModeMenuRestricted = Boolean(demoSession?.is_demo_mode);
  const trainingWorkspaceOwnerName = getTrainingWorkspaceOwnerName();

  useEffect(() => {
    if (isTrainingModeMenuRestricted) {
      setIsModulesOpen(false);
    }
  }, [isTrainingModeMenuRestricted]);

  // Helper to check if a feature is enabled for the user
  // Default to true if no settings loaded yet (show icons by default until settings are fetched)
  const isFeatureEnabled = (featureKey: FeatureKey): boolean => {
    if (!userFeatureSettings) return true; // Default to showing icons
    return userFeatureSettings[featureKey] ?? true;
  };

  // Debug log for client switcher visibility
  useEffect(() => {
    if (mounted && isAuthenticated) {
      // Client switcher visibility check (removed verbose logging)
    }
  }, [mounted, isAuthenticated, userClients, hasMultipleClients]);

  // Apply white-label colors to CSS variables
  useEffect(() => {
    if (isWhiteLabel && whitelabelPrimaryColor) {
      document.documentElement.style.setProperty('--theme-primary', whitelabelPrimaryColor);
    }
    if (isWhiteLabel && whitelabelSecondaryColor) {
      document.documentElement.style.setProperty('--theme-secondary', whitelabelSecondaryColor);
    }

    // Update page title for white-label clients
    if (isWhiteLabel && whitelabelTitle) {
      document.title = whitelabelTitle;
    }

    // Cleanup: restore default theme colors when unmounting or switching away from white-label
    return () => {
      if (!isWhiteLabel) {
        document.documentElement.style.removeProperty('--theme-primary');
        document.documentElement.style.removeProperty('--theme-secondary');
        document.title = 'Total Access Solutions'; // Restore default title
      }
    };
  }, [isWhiteLabel, whitelabelPrimaryColor, whitelabelSecondaryColor, whitelabelTitle, isWhiteLabelPort, productionWhitelabelConfig, whitelabelLogo]);

  // Get user display name (max 14 characters) - only first name
  const getUserDisplayName = () => {
    const currentUser = navUser || user;
    if (!currentUser) return 'User';

    // Handle different user types - only show first_name
    const firstName = 'first_name' in currentUser ? currentUser.first_name : currentUser.name;
    if (!firstName) return 'User';

    return firstName.length > 14 ? 'User' : firstName;
  };

  function getTrainingWorkspaceOwnerName() {
    const currentUser = navUser || user;
    if (!currentUser) return '';

    const firstName = typeof currentUser === 'object' && currentUser && 'first_name' in currentUser
      ? currentUser.first_name
      : typeof currentUser === 'object' && currentUser && 'name' in currentUser
        ? currentUser.name
        : '';
    const lastName = typeof currentUser === 'object' && currentUser && 'last_name' in currentUser
      ? currentUser.last_name
      : '';

    const fullName = [firstName, lastName]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .join(' ')
      .trim();

    if (fullName) {
      return fullName;
    }

    if (typeof firstName === 'string' && firstName.trim().length > 0) {
      return firstName.trim();
    }

    return '';
  }

  const buildTrainingRefreshPath = useCallback((path: string) => {
    if (typeof window === 'undefined') {
      return path;
    }

    const nextUrl = new URL(path, window.location.origin);
    nextUrl.searchParams.set('training_refresh', `${Date.now()}`);
    return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
  }, []);

  const loadDemoSession = useCallback(async () => {
    if (!mounted || !isAuthenticated || !shouldTrackDemoSession) {
      setDemoSession(null);
      setCanEnterOperationsDemo(false);
      setIsDemoSessionLoading(false);
      return;
    }

    setIsDemoSessionLoading(true);

    try {
      const hadDemoOverride = isOperationsDemoOverrideEnabled();
      const { ok, data } = await fetchDemoSessionResponse(operationsDemoModuleKey);
      if (ok && data.success) {
        const nextSession = data.session || null;
        const reconciledStaleDemoOverride = hadDemoOverride && !nextSession?.is_demo_mode;

        setDemoSession(nextSession);
        setCanEnterOperationsDemo(Boolean(data.can_enter_demo || nextSession?.is_demo_mode));
        reconcileOperationsDemoClientContext(nextSession);

        if (reconciledStaleDemoOverride) {
          permissionCache.clearCache();
          refreshNavigation().catch((error) => {
            if (!isSilentFetchError(error)) console.error('[Header] Failed to refresh navigation after clearing stale demo context:', error);
          });
        }
      } else {
        setDemoSession(null);
        setCanEnterOperationsDemo(false);
        clearOperationsDemoClientContext();
      }
    } catch (error) {
      if (!isSilentFetchError(error)) console.error('[Header] Failed to load demo session:', error);
      setDemoSession(null);
      setCanEnterOperationsDemo(false);
    } finally {
      setIsDemoSessionLoading(false);
    }
  }, [isAuthenticated, mounted, operationsDemoModuleKey, refreshNavigation, shouldTrackDemoSession]);

  useEffect(() => {
    if (!mounted || !isAuthenticated || !shouldTrackDemoSession) {
      setDemoSession(null);
      setCanEnterOperationsDemo(false);
      return;
    }

    loadDemoSession();
  }, [mounted, isAuthenticated, currentClientId, loadDemoSession, shouldTrackDemoSession]);

  useEffect(() => {
    if (mounted && !isAuthenticated) {
      clearOperationsDemoClientContext();
    }
  }, [isAuthenticated, mounted]);

  // Handle client switching - trigger navigation refresh to update all client-dependent data
  const handleClientSwitch = async (_client: unknown) => {
    try {
      // Close the modal first
      setIsClientSelectorOpen(false);

      // Web only: brief delay so the modal can unmount before router.refresh.
      // Capacitor navigates immediately (double rAF already deferred offline cleanup).
      if (!isCapacitor()) {
        await new Promise(resolve => setTimeout(resolve, 150));
      }

      if (isCapacitor()) {
        // Capacitor: navigate FIRST, then refresh data in the background.
        // Awaiting API calls before router.push leaves the user on a blank screen
        // with no feedback for multiple seconds (nav data + demo session can be slow
        // on mobile networks). The new page will run its own background refreshes.
        router.push('/client-zone/dashboard');
        void refreshNavigation().catch((err) => {
          console.warn('[HEADER] Background navigation refresh after client switch:', err);
        });
        void loadDemoSession().catch((err) => {
          console.warn('[HEADER] Background demo session refresh after client switch:', err);
        });
        return;
      }

      // Refresh navigation data to get updated modules for the new client
      await refreshNavigation();
      await loadDemoSession();

      // Web: soft refresh of current page (full reload handled by ClientSelector)
      router.refresh();
    } catch (error) {
      console.error('[HEADER] Error refreshing navigation after client switch:', error);
      // Fallback to page reload only if navigation refresh fails
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  useEffect(() => {
    if (demoActionLoading !== 'enter') {
      setDemoLoadingProgressActive(false);
      return;
    }

    setDemoLoadingProgressActive(false);
    const frame = window.requestAnimationFrame(() => {
      setDemoLoadingProgressActive(true);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [demoActionLoading]);



  const handleExitOperationsDemo = useCallback(async () => {
    if (!demoSession || demoActionLoading) {
      return;
    }

    const loadingStartedAt = Date.now();
    let shouldClearLoading = true;
    const sessionToClose = demoSession;
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
    setDemoActionLoading('exit');

    try {
      const accessToken = (() => {
        if (typeof window === 'undefined') {
          return null;
        }

        try {
          const storedTokens = localStorage.getItem('auth_tokens');
          if (!storedTokens) {
            return null;
          }

          const parsedTokens = JSON.parse(storedTokens);
          return typeof parsedTokens?.access_token === 'string' ? parsedTokens.access_token : null;
        } catch {
          return null;
        }
      })();

      let cleanupWarning: string | null = null;

      try {
        const releaseResponse = await fetch('/api/v1/demo/exit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ lease_id: sessionToClose.lease_id }),
        });

        const releaseData = await releaseResponse.json();
        if (!releaseResponse.ok || !releaseData.success) {
          cleanupWarning = releaseData.error || releaseData.message || 'The demo lease will be cleaned up automatically.';
        }
      } catch (releaseError: unknown) {
        cleanupWarning = releaseError instanceof Error ? releaseError.message : 'The demo lease will be cleaned up automatically.';
      }

      setDemoSession(null);
      setCanEnterOperationsDemo(false);
      clearOperationsDemoClientContext();
      permissionCache.clearCache();

      if (cleanupWarning) {
        console.warn('[Header] Demo exit cleanup warning:', cleanupWarning);
      }

      await ensureMinimumTrainingModeLoadingTime(loadingStartedAt, 'exit');

      shouldClearLoading = false;
      window.location.replace(buildTrainingRefreshPath(sessionToClose.return_path || '/client-zone/modules/total-operations'));
      return;
    } catch (error: unknown) {
      console.error('[Header] Failed to exit Operations demo:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to exit training mode.');
    } finally {
      if (shouldClearLoading) {
        setDemoActionLoading(null);
      }
    }
  }, [buildTrainingRefreshPath, demoActionLoading, demoSession]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleFooterExitTrainingRequest = () => {
      if (!mounted || !isAuthenticated || !demoSession?.is_demo_mode || demoActionLoading) {
        return;
      }

      handleExitOperationsDemo();
    };

    window.addEventListener(OPERATIONS_DEMO_EXIT_REQUESTED_EVENT, handleFooterExitTrainingRequest);

    return () => {
      window.removeEventListener(OPERATIONS_DEMO_EXIT_REQUESTED_EVENT, handleFooterExitTrainingRequest);
    };
  }, [demoActionLoading, demoSession?.is_demo_mode, handleExitOperationsDemo, isAuthenticated, mounted]);

  // Global Search Functionality
  const performSearch = useCallback(async (query: string) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      // Build search URL with module filter if in a specific module and scope is module-scoped
      let searchUrl = `/api/v1/global-search?q=${encodeURIComponent(query)}&limit=20`;
      if (currentModule?.key && searchScope === 'module') {
        searchUrl += `&module=${encodeURIComponent(currentModule.key)}`;
      }

      const token = authService.getTokens()?.access_token;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(searchUrl, { headers });
      const data = await response.json();

      if (data.success) {
        setSearchResults(data.results || []);
        if (data.results.length === 0) {
          setSearchError('No results found');
        }
      } else {
        setSearchError(data.error || 'Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('[HEADER] Search error:', error);
      setSearchError('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [currentModule?.key, searchScope]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 300); // 300ms debounce
    } else {
      setSearchResults([]);
      setSearchError(null);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [currentModule, performSearch, searchQuery]);

  // Handle search result click
  const isWorkItemBackedSearchResult = (result: SearchResult): boolean => {
    const metadata = result.metadata;
    const itemType = typeof metadata?.item_type === 'string' ? metadata.item_type : null;
    const docType = typeof metadata?.doc_type === 'string' ? metadata.doc_type : null;

    if (itemType || docType || result.share_token) {
      return true;
    }

    return result.module === 'total-operations' || result.module === 'total-automation' || (result.module === 'total-finance' && result.type === 'document');
  };

  const getSearchResultTeamMemberUserId = (result: SearchResult): number | null => {
    if (result.type !== 'employee') {
      return null;
    }

    const rawValue = result.metadata?.user_id;
    const parsedValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  };

  const handleResultClick = (result: SearchResult) => {
    const teamMemberUserId = getSearchResultTeamMemberUserId(result);

    if (teamMemberUserId) {
      void openTeamMember(teamMemberUserId);
    } else if (isMobileApp && result.route?.startsWith('/client-zone/modules/')) {
      toast('Module pages are only available in the web portal');
    } else if (isWorkItemBackedSearchResult(result)) {
      if (workItemOpener?.openWorkItem) {
        workItemOpener.openWorkItem(String(result.id));
      } else if (typeof window !== 'undefined') {
        emit('openWorkItem', { id: String(result.id) });
      } else {
        router.push(result.route);
      }
    } else {
      router.push(result.route);
    }

    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setSearchTypeFilter(null);
  };

  // Get icon for result type
  const getResultIcon = (result: SearchResult): string => {
    const iconMap: { [key: string]: string } = {
      'document': 'fa-file-invoice',
      'contact': 'fa-user',
      'employee': 'fa-user-tie',
      'task': 'fa-tasks',
      'project': 'fa-project-diagram',
      'ticket': 'fa-life-ring',
      'asset': 'fa-box',
      'transaction': 'fa-exchange-alt',
      'bank_account': 'fa-university'
    };
    return result.icon || iconMap[result.type] || 'fa-file';
  };

  const getResultTypeLabel = (type: string): string => {
    const labelMap: { [key: string]: string } = {
      'document': 'Documents',
      'contact': 'Contacts',
      'employee': 'Employees',
      'task': 'Tasks',
      'project': 'Projects',
      'ticket': 'Tickets',
      'asset': 'Assets',
      'transaction': 'Transactions',
      'bank_account': 'Bank Accounts'
    };
    return labelMap[type] || type;
  };

  // Get module badge color
  const getModuleBadgeColor = (module: string): string => {
    const colorMap: { [key: string]: string } = {
      'total-finance': 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/50',
      'total-workforce': 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/50',
      'total-operations': 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800/50',
      'total-support': 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800/50',
      'total-connect': 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800/50',
      'total-crm': 'bg-pink-100 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-800/50',
      'total-forms': 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/50',
      'total-attendance': 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/50',
      'total-pabx': 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/50',
      'total-integration': 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
      'total-automation': 'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800/50',
      'work-tracker': 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50'
    };
    return colorMap[module] || 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
  };

  // Convert icon names from database to FontAwesome classes or render emoji
  const getIconClass = (iconName: string): string => {
    // Check if it's an emoji - expanded to include more emoji ranges
    const isEmoji = /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{231A}-\u{23FF}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F64F}\u{1F680}-\u{1F6FF}]/u.test(iconName);

    if (isEmoji) {
      return ''; // Return empty string, we'll render emoji directly
    }

    const iconMap: { [key: string]: string } = {
      'alert-triangle': 'fa fa-exclamation-triangle',
      'bar-chart': 'fa fa-chart-bar',
      'briefcase': 'fa fa-briefcase',
      'building': 'fa fa-building',
      'clock': 'fa fa-clock',
      'cpu': 'fa fa-microchip',
      'dollar-sign': 'fa fa-dollar-sign',
      'folder': 'fa fa-folder',
      'headphones': 'fa fa-headphones',
      'help-circle': 'fa fa-question-circle',
      'key': 'fa fa-key',
      'layout': 'fa fa-th-large',
      'message-circle': 'fa fa-comment',
      'message-square': 'fa fa-comment-alt',
      'settings': 'fa fa-cog',
      'shield-check': 'fa fa-shield-alt',
      'truck': 'fa fa-truck',
      'user-circle': 'fa fa-user-circle',
      'users': 'fa fa-users',
      'wrench': 'fa fa-wrench'
    };

    return iconMap[iconName] || 'fa fa-cube'; // Default icon if not found
  };

  // Check if icon is emoji - expanded to include more emoji ranges including ⏰
  const isEmojiIcon = (iconName: string): boolean => {
    return /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{231A}-\u{23FF}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F64F}\u{1F680}-\u{1F6FF}]/u.test(iconName);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      const targetNode = event.target as Node;

      // Don't close dropdowns if clicking inside a modal portal (bug report modal, etc.)
      if (target.closest('[data-bug-report-modal="true"]')) {
        return;
      }

      const isInsideDesktopUserMenu = Boolean(userDropdownRef.current?.contains(targetNode));
      const isInsideMobileMenu = Boolean(mobileMenuRef.current?.contains(targetNode));
      const isInsideSearchBar = Boolean(searchBarRef.current?.contains(targetNode));
      // Portaled mega panels live in <header>, outside the nav/auth refs —
      // clicks inside them must not count as "outside" or the panel unmounts
      // between mousedown and click and swallows the navigation.
      const inMegaPanel = Boolean((targetNode as HTMLElement).closest?.('[data-header-mega-panel="true"]'));

      if (dropdownRef.current && !dropdownRef.current.contains(targetNode) && !inMegaPanel) {
        setIsModulesOpen(false);
      }
      const inPublicNav = Boolean(publicNavRef.current?.contains(targetNode));
      const inPublicAuth = Boolean(publicAuthRef.current?.contains(targetNode));
      if (!inPublicNav && !inPublicAuth && !inMegaPanel) {
        setIsPublicNavOpen(false);
        setPublicMenu(null);
      }
      if (!isInsideDesktopUserMenu && !isInsideMobileMenu && !inMegaPanel) {
        setIsUserMenuOpen(false);
        setOpenMenuSection(null);
      }
      if (searchBarRef.current && !isInsideSearchBar) {
        setIsSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        setSearchError(null);
        setSearchTypeFilter(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Listen for active client changes from heartbeat
  useEffect(() => {
    const handleActiveClientChanged = (event: CustomEvent) => {
      // Refresh navigation data to get updated client information
      refreshNavigation().catch((error) => {
        console.error('[HEADER] Error refreshing navigation after heartbeat:', error)
      })
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('activeClientChanged', handleActiveClientChanged as EventListener)

      return () => {
        window.removeEventListener('activeClientChanged', handleActiveClientChanged as EventListener)
      }
    }
  }, [refreshNavigation])

  const handleLogout = useCallback(async () => {
    void triggerHeaderHaptic('warning');
    const confirmed = await notify.confirm({
      title: 'Sign Out',
      message: 'Are you sure you want to sign out? You will need to sign in again to access your account.',
      confirmText: 'Sign Out',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await logout();
      // Let authService handle the redirect to the signed-out login page
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [logout, triggerHeaderHaptic]);

  // Manual lock — check PIN is set, confirm, then activate the lock screen
  const handleLockPortal = useCallback(async () => {
    void triggerHeaderHaptic('warning');

    // Check if the user has a lock-screen PIN set
    let hasPin = false;
    try {
      const token = authService.getTokens()?.access_token;
      if (token) {
        const pinResponse = await fetch('/api/v1/auth/pin/status', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (pinResponse.ok) {
          const pinData = await pinResponse.json();
          hasPin = Boolean(pinData.success && pinData.hasPin);
        }
      }
    } catch {
      // If we can't check, proceed — the lock screen still supports password/passkey unlock
    }

    if (!hasPin) {
      // No PIN set — prompt the user to set one before locking.
      // Use callbacks to distinguish the two actions since both resolve the promise.
      const action: { value: 'setPin' | 'lockAnyway' | 'cancel' } = { value: 'cancel' };

      await notify.confirm({
        title: 'Set a PIN first',
        message:
          'You need a 6-digit PIN to quickly unlock the portal after locking. ' +
          'Without a PIN you would need your full password or passkey every time. ' +
          'Set a PIN now for faster unlocking, or lock anyway.',
        confirmText: 'Set PIN Now',
        cancelText: 'Cancel',
        variant: 'info',
        onConfirm: () => { action.value = 'setPin'; },
        onCancel: () => { action.value = 'cancel'; },
        secondaryConfirmText: 'Lock Anyway',
        secondaryVariant: 'warning',
        onSecondaryConfirm: () => { action.value = 'lockAnyway'; },
      });

      if (action.value === 'setPin') {
        setIsUserMenuOpen(false);
        setIsMenuOpen(false);
        setOpenMenuSection(null);
        setProfileInitialTab('security');
        setShowProfileModal(true);
        notify.info('Set your 6-digit PIN in the Security tab, then lock the portal again.');
        return;
      }

      if (action.value === 'cancel') {
        return;
      }

      // action.value === 'lockAnyway' — proceed to lock
      setIsUserMenuOpen(false);
      setIsMenuOpen(false);
      setOpenMenuSection(null);
      session.lockSession();
      return;
    }

    // PIN is set — standard lock confirmation
    const confirmed = await notify.confirm({
      title: 'Lock portal',
      message: 'Lock the portal now? You will need your PIN, password, or passkey to unlock it again.',
      confirmText: 'Lock Now',
      cancelText: 'Cancel',
      variant: 'warning',
    });

    if (!confirmed) {
      return;
    }

    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
    setOpenMenuSection(null);
    session.lockSession();
  }, [session, triggerHeaderHaptic]);

  // Ctrl+L keyboard shortcut to lock the portal (desktop/web only, not Capacitor)
  useEffect(() => {
    if (isMobileApp) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'l') {
        e.preventDefault();

        if (isAuthenticated && !session.sessionState.isLocked) {
          void handleLockPortal();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleLockPortal, isAuthenticated, isMobileApp, session.sessionState.isLocked]);

  const handleClearCache = useCallback(({ closeMenu = true }: { closeMenu?: boolean } = {}) => {
    void triggerHeaderHaptic('warning');
    if (closeMenu) {
      setIsMenuOpen(false);
      setIsUserMenuOpen(false);
      setOpenMenuSection(null);
    }
    setShowClearCacheModal(true);
  }, [triggerHeaderHaptic]);

  const handleColdStartLogsToggle = useCallback(() => {
    if (typeof window === 'undefined' || !canUseColdStartLogToggle()) {
      return;
    }

    void triggerHeaderHaptic('menuAction');

    const nextEnabled = !coldStartLogsEnabled;

    try {
      const setDiagnosticsEnabled = (window as HeaderWindow).__setColdStartDiagnosticsEnabled;

      if (typeof setDiagnosticsEnabled === 'function') {
        setDiagnosticsEnabled(nextEnabled, { keepLogs: nextEnabled });
      } else {
        localStorage.setItem(COLDSTART_LOGS_ENABLED_STORAGE_KEY, nextEnabled ? 'true' : 'false');
        if (!nextEnabled) {
          localStorage.removeItem(COLDSTART_LOGS_STORAGE_KEY);
        }
        (window as HeaderWindow).__coldStartDiagnosticsEnabled = nextEnabled;
      }

      setColdStartLogsEnabled(nextEnabled);
      window.dispatchEvent(new CustomEvent(COLDSTART_LOGS_TOGGLED_EVENT, {
        detail: { enabled: nextEnabled },
      }));

      toast.success(
        nextEnabled
          ? 'Cold-start logs enabled. The next cold start will capture diagnostics.'
          : 'Cold-start logs disabled and stored startup logs cleared.'
      );
    } catch (error) {
      console.error('[Header] Failed to update cold-start log setting:', error);
      toast.error('Failed to update cold-start log setting.');
    }
  }, [coldStartLogsEnabled, triggerHeaderHaptic]);

  const handleRestartApp = useCallback(({ closeMenu = true }: { closeMenu?: boolean } = {}) => {
    if (typeof window === 'undefined') {
      return;
    }

    void triggerHeaderHaptic('menuAction');

    if (closeMenu) {
      setIsMenuOpen(false);
      setIsUserMenuOpen(false);
      setOpenMenuSection(null);
    }

    toast.success('Restarting app…');

    window.setTimeout(() => {
      void (async () => {
        try {
          if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((registration) => registration.unregister()));
          }
        } catch {
          /* PWAs occasionally block unregister transiently */
        }

        window.location.reload();
      })();
    }, 120);
  }, [triggerHeaderHaptic]);

  // Handle module navigation (global loading state managed by NavigationLoadingProvider)
  const handleModuleNavigation = (moduleKey: string, moduleName: string, route: string) => {
    void triggerHeaderHaptic('menuAction');
    setIsModulesOpen(false);
    setIsMenuOpen(false);
    if (isCapacitor()) {
      router.replace('/client-zone/dashboard');
      return;
    }
    router.push(route);
  };

  const openManagedTasksTab = useCallback((tab: ManagedTasksMainTab) => {
    setManagedTasksInitialTab(tab);
    openModal('tasks');
  }, [openModal]);

  const desktopHeaderQuickActions = useMemo(() => {
    const actions: Array<{
      key: 'calls' | 'email' | 'messages' | 'calendar' | 'myshell';
      label: string;
      icon: ReactNode;
      badge: number;
      onClick: () => void;
    }> = [];

    const featureEnabled = (featureKey: FeatureKey): boolean => {
      if (!userFeatureSettings) return true;
      return userFeatureSettings[featureKey] ?? true;
    };

    if (hasConnectAccess && (featureEnabled('voice_calls') || featureEnabled('video_calls'))) {
      actions.push({
        key: 'calls',
        label: 'Calls',
        icon: <Phone className={`h-6 w-6 ${HEADER_QUICK_ACTION_ICON_CLASS}`} />,
        badge: badgeCounts.missedCallsCount,
        onClick: () => setIsCallModalOpen(true),
      });
    }

    if (hasConnectAccess) {
      actions.push({
        key: 'email',
        label: 'Email',
        icon: <Mail className={`h-6 w-6 ${HEADER_QUICK_ACTION_ICON_CLASS}`} />,
        badge: badgeCounts.emailUnreadCount,
        onClick: toggleEmailExperience,
      });
    }

    if (hasConnectAccess && featureEnabled('message_centre')) {
      actions.push({
        key: 'messages',
        label: 'Messages',
        icon: <MessageSquare className={`h-6 w-6 ${HEADER_QUICK_ACTION_ICON_CLASS}`} />,
        badge: badgeCounts.messagesUnreadCount,
        onClick: () => toggleModal('messages'),
      });
    }

    if (hasConnectAccess) {
      actions.push({
        key: 'calendar',
        label: 'Calendar',
        icon: <Calendar className={`h-6 w-6 ${HEADER_QUICK_ACTION_ICON_CLASS}`} />,
        badge: badgeCounts.calendarUnreadCount,
        onClick: () => toggleModal('calendar'),
      });
    }

    if (isRoot) {
      actions.push({
        key: 'myshell',
        label: 'My Shell',
        icon: <SquareTerminal className={`h-6 w-6 ${HEADER_QUICK_ACTION_ICON_CLASS}`} />,
        badge: 0,
        onClick: () => toggleModal('myshell'),
      });
    }

    return actions;
  }, [hasConnectAccess, isRoot, userFeatureSettings, badgeCounts, setIsCallModalOpen, toggleEmailExperience, toggleModal]);

  const handleManagedTasksModalClose = useCallback(() => {
    setManagedTasksInitialTab('tasks');
    if (activeModal === 'tasks') {
      toggleModal('tasks');
    }
  }, [activeModal, toggleModal]);

  const toggleMenuSection = (sectionKey: MenuSectionKey) => {
    void triggerHeaderHaptic('accordionToggle');
    setOpenMenuSection((currentSection) => currentSection === sectionKey ? null : sectionKey);
  };

  const isModulesNavAccordionExpanded = (scope: 'desktop' | 'mobile', key: string) =>
    modulesNavAccordionExpanded[modulesNavAccordionCollapsedKey(scope, key)] === true;

  const toggleModulesNavAccordionSection = (scope: 'desktop' | 'mobile', key: string) => {
    void triggerHeaderHaptic('accordionToggle');
    setModulesNavAccordionExpanded((prev) => {
      const dictKey = modulesNavAccordionCollapsedKey(scope, key);
      // Replace the map entirely so only one accordion can be open at a time.
      return prev[dictKey] === true ? {} : { [dictKey]: true };
    });
  };

  /** Authenticated Modules mega menu sections — same items the old accordion
   *  panel listed, reshaped for the two-pane anatomy. */
  const authModuleNavSections = useMemo(() => {
    const sections: { label: string; items: NavPreviewItem[] }[] = [];
    for (const group of Object.values(navigationGroups)) {
      const isGeneralGroup = group.label === 'General';
      const items = group.items
        .filter(
          (i) =>
            !i.hidden &&
            i.module_key !== 'browse_modules' &&
            !shouldHideNavigationModule(i.module_key, i.user_role),
        )
        .sort((a, b) => {
          const sa = a.sort_order || 999;
          const sb = b.sort_order || 999;
          return sa !== sb ? sa - sb : a.label.localeCompare(b.label);
        })
        .map<NavPreviewItem>((i) => {
          const route = i.route || `/client-zone/modules/${i.module_key}`;
          const emoji = isEmojiIcon(i.icon) ? i.icon : undefined;
          return {
            href: route,
            label: i.label,
            subtitle: i.user_role === 'owner' || i.user_role === 'admin' ? `Role: ${i.user_role}` : undefined,
            description: i.description,
            emoji,
            faIcon: emoji ? undefined : getIconClass(i.icon),
            moduleKey: i.module_key,
          };
        });
      if (items.length > 0 || (isGeneralGroup && isRoot)) {
        if (isGeneralGroup && isRoot) {
          items.push({
            href: '/client-zone/modules/root',
            label: 'Root Administration',
            description: 'Root admin console — platform configuration and management.',
            faIcon: 'fa-shield',
            moduleKey: 'root',
          });
        }
        sections.push({ label: isGeneralGroup ? 'My Modules' : group.label, items });
      }
    }
    const resourceItems: NavPreviewItem[] = [
      { href: '/resources/software-downloads', label: 'Software Downloads', faIcon: 'fa-download', description: 'Desktop apps, agents and installers for your devices.' },
      { href: '/resources/videos', label: 'Product Videos', faIcon: 'fa-play', description: 'Feature walkthroughs and product demos.' },
      { href: '/resources/user-guides', label: 'User Guides', faIcon: 'fa-video-camera', description: 'Step-by-step guides for every module.' },
      { href: '/resources/downloads', label: 'Downloads', faIcon: 'fa-file-text', description: 'Forms, templates and documents.' },
      { href: '/resources/faqs', label: 'FAQs', faIcon: 'fa-question-circle', description: 'Answers to frequently asked questions.' },
    ];
    const menuItems: NavPreviewItem[] = isMainPlatformDomain
      ? [
          { href: '/', label: 'Home', faIcon: 'fa-home', description: 'Back to the public website.' },
          { href: '/products', label: 'All Products', faIcon: 'fa-th', description: 'Browse the full product suite.' },
          { href: '/products/industries', label: 'Industry Solutions', faIcon: 'fa-industry', description: 'Ready-made solutions by industry.' },
          { href: '/about', label: 'About Us', faIcon: 'fa-info-circle', description: 'About Total Access Solutions.' },
          { href: '/contact', label: 'Contact Us', faIcon: 'fa-envelope', description: 'Get in touch with the team.' },
          { href: '/pricing', label: 'Pricing', faIcon: 'fa-tag', description: 'Plans and pricing.' },
          { href: '/support', label: 'Support', faIcon: 'fa-life-ring', description: 'Help centre and system status.' },
          { href: '/case-studies', label: 'Case Studies', faIcon: 'fa-briefcase', description: 'Customer success stories.' },
          { href: '/legal', label: 'Legal', faIcon: 'fa-gavel', description: 'Terms, privacy and compliance.' },
          {
            href: process.env.NODE_ENV === 'production' ? 'https://developer.totalaccess.co.za/' : 'https://dev-developer.totalaccess.co.za',
            label: 'Developer Docs',
            faIcon: 'fa-code',
            description: 'API references and integration guides.',
          },
        ]
      : [];
    return { moduleSections: sections, resourceItems, menuItems };
  }, [navigationGroups, isRoot, isMainPlatformDomain, shouldHideNavigationModule]);

  /** Two-pane inner content for the authenticated Modules mega menu —
   *  rail = module groups + Resources + Navigation, pane = item preview. */
  const renderModulesMegaInner = (
    opts: { emphasiseActiveRoutes: boolean; disableWhenMatched: boolean },
    menuKey: 'modules' | 'resources' | 'menu',
  ) => {
    if (navLoading) {
      return (
        <div className="flex flex-1 items-center justify-center p-8 text-sm text-slate-500 dark:text-slate-400">
          <i className="fa fa-spinner fa-spin mr-2"></i> Loading modules...
        </div>
      );
    }
    const railSections =
      menuKey === 'modules'
        ? authModuleNavSections.moduleSections
        : [
            {
              label: menuKey === 'resources' ? 'Resources' : 'Menu',
              items: menuKey === 'resources' ? authModuleNavSections.resourceItems : authModuleNavSections.menuItems,
            },
          ];
    const allItems = railSections.flatMap((s) => s.items);
    if (allItems.length === 0) {
      return (
        <div className="flex flex-1 items-center justify-center p-8 text-sm text-slate-500 dark:text-slate-400">
          <i className="fa fa-info-circle mr-2"></i> No modules available
        </div>
      );
    }
    const preview = allItems.find((i) => i.href === navPreviewHref) ?? allItems[0];
    const isCurrent = (k?: string) => Boolean(k && pathname?.includes(k));
    return (
      <>
        <div className="w-80 shrink-0 overflow-y-auto border-r border-slate-100 py-3 dark:border-slate-800">
          <div className="px-4 pb-2">
            <div className="text-lg font-bold text-slate-900 dark:text-white">
              {menuKey === 'modules' ? 'My Modules' : menuKey === 'resources' ? 'Resources' : 'Menu'}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {menuKey === 'modules'
                ? 'Your workspace — hover to preview'
                : menuKey === 'resources'
                  ? 'Guides, downloads and help'
                  : 'Quick links around the site'}
            </div>
          </div>
          {railSections.map((section) => (
            <div key={section.label}>
              <div className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                {section.label}
              </div>
              {section.items.map((item) => (
                <PublicNavRailItem
                  key={item.href}
                  item={{
                    ...item,
                    disabled: item.disabled || (opts.disableWhenMatched && isCurrent(item.moduleKey)),
                    onSelect: item.moduleKey
                      ? () => handleModuleNavigation(item.moduleKey!, item.label, item.href)
                      : item.onSelect,
                  }}
                  active={navPreviewHref === item.href || (opts.emphasiseActiveRoutes && isCurrent(item.moduleKey))}
                  onHover={setNavPreviewHref}
                  onNavigate={() => setIsModulesOpen(false)}
                />
              ))}
            </div>
          ))}
        </div>
        <div className="min-w-0 flex-1 p-8">
          <div className="max-w-2xl">
            {preview ? <PublicNavPreview item={preview} onNavigate={() => setIsModulesOpen(false)} /> : null}
          </div>
        </div>
      </>
    );
  };

  const renderSessionAndAppActions = (options?: {
    buttonClassName?: string;
    labelClassName?: string;
    showSignOutName?: boolean;
  }) => {
    const buttonClassName = options?.buttonClassName || 'flex items-center w-full px-4 py-2 theme-text-primary hover:theme-surface-dark text-left transition-colors';
    const labelClassName = options?.labelClassName || 'flex-1';
    const showSignOutName = options?.showSignOutName ?? false;

    return (
      <>
        {!showAppLikeMenuLayout && (
          <button
            type="button"
            onClick={() => { void handleLockPortal(); }}
            className={buttonClassName}
            title="Lock the current session"
          >
            <span className="mr-3">🔒</span>
            <span className={labelClassName}>Lock</span>
            <span className="text-xs opacity-50">Ctrl+L</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => handleRestartApp({ closeMenu: true })}
          className={buttonClassName}
          title="Reload the current app session"
        >
          <span className="mr-3">🔄</span>
          <span className={labelClassName}>Restart app</span>
        </button>

        {showClearCacheAction && (
          <button
            type="button"
            onClick={() => void handleClearCache({ closeMenu: true })}
            disabled={cacheStatus === 'clearing'}
            className={`${buttonClassName} disabled:opacity-50`}
          >
            <span className="mr-3">
              {cacheStatus === 'clearing' ? '⏳' : cacheStatus === 'success' ? '✅' : cacheStatus === 'error' ? '❌' : '🗑️'}
            </span>
            <span className={labelClassName}>
              {cacheStatus === 'clearing' ? 'Clearing cache...' :
                cacheStatus === 'success' ? 'Reloading...' :
                  cacheStatus === 'error' ? 'Clear Cache Failed' :
                    'Clear Cache'}
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            setIsUserMenuOpen(false);
            setIsMenuOpen(false);
            setOpenMenuSection(null);
            void handleLogout();
          }}
          className={`${buttonClassName} text-red-600 dark:text-red-400`}
        >
          <span className="mr-3">🚪</span>
          <span className={labelClassName}>Sign Out</span>
          {showSignOutName && user && (user.first_name || user.last_name) && (
            <span className="text-sm text-gray-400 dark:text-gray-500 truncate max-w-[150px]">
              {user.first_name} {user.last_name}
            </span>
          )}
        </button>
      </>
    );
  };

  const renderMenuAccordion = (options: {
    sectionKey: MenuSectionKey;
    title: string;
    icon: ReactNode;
    variant: MenuSectionVariant;
    children: ReactNode;
    /** Mega-menu pane: render children directly, no accordion toggle. */
    embedded?: boolean;
  }) => {
    const isOpen = openMenuSection === options.sectionKey;
    if (options.embedded) {
      return <>{options.children}</>;
    }
    const wrapperClassName = options.variant === 'desktop'
      ? 'border-t theme-border'
      : 'border-b theme-border my-2';
    const buttonClassName = options.variant === 'desktop'
      ? 'flex items-center justify-between w-full px-4 py-2 theme-text-primary hover:bg-slate-100 hover:text-[#047cde] dark:hover:bg-slate-700/50 dark:hover:text-sky-300 text-left transition-colors duration-150 ease-in-out'
      : 'theme-text-secondary flex items-center justify-between py-3 w-full text-left transition-colors';
    const contentClassName = 'pb-1';
    const contentWrapperClassName = `grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`;
    const contentInnerClassName = `${contentClassName} overflow-hidden ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`;

    return (
      <div className={wrapperClassName}>
        <button
          type="button"
          onClick={() => toggleMenuSection(options.sectionKey)}
          className={buttonClassName}
          aria-expanded={isOpen}
          title={options.title}
          data-tour-id={`menu-section-${options.sectionKey}`}
        >
          <div className="flex items-center">
            <span className="mr-3">{options.icon}</span>
            <span className="font-medium">{options.title}</span>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {options.variant === 'mobile' ? (
          isOpen ? (
            <div className={contentClassName}>
              {options.children}
            </div>
          ) : null
        ) : (
          <div className={contentWrapperClassName} aria-hidden={!isOpen}>
            <div className={contentInnerClassName}>
              {options.children}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAvailableUpdatesActions = (variant: MenuSectionVariant) => {
    if (variant !== 'mobile' || !isMobileApp) {
      return null;
    }

    const itemClasses = getMenuItemClasses(variant);
    const versionsBehind = typeof updateInfo?.versionsBehind === 'number' ? updateInfo.versionsBehind : null;
    const versionsBehindLabel = versionsBehind && versionsBehind > 0
      ? `${versionsBehind} release${versionsBehind === 1 ? '' : 's'} behind`
      : null;
    const statusLabel = updateStatus === 'checking'
      ? 'Checking Version'
      : updateStatus === 'available'
        ? 'Update Available'
        : updateStatus === 'error'
          ? 'Retry Version Check'
          : 'App Version';
    const statusSummary = updateStatus === 'checking'
      ? (progress?.message || 'Comparing your installed build with the latest deployment record...')
      : updateStatus === 'error'
        ? (progress?.error || 'Could not verify the latest release right now')
        : updateStatus === 'available' && updateInfo?.latestVersion
          ? `v${currentVersion} → v${updateInfo.latestVersion}${versionsBehindLabel ? ` • ${versionsBehindLabel}` : ''}`
          : updateInfo?.latestVersion
            ? `Latest v${updateInfo.latestVersion}${updateInfo.latestReleasedAtLabel ? ` • ${updateInfo.latestReleasedAtLabel}` : ''}`
            : `v${currentVersion}`;
    const statusChip = updateStatus === 'checking'
      ? 'Checking'
      : updateStatus === 'available'
        ? 'Install'
        : updateStatus === 'error'
          ? 'Retry'
          : 'Details';
    const statusChipClassName = updateStatus === 'available'
      ? 'bg-teal-500 text-white'
      : updateStatus === 'checking'
        ? 'bg-blue-500 text-white'
        : updateStatus === 'error'
          ? 'bg-rose-500 text-white'
          : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-100';
    const statusIcon = updateStatus === 'checking'
      ? '⏳'
      : updateStatus === 'available'
        ? '🆕'
        : updateStatus === 'error'
          ? '❌'
          : '📲';

    return (
      <button
        type="button"
        onClick={async () => {
          void triggerHeaderHaptic(updateStatus === 'available' ? 'success' : 'menuAction');
          setIsMenuOpen(false);
          setIsUserMenuOpen(false);
          setOpenMenuSection(null);
          await openAppVersionDialog(!updateInfo || updateStatus === 'error' || updateStatus === 'idle');
        }}
        disabled={isChecking || updateStatus === 'checking'}
        className={`${itemClasses.justified} disabled:opacity-50`}
        title="View installed app version details and available updates"
      >
        <div className="flex items-center">
          <span className="mr-3">{statusIcon}</span>
          <div className="flex flex-col">
            <span>{statusLabel}</span>
            <span className={`${itemClasses.subtle} max-w-[13rem] truncate`}>
              {statusSummary}
            </span>
          </div>
        </div>

        <span className={`text-xs px-2 py-1 rounded-full ${statusChipClassName}`}>
          {statusChip}
        </span>
      </button>
    );
  };

  const renderMobileModulesSection = () => {
    const closeMenu = closeMobileMenuWithHaptic;

    if (navLoading) {
      return (
        <div className="theme-text-secondary flex items-center py-3">
          <i className="fa fa-spinner fa-spin mr-2"></i>
          Loading modules...
        </div>
      );
    }

    if (Object.keys(navigationGroups).length === 0) {
      return (
        <div className="theme-text-secondary flex items-center py-3">
          <i className="fa fa-info-circle mr-2"></i>
          No modules available
        </div>
      );
    }

    return (
      <>
        {Object.entries(navigationGroups).map(([groupKey, group]) => {
          const sortedItems = group.items && group.items.length > 0
            ? [...group.items]
              .filter((item: NavigationItem) => {
                if (item.hidden) return false;
                if (item.module_key === 'browse_modules') return false;
                if (shouldHideNavigationModule(item.module_key, item.user_role)) return false;

                if (showAppLikeMenuLayout && item.allow_mobile_app && isUserLevelOnly(item.module_key)) {
                  return false;
                }

                return true;
              })
              .sort((a: NavigationItem, b: NavigationItem) => {
                const sortA = a.sort_order || 999;
                const sortB = b.sort_order || 999;
                if (sortA !== sortB) return sortA - sortB;
                return a.label.localeCompare(b.label);
              })
            : [];

          const isGeneralGroup = group.label === 'General';
          if (sortedItems.length === 0 && !(isGeneralGroup && isRoot)) {
            return null;
          }

          const groupExpanded = isModulesNavAccordionExpanded('mobile', groupKey);

          return (
            <div key={groupKey} className="border-b theme-border my-2 pb-2">
              <button
                type="button"
                onClick={() => toggleModulesNavAccordionSection('mobile', groupKey)}
                className="theme-text-secondary flex items-center justify-between py-3 w-full text-left transition-colors"
                aria-expanded={groupExpanded}
              >
                <span className="flex items-center">
                  <span className="mr-3">{isGeneralGroup ? '📦' : '🗂️'}</span>
                  <span className="font-medium">{isGeneralGroup ? 'My Modules' : group.label}</span>
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${groupExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              {groupExpanded ? (
                <div>
                  {sortedItems.map((item: NavigationItem, index: number) => {
                    const isCurrentModule = pathname?.includes(item.module_key);
                    const moduleRoute = item.route || `/client-zone/modules/${item.module_key}`;

                    return (
                      <Link
                        key={item.module_key || `mobile-module-${index}`}
                        href={moduleRoute}
                        className={`theme-text-secondary flex items-center py-3 ${isCurrentModule ? 'opacity-50' : ''}`}
                        onClick={closeMenu}
                      >
                        {isEmojiIcon(item.icon) ? (
                          <span className="mr-3">{item.icon}</span>
                        ) : (
                          <i className={`${getIconClass(item.icon)} mr-3`}></i>
                        )}
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                  {isGeneralGroup && isRoot ? (
                    <Link
                      href="/client-zone/modules/root"
                      className="flex items-center py-3 font-semibold text-red-600 dark:text-red-400"
                      onClick={closeMenu}
                    >
                      <span className="mr-3">🛡️</span>
                      Root Administration
                    </Link>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="border-t theme-border my-2 pt-1">
          <button
            type="button"
            onClick={() => toggleModulesNavAccordionSection('mobile', MODULES_RESOURCES_ACCORDION_GROUP_KEY)}
            className="theme-text-secondary flex items-center justify-between py-3 w-full text-left transition-colors"
            aria-expanded={isModulesNavAccordionExpanded('mobile', MODULES_RESOURCES_ACCORDION_GROUP_KEY)}
          >
            <span className="flex items-center">
              <span className="mr-3">📚</span>
              <span className="font-medium">Resources</span>
            </span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isModulesNavAccordionExpanded('mobile', MODULES_RESOURCES_ACCORDION_GROUP_KEY) ? 'rotate-180' : ''}`}
            />
          </button>
          {isModulesNavAccordionExpanded('mobile', MODULES_RESOURCES_ACCORDION_GROUP_KEY) ? (
            <div className="pb-1">
              <Link
                href="/resources/software-downloads"
                className="theme-text-secondary flex items-center py-3"
                onClick={closeMenu}
              >
                <span className="mr-3">💻</span>
                Software Downloads
              </Link>
              <Link
                href="/resources/videos"
                className="theme-text-secondary flex items-center py-3"
                onClick={closeMenu}
              >
                <span className="mr-3">🎬</span>
                Product Videos
              </Link>
              <Link
                href="/resources/user-guides"
                className="theme-text-secondary flex items-center py-3"
                onClick={closeMenu}
              >
                <span className="mr-3">🎓</span>
                User Guides
              </Link>
              <Link
                href="/resources/downloads"
                className="theme-text-secondary flex items-center py-3"
                onClick={closeMenu}
              >
                <span className="mr-3">📄</span>
                Downloads
              </Link>
              <Link
                href="/resources/faqs"
                className="theme-text-secondary flex items-center py-3"
                onClick={closeMenu}
              >
                <span className="mr-3">❓</span>
                FAQs
              </Link>
            </div>
          ) : null}
        </div>

        {isMainPlatformDomain ? (
          <div className="border-b theme-border my-2 pb-2">
            <button
              type="button"
              onClick={() => toggleModulesNavAccordionSection('mobile', MODULES_NAVIGATION_ACCORDION_GROUP_KEY)}
              className="theme-text-secondary flex items-center justify-between py-3 w-full text-left transition-colors"
              aria-expanded={isModulesNavAccordionExpanded('mobile', MODULES_NAVIGATION_ACCORDION_GROUP_KEY)}
            >
              <span className="flex items-center">
                <span className="mr-3">🧭</span>
                <span className="font-medium">Navigation</span>
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isModulesNavAccordionExpanded('mobile', MODULES_NAVIGATION_ACCORDION_GROUP_KEY) ? 'rotate-180' : ''}`}
              />
            </button>
            {isModulesNavAccordionExpanded('mobile', MODULES_NAVIGATION_ACCORDION_GROUP_KEY) ? (
              <div className="pb-1">
                <Link href="/" className="theme-text-secondary flex items-center py-3" onClick={closeMenu}>
                  <span className="mr-3">🏠</span> Home
                </Link>
                <Link href="/about" className="theme-text-secondary flex items-center py-3" onClick={closeMenu}>
                  <span className="mr-3">ℹ️</span> About Us
                </Link>
                <Link href="/products" className="theme-text-secondary flex items-center py-3" onClick={closeMenu}>
                  <span className="mr-3">🗂️</span> All Products
                </Link>
                {productMenuChildren.map((child) => (
                  <Link
                    key={child.href}
                    href={child.href}
                    className="theme-text-secondary flex items-center py-2 pl-4 text-sm"
                    onClick={closeMenu}
                  >
                    <i className={`fa ${child.icon} mr-3 text-[12px] text-[#047cde] dark:text-sky-300`}></i>
                    {child.label}
                  </Link>
                ))}
                <Link href="/products/industries" className="theme-text-secondary flex items-center py-3" onClick={closeMenu}>
                  <span className="mr-3">🏭</span> Industry Solutions
                </Link>
                <Link href="/contact" className="theme-text-secondary flex items-center py-3" onClick={closeMenu}>
                  <span className="mr-3">✉️</span> Contact Us
                </Link>
                <Link href="/pricing" className="theme-text-secondary flex items-center py-3" onClick={closeMenu}>
                  <span className="mr-3">🏷️</span> Pricing
                </Link>
                <Link href="/support" className="theme-text-secondary flex items-center py-3" onClick={closeMenu}>
                  <span className="mr-3">🎫</span> Support
                </Link>
                <Link href="/case-studies" className="theme-text-secondary flex items-center py-3" onClick={closeMenu}>
                  <span className="mr-3">🧩</span> Case Studies
                </Link>
                <Link href="/legal" className="theme-text-secondary flex items-center py-3" onClick={closeMenu}>
                  <span className="mr-3">⚖️</span> Legal
                </Link>
                <a
                  href={process.env.NODE_ENV === 'production' ? 'https://developer.totalaccess.co.za/' : 'https://dev-developer.totalaccess.co.za'}
                  className="theme-text-secondary flex items-center py-3"
                  onClick={closeMenu}
                >
                  <span className="mr-3">💻</span> Developer Docs
                </a>
              </div>
            ) : null}
          </div>
        ) : null}
      </>
    );
  };

  const renderMobileModulesAccordion = () => {
    // The Capacitor app has no module access — workflow views only.
    if (isMobileApp) {
      return null;
    }

    return renderMenuAccordion({
      sectionKey: 'modules',
      title: 'Modules',
      icon: '📦',
      variant: 'mobile',
      children: renderMobileModulesSection(),
    });
  };

  // USER DOCS: the My Corner user-menu surface (Profile, ESS, My Devices,
  // Managed Tasks, Support, favourites, theme, display size) plus the My
  // Workspace / My System / Session & App sections below are documented in
  // next_portal/user-docs/content/docs/getting-started/my-corner.mdx
  // (published at user-docs.totalaccess.co.za/getting-started/my-corner) —
  // keep that page in sync when adding or renaming menu entries.
  const renderMyCornerSection = (variant: MenuSectionVariant, embedded?: boolean) => {
    const itemClasses = getMenuItemClasses(variant);
    const closeMenu = () => {
      if (variant === 'desktop') {
        setIsUserMenuOpen(false);
      } else {
        closeMobileMenuWithHaptic();
        return;
      }
      setOpenMenuSection(null);
    };

    const myCornerQuickActions: Array<{
      key: 'calls' | 'email' | 'messages' | 'calendar' | 'myshell';
      label: string;
      icon: ReactNode;
      badge: number;
      onClick: () => void;
    }> = [];

    if (hasConnectAccess && (isFeatureEnabled('voice_calls') || isFeatureEnabled('video_calls'))) {
      myCornerQuickActions.push({
        key: 'calls',
        label: 'Calls',
        icon: <Phone className={`h-3.5 w-3.5 ${HEADER_QUICK_ACTION_ICON_CLASS}`} />,
        badge: badgeCounts.missedCallsCount,
        onClick: () => {
          closeMenu();
          setIsCallModalOpen(true);
        },
      });
    }

    if (hasConnectAccess) {
      myCornerQuickActions.push({
        key: 'email',
        label: 'Email',
        icon: <Mail className={`h-3.5 w-3.5 ${HEADER_QUICK_ACTION_ICON_CLASS}`} />,
        badge: badgeCounts.emailUnreadCount,
        onClick: () => {
          closeMenu();
          toggleEmailExperience();
        },
      });
    }

    if (hasConnectAccess && isFeatureEnabled('message_centre')) {
      myCornerQuickActions.push({
        key: 'messages',
        label: 'Messages',
        icon: <MessageSquare className={`h-3.5 w-3.5 ${HEADER_QUICK_ACTION_ICON_CLASS}`} />,
        badge: badgeCounts.messagesUnreadCount,
        onClick: () => {
          closeMenu();
          toggleModal('messages');
        },
      });
    }

    if (hasConnectAccess) {
      myCornerQuickActions.push({
        key: 'calendar',
        label: 'Calendar',
        icon: <Calendar className={`h-3.5 w-3.5 ${HEADER_QUICK_ACTION_ICON_CLASS}`} />,
        badge: badgeCounts.calendarUnreadCount,
        onClick: () => {
          closeMenu();
          toggleModal('calendar');
        },
      });
    }

    const content = (
      <>
        <button
          type="button"
          onClick={() => {
            closeMenu();
            setProfileInitialTab('personal');
            setShowProfileModal(true);
          }}
          className={itemClasses.base}
          data-tour-id="menu-item-profile"
        >
          <span className="mr-3">👤</span>
          Profile
        </button>

        {hasEssAccess && (
          <button
            type="button"
            onClick={() => {
              closeMenu();
              setEssInitialTab(undefined);
              setEssInitialSection(undefined);
              setShowEssModal(true);
            }}
            className={itemClasses.base}
            data-tour-id="menu-item-ess-portal"
          >
            <span className="mr-3">💼</span>
            Employee Self-Service
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            closeMenu();
            setShowMyDevicesModal(true);
          }}
          className={itemClasses.base}
          data-tour-id="menu-item-my-devices"
        >
          <span className="mr-3">📱</span>
          My Devices
        </button>

        {user?.role !== 'guest' && hasValidClient && (
          <button
            type="button"
            onClick={() => {
              closeMenu();
              openManagedTasksTab('tasks');
            }}
            className={itemClasses.justified}
            data-tour-id="menu-item-managed-tasks"
          >
            <div className="flex items-center">
              <span className="mr-3">📋</span>
              <span>Managed Tasks</span>
            </div>
            {taskCount > 0 && (
              <span className={itemClasses.subtle}>{taskCount} due</span>
            )}
          </button>
        )}

        {variant !== 'desktop' && myCornerQuickActions.length > 0 && (
          <div className="py-3">
            <div className="flex justify-center">
              <div className="inline-flex flex-wrap items-center justify-center gap-1.5 rounded-2xl border border-slate-200/90 bg-slate-50/90 px-2 py-1.5 shadow-sm dark:border-slate-700/80 dark:bg-slate-900/65">
                {myCornerQuickActions.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    onClick={action.onClick}
                    className="relative inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/80 dark:hover:border-slate-600 dark:hover:bg-slate-700"
                    title={action.label}
                    aria-label={action.label}
                  >
                    <span className="flex items-center justify-center">{action.icon}</span>
                    {action.badge > 0 && (
                      <span className={`absolute -top-1 -right-1 ${HEADER_QUICK_ACTION_BADGE_CLASS} text-[8px] font-bold rounded-full min-w-[13px] h-[13px] flex items-center justify-center px-0.5`}>
                        {action.badge > 99 ? '99+' : action.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {user?.role !== 'guest' && (
          <button
            type="button"
            onClick={() => {
              void triggerHeaderHaptic('menuAction');
              toggleFavouritesBar();
            }}
            className={itemClasses.justified}
            title={favouritesBarVisible ? 'Hide favourites bar' : 'Show favourites bar'}
          >
            <div className="flex items-center">
              <Star
                className={`mr-3 h-4 w-4 shrink-0 ${HEADER_QUICK_ACTION_SVG_OUTLINE} ${favouritesBarVisible
                  ? 'fill-amber-400 text-amber-300'
                  : 'fill-white text-white'
                  }`}
              />
              <span>Favourites</span>
            </div>
            <span className={itemClasses.subtle}>{favouritesBarVisible ? 'Shown' : 'Hidden'}</span>
          </button>
        )}

        <div className={variant === 'desktop' ? 'hover:theme-surface-dark transition-colors' : ''}>
          <ThemeToggle variant="inline" className={itemClasses.themeToggle} />
        </div>

      </>
    );

    return renderMenuAccordion({
      embedded,
      sectionKey: 'my-corner',
      title: 'My Corner',
      icon: '👤',
      variant,
      children: content,
    });
  };

  // IAM console access — same gate as the admin-hub page
  // (owner/root/system_admin). Checked across navigation + auth role sources
  // because either can briefly lag after login or a client switch.
  const hasIamAccess = useMemo(() => {
    const roleCandidates = [userRole, navUser?.role, user?.role]
      .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
      .map((role) => role.toLowerCase());

    return isRoot
      || roleCandidates.includes('root')
      || roleCandidates.includes('owner')
      || roleCandidates.includes('system_admin');
  }, [user?.role, isRoot, navUser?.role, userRole]);

  const renderMyWorkspaceSection = (variant: MenuSectionVariant, embedded?: boolean) => {
    const itemClasses = getMenuItemClasses(variant);
    const closeMenu = () => {
      if (variant === 'desktop') {
        setIsUserMenuOpen(false);
      } else {
        closeMobileMenuWithHaptic();
        return;
      }
      setOpenMenuSection(null);
    };

    const content = (
      <>
        {(isRoot || isAdmin || userRole === 'owner') && (
          <Link
            href="/client-zone/admin-hub"
            className={itemClasses.base}
            onClick={closeMenu}
          >
            <span className="mr-3">⚙️</span>
            Admin Hub
          </Link>
        )}

        {hasIamAccess && (
          <button
            type="button"
            onClick={() => {
              closeMenu();
              setShowIamModal(true);
            }}
            className={itemClasses.base}
            data-tour-id="menu-item-iam-console"
          >
            <span className="mr-3">🛡️</span>
            IAM Console
          </button>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            supportModalRef.current?.open();
            emitGuidedTourTrainingEvent({ type: 'bug_report_opened' });
            closeMenu();
          }}
          data-tour-id="menu-item-bug-report"
          className={`flex items-center w-full px-4 py-2 theme-text-primary hover:theme-surface-dark text-left transition-colors ${variant === 'mobile' ? '!px-0 !py-3 theme-text-secondary' : ''
            }`}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--theme-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '';
          }}
        >
          <span className="mr-3">🎧</span>
          Help &amp; Feedback
        </button>

        {hasMultipleClients && !isWhiteLabel && !loadingWhitelabel && (
          <button
            type="button"
            onClick={() => {
              if (isClientSwitchBlockedByDemo) {
                return;
              }

              closeMenu();
              setIsClientSelectorOpen(true);
            }}
            disabled={isClientSwitchBlockedByDemo}
            title={isClientSwitchBlockedByDemo ? 'Exit Training Mode before switching clients' : 'Switch Clients'}
            className={`${itemClasses.base} disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            <span className="mr-3">🔄</span>
            {isClientSwitchBlockedByDemo ? 'Exit Training Mode to Switch Clients' : 'Switch Clients'}
          </button>
        )}
      </>
    );

    return renderMenuAccordion({
      embedded,
      sectionKey: 'my-workspace',
      title: 'My Workspace',
      icon: '🏢',
      variant,
      children: content,
    });
  };

  const renderMySystemItems = (variant: MenuSectionVariant) => {
    const itemClasses = getMenuItemClasses(variant);
    const closeMenu = () => {
      if (variant === 'desktop') {
        setIsUserMenuOpen(false);
      } else {
        closeMobileMenuWithHaptic();
        return;
      }
      setOpenMenuSection(null);
    };

    return (
      <>
        <button
          type="button"
          onClick={() => {
            closeMenu();
            setShowDiagnosticModal(true);
          }}
          className={itemClasses.base}
          data-tour-id="menu-item-diagnostics"
        >
          <span className="mr-3">🔧</span>
          Diagnostics
        </button>

        {renderAvailableUpdatesActions(variant)}

        {installedPwa && variant === 'mobile' && !isTrainingModeMenuRestricted && (
          <button
            type="button"
            onClick={() => {
              closeMenu();
              void runInstalledPwaRefresh({
                closeMenu: false,
                acknowledgedVersion: pwaVersionInfo.version,
              });
            }}
            disabled={pwaUpdateStatus === 'checking'}
            className={`${itemClasses.justified} disabled:opacity-50`}
            title="Check for a newer installed PWA build and force a fresh reload when needed"
          >
            <div className="flex items-center">
              <span className="mr-3">
                {pwaUpdateStatus === 'checking'
                  ? '⏳'
                  : pwaUpdateStatus === 'refreshing'
                    ? '🔄'
                    : pwaUpdateStatus === 'updated'
                      ? '🆕'
                      : pwaUpdateStatus === 'up-to-date'
                        ? '✅'
                        : pwaUpdateStatus === 'error'
                          ? '❌'
                          : '📲'}
              </span>
              <div className="flex flex-col">
                <span>
                  {pwaUpdateStatus === 'checking'
                    ? 'Checking...'
                    : pwaUpdateStatus === 'refreshing'
                      ? 'Refreshing...'
                      : pwaUpdateStatus === 'updated'
                        ? 'Update Found'
                        : pwaUpdateStatus === 'up-to-date'
                          ? 'Up to Date'
                          : pwaUpdateStatus === 'error'
                            ? 'Refresh Failed'
                            : 'Check for Updates'}
                </span>
                <span className="text-xs opacity-60">
                  {pwaVersionInfo.loading
                    ? 'Loading version details...'
                    : pwaUpdateStatus === 'refreshing'
                      ? 'Clearing stale assets and loading a fresh copy...'
                      : pwaUpdateStatus === 'updated'
                        ? 'New build activated — reopening the app...'
                        : `Latest deploy v${pwaVersionInfo.version} • ${pwaVersionInfo.deployedAt}`}
                </span>
              </div>
            </div>
          </button>
        )}

        {showColdStartLogToggle && (
          <button
            type="button"
            onClick={handleColdStartLogsToggle}
            className={itemClasses.justified}
            title="Dev only: enable or disable persistent Capacitor cold-start diagnostics"
          >
            <div className="flex items-center">
              <span className="mr-3">{coldStartLogsEnabled ? '🧊' : '🌙'}</span>
              <span>Cold-start logs</span>
            </div>
            <span className={itemClasses.subtle}>{coldStartLogsEnabled ? 'On' : 'Off'}</span>
          </button>
        )}
      </>
    );
  };

  const renderSessionAndAppSection = (variant: MenuSectionVariant, embedded?: boolean) => {
    const itemClasses = getMenuItemClasses(variant);

    return renderMenuAccordion({
      embedded,
      sectionKey: 'session-app',
      title: 'Session & App',
      icon: '🧰',
      variant,
      children: (
        <>
          {renderSessionAndAppActions({
            buttonClassName: itemClasses.base,
            labelClassName: 'flex-1',
            showSignOutName: variant === 'mobile',
          })}
          <div className="mt-1 border-t theme-border" />
          {renderMySystemItems(variant)}
        </>
      ),
    });
  };

  /** Two-pane user dropdown — left rail picks the section (openMenuSection),
   *  right pane renders that section's existing content unchanged. */
  const renderUserMegaInner = () => {
    const sections: { key: MenuSectionKey; label: string; emoji: string; render: () => ReactNode }[] = [
      { key: 'my-corner', label: 'My Corner', emoji: '👤', render: () => renderMyCornerSection('desktop', true) },
      { key: 'my-workspace', label: 'My Workspace', emoji: '🗂️', render: () => renderMyWorkspaceSection('desktop', true) },
      { key: 'session-app', label: 'Session & App', emoji: '🧰', render: () => renderSessionAndAppSection('desktop', true) },
    ];
    const activeKey = openMenuSection ?? 'my-corner';
    const activeSection = sections.find((s) => s.key === activeKey) ?? sections[0];
    return (
      <>
        <div className="w-72 shrink-0 overflow-y-auto p-4">
          {activeSection.render()}
        </div>
        <div className="w-52 shrink-0 overflow-y-auto border-l border-slate-100 py-3 dark:border-slate-800">
          <div className="px-4 pb-2">
            <div className="truncate text-lg font-bold text-slate-900 dark:text-white">
              {user?.display_name || `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || user?.email || 'My Account'}
            </div>
            <div className="truncate text-xs text-slate-500 dark:text-slate-400">Your workspace menu</div>
          </div>
          {sections.map((s) => (
            <button
              key={s.key}
              type="button"
              role="menuitem"
              onMouseEnter={() => setOpenMenuSection(s.key)}
              onClick={() => setOpenMenuSection(s.key)}
              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ease-in-out ${
                activeKey === s.key
                  ? 'bg-slate-100 font-medium text-[#047cde] dark:bg-slate-700/60 dark:text-sky-300'
                  : 'text-slate-800 hover:bg-slate-100 hover:text-[#047cde] dark:text-slate-100 dark:hover:bg-slate-700/50 dark:hover:text-sky-300'
              }`}
            >
              <span className="w-4 text-center text-base leading-none">{s.emoji}</span>
              <span className="truncate">{s.label}</span>
              <ChevronRight className="ml-auto h-3.5 w-3.5 shrink-0 opacity-40" />
            </button>
          ))}
        </div>
      </>
    );
  };

  const trainingModeHeaderBadge = (
    <div className="inline-flex h-9 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10">
      <span className="text-base leading-none">🎯</span>
      <span className="text-sm font-semibold text-amber-800 dark:text-amber-200">Training / Demo Mode</span>
    </div>
  );

  const shouldAbsoluteCenterDesktopNav = mounted && isAuthenticated && !showAppLikeMenuLayout;

  const isDeveloperPortalPage = pathname?.startsWith('/developer-portal');

  if (isDeveloperPortalPage) {
    return (
      <>
        <header
          ref={headerElementRef}
          className="sticky top-0 z-[80] shadow-sm theme-border border-b theme-surface flex-shrink-0"
        >
          <div className="w-full px-4 lg:px-8 xl:px-12">
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Link href="/developer-portal" className="flex items-center">
                  <img
                    src={publicSiteLogo || currentLogo}
                    alt="Total Access Solutions"
                    width={140}
                    height={45}
                    loading="eager"
                    style={{ maxHeight: '45px', width: 'auto', height: 'auto' }}
                    suppressHydrationWarning
                  />
                </Link>
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-full hidden sm:inline-block"
                  style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-secondary-dark)' }}
                >
                  Developer Platform
                </span>
              </div>

              <div className="flex items-center gap-3">
                <Link
                  href="/docs"
                  className="hidden sm:inline-flex items-center gap-2 text-sm font-medium theme-text-secondary hover:theme-primary transition-colors"
                >
                  <i className="fa fa-book-open text-xs"></i>
                  <span>Docs</span>
                </Link>

                <ThemeToggle />

                {mounted && isAuthenticated ? (
                  <div className="flex items-center gap-3">
                    <Link
                      href="/client-zone/modules/total-integration"
                      className="px-2 py-0.5 md:px-3 md:py-1 text-white text-[10px] md:text-xs font-bold rounded-full uppercase tracking-wide shadow-md hover:opacity-90 transition-opacity cursor-pointer"
                      style={{ backgroundColor: '#047cde' }}
                    >
                      Developer Portal
                    </Link>
                    <div
                      className="relative"
                      ref={userDropdownRef}
                    >
                      <button
                        type="button"
                        onClick={toggleUserMenu}
                        className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                        aria-haspopup="menu"
                        aria-expanded={isUserMenuOpen}
                        data-tour-id="user-dropdown-trigger"
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <i className="fa fa-user text-sm shrink-0"></i>
                          <span className="truncate min-w-0 text-[15px]">{getUserDisplayName()}</span>
                        </span>
                        <svg
                          className={`h-4 w-4 shrink-0 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {isUserMenuOpen && headerElementRef.current &&
                        createPortal(
                        <div
                          className={HEADER_PUBLIC_MEGA_PANEL_CLASS}
                          role="menu"
                          data-header-mega-panel="true"
                          onMouseEnter={cancelPublicMenuClose}
                        >
                          <div className="mx-auto flex h-[min(72vh,32rem)] max-w-7xl px-4 lg:px-8">
                            <div className="ml-auto flex max-h-[min(72vh,32rem)]">
                              {renderUserMegaInner()}
                            </div>
                          </div>
                        </div>,
                        headerElementRef.current
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={HEADER_PUBLIC_AUTH_GROUP_CLASS}>
                    <Link
                      href="/auth/login?redirect=/client-zone/modules/total-integration"
                      className={HEADER_PUBLIC_SECONDARY_AUTH_BUTTON_CLASS}
                    >
                      <i className="fa fa-sign-in-alt text-[10px]"></i>
                      <span>Login</span>
                    </Link>
                    <Link
                      href="/auth/signup?redirect=/client-zone/modules/total-integration"
                      className={HEADER_PUBLIC_PRIMARY_AUTH_BUTTON_CLASS}
                    >
                      <i className="fa fa-user-plus text-[10px]"></i>
                      <span>Sign Up</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
      </>
    );
  }

  return (
    <>
      {/* Safe area background for notch/camera cutout - matches header color */}
      {/* Only show on Capacitor mobile app (for notch/camera cutout), hidden on desktop/web */}
      {!hideHeaderForCapacitorLogin && showAppLikeMenuLayout && !immersedClientZoneModule && (
        <div
          className="fixed top-0 left-0 right-0 h-[env(safe-area-inset-top)] theme-surface z-[79]"
          style={whitelabelHeaderColor ? { backgroundColor: whitelabelHeaderColor } : undefined}
        />
      )}

      {/* Header - Hidden on Capacitor login page; immersive mobile shells hide portal chrome only in app-like layout */}
      {!hideHeaderForCapacitorLogin && !desktopHeaderHidden && !(showAppLikeMenuLayout && immersedClientZoneModule) && (
        <header
          ref={headerElementRef}
          data-header-custom={whitelabelHeaderColor ? '' : undefined}
          className={`sticky top-0 z-[80] shadow-sm theme-border border-b pt-[env(safe-area-inset-top)] transition-transform duration-300 flex-shrink-0 ${showHeader ? 'translate-y-0' : '-translate-y-full'
            } ${!whitelabelHeaderColor ? 'theme-surface' : ''}`}
          style={headerContrastStyle}
        >
          <div className={`w-full px-2 lg:px-4 xl:px-6 2xl:px-8 ${!showAppLikeMenuLayout ? 'md:px-8 lg:px-12 xl:px-16 2xl:px-20' : ''}`}>
            <div className="relative flex items-center justify-between py-2 lg:py-3">
              {/* Left side: Logo and Badge */}
              <div className="flex items-center space-x-1 md:space-x-4">
                {/* Logo - conditionally linked based on white-label mode, desktop app, and mobile app */}
                {showAppLikeMenuLayout ? (
                  // App-like mobile session: logo links to dashboard
                  <Link href="/client-zone/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
                    <img
                      src={whitelabelLogo || publicSiteLogo}
                      alt={whitelabelTitle || "Total Access Solutions"}
                      width={100}
                      height={35}
                      loading="eager"
                      style={{ maxHeight: '35px', width: 'auto', height: 'auto' }}
                      suppressHydrationWarning
                    />
                  </Link>
                ) : isWhiteLabel || isDesktopApp ? (
                  // White-label / desktop shell: prefer the configured return URL, otherwise keep the logo static
                  whitelabelReturnUrl ? (
                    <a
                      href={whitelabelReturnUrl}
                      className="flex items-center hover:opacity-80 transition-opacity"
                      title="Back to Website"
                    >
                      <img
                        src={whitelabelLogo || publicSiteLogo}
                        alt={whitelabelTitle || "Total Access Solutions"}
                        width={isDesktopApp ? 100 : 140}
                        height={isDesktopApp ? 35 : 45}
                        loading="eager"
                        style={{ maxHeight: isDesktopApp ? '35px' : '45px', width: 'auto', height: 'auto' }}
                        suppressHydrationWarning
                      />
                    </a>
                  ) : (
                    <div className="flex items-center cursor-default">
                      <img
                        src={whitelabelLogo || publicSiteLogo}
                        alt={whitelabelTitle || "Total Access Solutions"}
                        width={isDesktopApp ? 100 : 140}
                        height={isDesktopApp ? 35 : 45}
                        loading="eager"
                        style={{ maxHeight: isDesktopApp ? '35px' : '45px', width: 'auto', height: 'auto' }}
                        suppressHydrationWarning
                      />
                    </div>
                  )
                ) : (
                  <Link href="/" className="flex items-center">
                    <img
                      src={isInClientZone ? (whitelabelLogo || publicSiteLogo) : publicSiteLogo}
                      alt="Total Access Solutions"
                      width={140}
                      height={45}
                      loading="eager"
                      style={{ maxHeight: '45px', width: 'auto', height: 'auto' }}
                      suppressHydrationWarning
                    />
                  </Link>
                )}

                {/* Platform Badge - Always shown for easy identification */}
                {mounted && (
                  <>
                    {!showAppLikeMenuLayout && (
                      <span className="text-gray-400 text-xl hidden md:inline">|</span>
                    )}
                    {/* Desktop App Badge */}
                    {isDesktopApp ? (
                      <div className="flex items-center gap-2">
                        <Link
                          href="/client-zone"
                          className="px-2 py-0.5 md:px-3 md:py-1 text-white text-[10px] md:text-xs font-bold rounded-full uppercase tracking-wide shadow-md hover:opacity-90 transition-opacity cursor-pointer"
                          style={{ backgroundColor: '#047cde' }}
                        >
                          {isAuthenticated ? 'Dashboard' : 'Desktop'}
                        </Link>
                        {!isSearchOpen && (
                          <button
                            onClick={() => setIsSearchOpen(!isSearchOpen)}
                            className="p-1 md:p-1.5 transition-colors theme-text-secondary hover:theme-primary"
                            title="Search"
                          >
                            <i className="fa fa-search text-sm md:text-lg"></i>
                          </button>
                        )}
                      </div>
                    ) : /* Mobile App Badge */
                      showAppLikeMenuLayout ? null /* Network status shown in centered section */
                        : /* White-label Badge */
                        isWhiteLabel ? (
                          <div className="flex items-center gap-2">
                            {/* Cloud badge (unauthenticated) or Dashboard badge (authenticated) */}
                            {isAuthenticated ? (
                              <Link
                                href="/client-zone"
                                className="px-2 py-0.5 md:px-3 md:py-1 text-white text-[10px] md:text-xs font-bold rounded-full uppercase tracking-wide shadow-md hover:opacity-90 transition-opacity cursor-pointer"
                                style={{ backgroundColor: '#047cde' }}
                              >
                                Dashboard
                              </Link>
                            ) : (
                              <div className="px-2 py-0.5 md:px-3 md:py-1 text-white text-[10px] md:text-xs font-bold rounded-full uppercase tracking-wide shadow-md" style={{ backgroundColor: '#047cde' }}>
                                Cloud
                              </div>
                            )}
                            {/* Back to Website button - only show if return URL is configured AND user is not signed in */}
                            {whitelabelReturnUrl && !isAuthenticated && (
                              <a
                                href={whitelabelReturnUrl}
                                className="hidden sm:flex items-center gap-1 px-2 py-0.5 md:px-3 md:py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[10px] md:text-xs font-medium rounded-full shadow-md hover:from-blue-600 hover:to-blue-700 transition-all"
                              >
                                <i className="fa fa-arrow-left text-[8px] md:text-[10px]"></i>
                                <span>Website</span>
                              </a>
                            )}
                            {/* Search icon - only show when authenticated on whitelabel and in a module */}
                            {isAuthenticated && !isSearchOpen && (
                              <button
                                onClick={() => setIsSearchOpen(!isSearchOpen)}
                                className="p-1 md:p-1.5 transition-colors theme-text-secondary hover:theme-primary"
                                title="Search"
                              >
                                <i className="fa fa-search text-sm md:text-lg"></i>
                              </button>
                            )}
                          </div>
                        ) : /* Portal Badge (authenticated) */
                          isAuthenticated ? (
                            <div className="flex items-center gap-2">
                              <Link
                                href={isDeveloperPortalDomain ? '/developer-portal' : '/client-zone'}
                                className="px-2 py-0.5 md:px-3 md:py-1 text-white text-[10px] md:text-xs font-bold rounded-full uppercase tracking-wide shadow-md hover:opacity-90 transition-all"
                                style={{ backgroundColor: '#047cde' }}
                              >
                                {isDeveloperPortalDomain ? 'Developer Portal' : 'Dashboard'}
                              </Link>
                              {!isSearchOpen && (
                                <button
                                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                                  className="p-1 md:p-1.5 transition-colors theme-text-secondary hover:theme-primary"
                                  title="Search"
                                >
                                  <i className="fa fa-search text-sm md:text-lg"></i>
                                </button>
                              )}
                            </div>
                          ) : /* Website Badge (not authenticated) */
                            (
                              <div className="hidden md:block px-3 py-1 text-white text-xs font-bold rounded-full uppercase tracking-wide shadow-md" style={{ backgroundColor: '#047cde' }}>
                                Enterprise Platform
                              </div>
                            )}
                  </>
                )}
              </div>

              {/* App-like mobile - GPS status icon to the left of the centered Online badge */}
              {mounted && showAppLikeMenuLayout && isAuthenticated && gpsEnabled !== null && (
                <div
                  className="absolute md:hidden flex items-center z-50"
                  style={{ left: 'calc(50% - 55px)', transform: 'translateX(-100%)' }}
                >
                  <i
                    className={`fa fa-map-marker-alt text-base ${gpsEnabled ? 'text-green-500' : 'text-red-500'}`}
                    title={gpsEnabled ? 'GPS enabled' : 'GPS disabled'}
                  />
                </div>
              )}

              {/* App-like mobile - Notification bell to right of center badge */}
              {mounted && showAppLikeMenuLayout && isAuthenticated && !isMdDesktopViewport && user?.role !== 'guest' && hasValidClient && (
                <div
                  className="absolute md:hidden flex items-center gap-2 z-50"
                  style={{ left: 'calc(50% + 55px)' }}
                >
                  <NotificationBell
                    buttonClassName="p-1"
                    iconClassName={`h-4 w-4 ${HEADER_QUICK_ACTION_ICON_CLASS}`}
                    onNavigate={(link) => router.push(link)}
                    onOpenThread={(threadId) => {
                      setPendingMessagesThreadId(threadId);
                      openModal('messages');
                      setTimeout(() => setPendingMessagesThreadId(null), 2000);
                    }}
                    onOpenManagedTasks={() => openManagedTasksTab('tasks')}
                  />
                </div>
              )}

              {/* Mobile App / Dev Browser Override - Absolutely Centered Network Status Badge */}
              {mounted && showMobileSyncBadge && isAuthenticated && (
                <div
                  className="absolute left-1/2 top-1/2 z-50 flex items-center gap-1.5 -translate-x-1/2 -translate-y-1/2"
                  style={{ top: 'calc(50% + 8px)' }}
                >
                  <div className="flex flex-col items-center leading-none" style={{ transform: 'translateY(0px)' }}>
                    {/* Network/Sync Status Badge - 3 states: ONLINE, OFFLINE, SYNCING */}
                    <button
                      data-testid="mobile-sync-status-badge"
                      onClick={() => {
                        if (masterSyncModal.canPrepareOffline) {
                          masterSyncModal.openModal();
                        } else if (networkStatus === 'syncing') {
                          cancelSync();
                        } else if (networkStatus === 'online' && totalPending > 0) {
                          triggerSync();
                        }
                      }}
                      className={`relative px-2 py-0.5 text-white text-[10px] font-bold rounded-full uppercase tracking-wide shadow-md transition-all ${mobileBadgeSyncActive
                        ? 'bg-blue-500 active:bg-blue-600'
                        : networkStatus === 'online'
                          ? 'bg-green-500'
                          : networkStatus === 'offline-healthy'
                            ? 'bg-amber-500'
                            : 'bg-red-500'
                        }`}
                      title={
                        `${showBrowserSyncBadgeOverride ? 'Browser test mode · ' : ''}${isUploadSyncing
                          ? `Syncing: ${syncProgress.message || 'Uploading data...'}${syncProgress.currentModule ? ` (${syncProgress.currentModule})` : ''}`
                          : isOfflinePreparing
                            ? (masterSyncModal.progress?.step || 'Preparing offline data…')
                            : masterSyncModal.canPrepareOffline
                              ? networkStatus !== 'online'
                                ? 'Tap to review offline downloads'
                                : 'Tap to open offline downloads'
                              : networkStatus === 'online'
                                ? totalPending > 0 ? 'Tap to sync' : 'Connected and up to date'
                                : 'Working offline'
                        }`
                      }
                    >
                      {/* Pulse animation for syncing state */}
                      {mobileBadgeSyncActive && (
                        <span className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-75" />
                      )}
                      <span className="relative flex items-center gap-1">
                        {mobileBadgeSyncActive && (
                          <i className="fa fa-sync-alt fa-spin text-[8px] flex-shrink-0" />
                        )}
                        <span>{mobileSyncBadgeLabel}</span>
                      </span>
                    </button>

                    {appVariationBadge && (
                      <div
                        className="px-1.5 py-0.5 text-white text-[9px] font-bold rounded-full uppercase tracking-[0.12em] shadow-md leading-none"
                        style={{ backgroundColor: '#047cde', marginTop: '4px', marginBottom: '4px' }}
                        title={`App variation: ${appVariationBadge.label}`}
                      >
                        {appVariationBadge.kind === 'android' ? 'ANDROID' : appVariationBadge.label}
                      </div>
                    )}
                  </div>

                  {/* Pending sync badge - show count when offline with pending actions */}
                  {totalPending > 0 && !mobileBadgeSyncActive && (
                    <span
                      data-testid="mobile-sync-pending-badge"
                      className="px-1.5 py-0.5 bg-orange-500 text-white text-[9px] font-bold rounded-full cursor-pointer"
                      title={`${syncStats.pendingActions} clock actions, ${syncStats.pendingWorkItems || 0} work items, ${syncStats.pendingWorkItemUploads} uploads, ${syncStats.pendingChildEntries || 0} child entries, ${syncStats.pendingFormSubmissions || 0} forms, ${syncStats.pendingSmartTaskActions || 0} workflow actions, ${syncStats.pendingMessages || 0} messages, ${syncStats.pendingEmails || 0} emails pending sync`}
                      onClick={() => networkStatus === 'online' && triggerSync()}
                    >
                      {totalPending > 99 ? '99+' : totalPending}
                    </span>
                  )}
                </div>
              )}

              {/* Mobile-only Quick Action Icons (Non-Capacitor) - Positioned to right of center badge */}
              {mounted && isAuthenticated && !isDesktopApp && !isCapacitor() && !showAppLikeMenuLayout && (
                <div
                  className="absolute md:hidden flex items-center z-50"
                  style={{ left: 'calc(50% + 50px)', right: '56px' }}
                >
                  {/* Icons on right side of centered badge - evenly spaced */}
                  <div className="flex items-center justify-evenly w-full">
                    {/* Notification bell — mentions, approvals, tasks due + notification feed */}
                    {!isMdDesktopViewport && user?.role !== 'guest' && hasValidClient && (
                      <NotificationBell
                        buttonClassName="p-1"
                        iconClassName={`h-4 w-4 ${HEADER_QUICK_ACTION_ICON_CLASS}`}
                        onNavigate={(link) => router.push(link)}
                        onOpenThread={(threadId) => {
                          setPendingMessagesThreadId(threadId);
                          openModal('messages');
                          setTimeout(() => setPendingMessagesThreadId(null), 2000);
                        }}
                        onOpenManagedTasks={() => openManagedTasksTab('tasks')}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Desktop Navigation - Hidden on developer portal domains and inside the Capacitor app */}
              {!isDeveloperPortalDomain && !isMobileApp && (
              <nav className={[
                'hidden md:flex items-center',
                isDesktopApp ? 'space-x-6' : 'space-x-8',
                shouldAbsoluteCenterDesktopNav ? 'lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2' : '',
                shouldAbsoluteCenterDesktopNav ? (isModulesOpen ? 'lg:z-[84]' : 'lg:z-[1]') : '',
              ].join(' ')}>
                {/* Desktop App Mode - Show Client Zone and Modules in center */}
                {mounted && isDesktopApp && isAuthenticated ? (
                  <>
                    <Link
                      href="/client-zone"
                      className={`text-lg font-semibold transition-colors ${isInClientZone
                        ? 'theme-primary'
                        : 'theme-text-secondary hover:theme-primary'
                        }`}
                    >
                      Client Zone
                    </Link>
                    {isTrainingModeMenuRestricted ? trainingModeHeaderBadge : (
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => toggleAuthMenu('modules')}
                          onMouseEnter={() => hoverAuthMenu('modules')}
                          className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                          aria-haspopup="menu"
                          aria-expanded={isModulesOpen && authMenuKey === 'modules'}
                        >
                          <i className="fa fa-th-large text-[12px]"></i>
                          <span>Modules</span>
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isModulesOpen && authMenuKey === 'modules' ? 'rotate-180' : ''}`} />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAuthMenu('resources')}
                          onMouseEnter={() => hoverAuthMenu('resources')}
                          className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                          aria-haspopup="menu"
                          aria-expanded={isModulesOpen && authMenuKey === 'resources'}
                        >
                          <i className="fa fa-book-open text-[12px]"></i>
                          <span>Resources</span>
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isModulesOpen && authMenuKey === 'resources' ? 'rotate-180' : ''}`} />
                        </button>
                        {isMainPlatformDomain && !isWhiteLabel && (
                          <button
                            type="button"
                            onClick={() => toggleAuthMenu('menu')}
                            onMouseEnter={() => hoverAuthMenu('menu')}
                            className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                            aria-haspopup="menu"
                            aria-expanded={isModulesOpen && authMenuKey === 'menu'}
                          >
                            <i className="fa fa-bars text-[12px]"></i>
                            <span>Menu</span>
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isModulesOpen && authMenuKey === 'menu' ? 'rotate-180' : ''}`} />
                          </button>
                        )}

                        {isModulesOpen && headerElementRef.current &&
                            createPortal(
                            <div
                              className={HEADER_PUBLIC_MEGA_PANEL_CLASS}
                              role="menu"
                              data-header-mega-panel="true"
                              onMouseEnter={cancelPublicMenuClose}
                            >
                              <div className="mx-auto flex h-[min(72vh,32rem)] max-w-7xl px-4 lg:px-8">
                                {renderModulesMegaInner({ emphasiseActiveRoutes: false, disableWhenMatched: true }, authMenuKey)}
                              </div>
                            </div>,
                            headerElementRef.current
                          )}
                      </div>
                    )}
                  </>
                ) : mounted && isAuthenticated && !isInClientZone ? (
                  /* Home Page Navigation for Authenticated Users - Use Menu button style */
                  <>
                    {isTrainingModeMenuRestricted ? trainingModeHeaderBadge : (
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => toggleAuthMenu('modules')}
                          onMouseEnter={() => hoverAuthMenu('modules')}
                          className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                          aria-haspopup="menu"
                          aria-expanded={isModulesOpen && authMenuKey === 'modules'}
                        >
                          <i className="fa fa-th-large text-[12px]"></i>
                          <span>Modules</span>
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isModulesOpen && authMenuKey === 'modules' ? 'rotate-180' : ''}`} />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAuthMenu('resources')}
                          onMouseEnter={() => hoverAuthMenu('resources')}
                          className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                          aria-haspopup="menu"
                          aria-expanded={isModulesOpen && authMenuKey === 'resources'}
                        >
                          <i className="fa fa-book-open text-[12px]"></i>
                          <span>Resources</span>
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isModulesOpen && authMenuKey === 'resources' ? 'rotate-180' : ''}`} />
                        </button>
                        {isMainPlatformDomain && !isWhiteLabel && (
                          <button
                            type="button"
                            onClick={() => toggleAuthMenu('menu')}
                            onMouseEnter={() => hoverAuthMenu('menu')}
                            className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                            aria-haspopup="menu"
                            aria-expanded={isModulesOpen && authMenuKey === 'menu'}
                          >
                            <i className="fa fa-bars text-[12px]"></i>
                            <span>Menu</span>
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isModulesOpen && authMenuKey === 'menu' ? 'rotate-180' : ''}`} />
                          </button>
                        )}

                        {isModulesOpen && headerElementRef.current &&
                            createPortal(
                            <div
                              className={HEADER_PUBLIC_MEGA_PANEL_CLASS}
                              role="menu"
                              data-header-mega-panel="true"
                              onMouseEnter={cancelPublicMenuClose}
                            >
                              <div className="mx-auto flex h-[min(72vh,32rem)] max-w-7xl px-4 lg:px-8">
                                {renderModulesMegaInner({ emphasiseActiveRoutes: false, disableWhenMatched: false }, authMenuKey)}
                              </div>
                            </div>,
                            headerElementRef.current
                          )}
                      </div>
                    )}

                    {/* Show Support link on main platform domains - only for unauthenticated users */}
                    {/* Authenticated users have Support in the user dropdown menu */}
                    {isMainPlatformDomain && !isAuthenticated && (
                      <Link
                        href="/support"
                        className="text-sm theme-text-secondary hover:text-[#047cde] transition-colors"
                      >
                        Support
                      </Link>
                    )}
                  </>
                ) : mounted && isAuthenticated && isInClientZone ? (
                  /* Client Zone Navigation with Menu-style Modules Dropdown */
                  <>
                    {isTrainingModeMenuRestricted ? trainingModeHeaderBadge : (
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => toggleAuthMenu('modules')}
                          onMouseEnter={() => hoverAuthMenu('modules')}
                          className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                          aria-haspopup="menu"
                          aria-expanded={isModulesOpen && authMenuKey === 'modules'}
                        >
                          <i className="fa fa-th-large text-[12px]"></i>
                          <span>Modules</span>
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isModulesOpen && authMenuKey === 'modules' ? 'rotate-180' : ''}`} />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAuthMenu('resources')}
                          onMouseEnter={() => hoverAuthMenu('resources')}
                          className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                          aria-haspopup="menu"
                          aria-expanded={isModulesOpen && authMenuKey === 'resources'}
                        >
                          <i className="fa fa-book-open text-[12px]"></i>
                          <span>Resources</span>
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isModulesOpen && authMenuKey === 'resources' ? 'rotate-180' : ''}`} />
                        </button>
                        {isMainPlatformDomain && !isWhiteLabel && (
                          <button
                            type="button"
                            onClick={() => toggleAuthMenu('menu')}
                            onMouseEnter={() => hoverAuthMenu('menu')}
                            className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                            aria-haspopup="menu"
                            aria-expanded={isModulesOpen && authMenuKey === 'menu'}
                          >
                            <i className="fa fa-bars text-[12px]"></i>
                            <span>Menu</span>
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isModulesOpen && authMenuKey === 'menu' ? 'rotate-180' : ''}`} />
                          </button>
                        )}

                        {isModulesOpen && headerElementRef.current &&
                            createPortal(
                            <div
                              className={HEADER_PUBLIC_MEGA_PANEL_CLASS}
                              role="menu"
                              data-header-mega-panel="true"
                              onMouseEnter={cancelPublicMenuClose}
                            >
                              <div className="mx-auto flex h-[min(72vh,32rem)] max-w-7xl px-4 lg:px-8">
                                {renderModulesMegaInner({ emphasiseActiveRoutes: true, disableWhenMatched: true }, authMenuKey)}
                              </div>
                            </div>,
                            headerElementRef.current
                          )}
                      </div>
                    )}

                    {/* Support link removed for authenticated client-zone users */}
                    {/* They have Support in the user dropdown menu */}
                  </>
                ) : mounted && !isAuthenticated && !isDesktopApp && !isWhiteLabel ? (
                  /* Public navigation — top-level items + mega menus on lg+;
                     a compact Menu pill with accordions covers md–lg. */
                  <>
                    <div
                      className="relative flex items-center"
                      ref={publicNavRef}
                      onMouseEnter={cancelPublicMenuClose}
                      onMouseLeave={schedulePublicMenuClose}
                    >
                      {/* lg+ top-level nav row */}
                      <div className="hidden lg:flex items-center gap-0.5">
                        <button
                          type="button"
                          className={`${HEADER_PUBLIC_NAV_ITEM_CLASS} ${publicMenu === 'products' ? HEADER_PUBLIC_NAV_ITEM_ACTIVE_CLASS : ''}`}
                          aria-haspopup="menu"
                          aria-expanded={publicMenu === 'products'}
                          onClick={() => togglePublicMenu('products')}
                          onMouseEnter={() => openPublicMenu('products')}
                        >
                          <span>Products</span>
                          <ChevronDown
                            className={`h-3.5 w-3.5 transition-transform ${publicMenu === 'products' ? 'rotate-180' : ''}`}
                          />
                        </button>
                        <button
                          type="button"
                          className={`${HEADER_PUBLIC_NAV_ITEM_CLASS} ${publicMenu === 'industries' ? HEADER_PUBLIC_NAV_ITEM_ACTIVE_CLASS : ''}`}
                          aria-haspopup="menu"
                          aria-expanded={publicMenu === 'industries'}
                          onClick={() => togglePublicMenu('industries')}
                          onMouseEnter={() => openPublicMenu('industries')}
                        >
                          <span>Industries</span>
                          <ChevronDown
                            className={`h-3.5 w-3.5 transition-transform ${publicMenu === 'industries' ? 'rotate-180' : ''}`}
                          />
                        </button>
                        <Link
                          href="/pricing"
                          className={`${HEADER_PUBLIC_NAV_ITEM_CLASS} ${pathname === '/pricing' ? HEADER_PUBLIC_NAV_ITEM_ACTIVE_CLASS : ''}`}
                          onMouseEnter={closePublicMenu}
                        >
                          Pricing
                        </Link>
                        <button
                          type="button"
                          className={`${HEADER_PUBLIC_NAV_ITEM_CLASS} ${publicMenu === 'resources' ? HEADER_PUBLIC_NAV_ITEM_ACTIVE_CLASS : ''}`}
                          aria-haspopup="menu"
                          aria-expanded={publicMenu === 'resources'}
                          onClick={() => togglePublicMenu('resources')}
                          onMouseEnter={() => openPublicMenu('resources')}
                        >
                          <span>Resources</span>
                          <ChevronDown
                            className={`h-3.5 w-3.5 transition-transform ${publicMenu === 'resources' ? 'rotate-180' : ''}`}
                          />
                        </button>
                        <Link
                          href="/about"
                          className={`${HEADER_PUBLIC_NAV_ITEM_CLASS} ${pathname === '/about' ? HEADER_PUBLIC_NAV_ITEM_ACTIVE_CLASS : ''}`}
                          onMouseEnter={closePublicMenu}
                        >
                          About
                        </Link>
                        <Link
                          href="/contact"
                          className={`${HEADER_PUBLIC_NAV_ITEM_CLASS} ${pathname === '/contact' ? HEADER_PUBLIC_NAV_ITEM_ACTIVE_CLASS : ''}`}
                          onMouseEnter={closePublicMenu}
                        >
                          Contact
                        </Link>
                      </div>

                      {/* md–lg compact menu — click-driven accordions inside a small panel */}
                      <div className="lg:hidden">
                        <button
                          type="button"
                          onClick={() => setIsPublicNavOpen((prev) => !prev)}
                          className={`${HEADER_MODULES_TRIGGER_CLASS} w-56 justify-between`}
                          aria-haspopup="menu"
                          aria-expanded={isPublicNavOpen}
                        >
                          <span className="flex items-center gap-2">
                            <i className="fa fa-bars text-[12px]"></i>
                            <span>Menu</span>
                          </span>
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${isPublicNavOpen ? 'rotate-180' : ''}`}
                          />
                        </button>

                        {isPublicNavOpen && (
                          <div
                            className={`${HEADER_PUBLIC_MENU_PANEL_CLASS} max-h-[80vh] overflow-y-auto`}
                            role="menu"
                          >
                            <Link
                              href="/about"
                              className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                              role="menuitem"
                              onClick={() => setIsPublicNavOpen(false)}
                            >
                              <i className={`fa fa-info-circle ${HEADER_MODULES_ICON_CLASS}`}></i>
                              <span>About Us</span>
                            </Link>
                            <button
                              type="button"
                              onClick={() => setCompactNavSection((s) => (s === 'products' ? null : 'products'))}
                              className={`${HEADER_PUBLIC_MENU_ITEM_CLASS} w-full justify-between`}
                              aria-expanded={compactNavSection === 'products'}
                            >
                              <span className="flex items-center gap-2">
                                <i className={`fa fa-box-open ${HEADER_MODULES_ICON_CLASS}`}></i>
                                <span>Products</span>
                              </span>
                              <ChevronDown
                                className={`h-3.5 w-3.5 opacity-60 transition-transform ${compactNavSection === 'products' ? 'rotate-180' : ''}`}
                              />
                            </button>
                            {compactNavSection === 'products' && (
                              <div className="mb-1 ml-4 border-l border-slate-200 pl-2 dark:border-slate-700">
                                <Link
                                  href="/products"
                                  className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                  role="menuitem"
                                  onClick={() => setIsPublicNavOpen(false)}
                                >
                                  <i className={`fa fa-th ${HEADER_MODULES_ICON_CLASS}`}></i>
                                  <span>All Products</span>
                                </Link>
                                {productMenuChildren.map((child) => (
                                  <Link
                                    key={child.href}
                                    href={child.href}
                                    className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                    role="menuitem"
                                    onClick={() => setIsPublicNavOpen(false)}
                                  >
                                    <i className={`fa ${child.icon} ${HEADER_MODULES_ICON_CLASS}`}></i>
                                    <span>{child.label}</span>
                                  </Link>
                                ))}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => setCompactNavSection((s) => (s === 'industries' ? null : 'industries'))}
                              className={`${HEADER_PUBLIC_MENU_ITEM_CLASS} w-full justify-between`}
                              aria-expanded={compactNavSection === 'industries'}
                            >
                              <span className="flex items-center gap-2">
                                <i className={`fa fa-industry ${HEADER_MODULES_ICON_CLASS}`}></i>
                                <span>Industries</span>
                              </span>
                              <ChevronDown
                                className={`h-3.5 w-3.5 opacity-60 transition-transform ${compactNavSection === 'industries' ? 'rotate-180' : ''}`}
                              />
                            </button>
                            {compactNavSection === 'industries' && (
                              <div className="mb-1 ml-4 border-l border-slate-200 pl-2 dark:border-slate-700">
                                <Link
                                  href="/products/industries"
                                  className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                  role="menuitem"
                                  onClick={() => setIsPublicNavOpen(false)}
                                >
                                  <i className={`fa fa-th ${HEADER_MODULES_ICON_CLASS}`}></i>
                                  <span>All Industries</span>
                                </Link>
                                {industryMenuEntries.slice(0, INDUSTRY_MENU_MAX_ITEMS).map((child) => (
                                  <Link
                                    key={child.href}
                                    href={child.href}
                                    className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                    role="menuitem"
                                    onClick={() => setIsPublicNavOpen(false)}
                                  >
                                    {renderIndustryGlyph(child.icon, 'h-3.5 w-3.5 text-[#047cde] dark:text-sky-300')}
                                    <span>{child.label}</span>
                                  </Link>
                                ))}
                                {industryMenuEntries.length > INDUSTRY_MENU_MAX_ITEMS && (
                                  <Link
                                    href="/products/industries"
                                    className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                    role="menuitem"
                                    onClick={() => setIsPublicNavOpen(false)}
                                  >
                                    <i className={`fa fa-arrow-right ${HEADER_MODULES_ICON_CLASS}`}></i>
                                    <span>View all {industryMenuEntries.length} industries</span>
                                  </Link>
                                )}
                              </div>
                            )}
                            <Link
                              href="/pricing"
                              className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                              role="menuitem"
                              onClick={() => setIsPublicNavOpen(false)}
                            >
                              <i className={`fa fa-tag ${HEADER_MODULES_ICON_CLASS}`}></i>
                              <span>Pricing</span>
                            </Link>
                            <Link
                              href="/contact"
                              className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                              role="menuitem"
                              onClick={() => setIsPublicNavOpen(false)}
                            >
                              <i className={`fa fa-envelope ${HEADER_MODULES_ICON_CLASS}`}></i>
                              <span>Contact Us</span>
                            </Link>
                            <button
                              type="button"
                              onClick={() => setCompactNavSection((s) => (s === 'resources' ? null : 'resources'))}
                              className={`${HEADER_PUBLIC_MENU_ITEM_CLASS} w-full justify-between`}
                              aria-expanded={compactNavSection === 'resources'}
                            >
                              <span className="flex items-center gap-2">
                                <i className={`fa fa-book-open ${HEADER_MODULES_ICON_CLASS}`}></i>
                                <span>Resources</span>
                              </span>
                              <ChevronDown
                                className={`h-3.5 w-3.5 opacity-60 transition-transform ${compactNavSection === 'resources' ? 'rotate-180' : ''}`}
                              />
                            </button>
                            {compactNavSection === 'resources' && (
                              <div className="mb-1 ml-4 border-l border-slate-200 pl-2 dark:border-slate-700">
                                <Link
                                  href="/resources"
                                  className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                  role="menuitem"
                                  onClick={() => setIsPublicNavOpen(false)}
                                >
                                  <i className={`fa fa-book-open ${HEADER_MODULES_ICON_CLASS}`}></i>
                                  <span>Resources</span>
                                </Link>
                                <Link
                                  href="/case-studies"
                                  className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                  role="menuitem"
                                  onClick={() => setIsPublicNavOpen(false)}
                                >
                                  <i className={`fa fa-briefcase ${HEADER_MODULES_ICON_CLASS}`}></i>
                                  <span>Case Studies</span>
                                </Link>
                                <Link
                                  href="/support"
                                  className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                  role="menuitem"
                                  onClick={() => setIsPublicNavOpen(false)}
                                >
                                  <i className={`fa fa-life-ring ${HEADER_MODULES_ICON_CLASS}`}></i>
                                  <span>Support</span>
                                </Link>
                                <Link
                                  href="/legal"
                                  className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                  role="menuitem"
                                  onClick={() => setIsPublicNavOpen(false)}
                                >
                                  <i className={`fa fa-balance-scale ${HEADER_MODULES_ICON_CLASS}`}></i>
                                  <span>Legal</span>
                                </Link>
                                {otherSitePageLinks.map((page) => (
                                  <Link
                                    key={page.page_key}
                                    href={page.route}
                                    className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                    role="menuitem"
                                    onClick={() => setIsPublicNavOpen(false)}
                                  >
                                    <i className={`fa fa-file-alt ${HEADER_MODULES_ICON_CLASS}`}></i>
                                    <span>{page.page_name}</span>
                                  </Link>
                                ))}
                                <a
                                  href={process.env.NODE_ENV === 'production' ? 'https://developer.totalaccess.co.za/' : 'https://dev-developer.totalaccess.co.za'}
                                  className={HEADER_PUBLIC_MENU_ITEM_CLASS}
                                  role="menuitem"
                                  onClick={() => setIsPublicNavOpen(false)}
                                >
                                  <i className={`fa fa-code ${HEADER_MODULES_ICON_CLASS}`}></i>
                                  <span>Developer Docs</span>
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Two-pane mega menus — full-width strip under the
                          header (portaled), left rail + right preview pane.
                          Same anatomy for Products, Industries, Resources. */}
                      {publicMenu !== null && headerElementRef.current &&
                        createPortal(
                        <div
                          className={HEADER_PUBLIC_MEGA_PANEL_CLASS}
                          role="menu"
                          data-header-mega-panel="true"
                          onMouseEnter={cancelPublicMenuClose}
                        >
                          <div className="mx-auto flex h-[min(72vh,32rem)] max-w-7xl px-4 lg:px-8">
                            {/* Left rail — scrollable item list, hover previews the right pane */}
                            <div className="w-80 shrink-0 overflow-y-auto border-r border-slate-100 py-3 dark:border-slate-800">
                              <div className="px-4 pb-2">
                                <div className="text-lg font-bold text-slate-900 dark:text-white">
                                  {publicMenu === 'products'
                                    ? 'Products'
                                    : publicMenu === 'industries'
                                      ? 'Industries'
                                      : publicMenu === 'get-started'
                                        ? 'Get Started'
                                        : 'Resources'}
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                  {publicMenu === 'products'
                                    ? `${productNavItems.length} modules on one platform`
                                    : publicMenu === 'industries'
                                      ? `${industryNavItems.length} ready-made solutions`
                                      : publicMenu === 'get-started'
                                        ? 'Choose how you’d like to sign up'
                                        : 'Help, guides and company info'}
                                </div>
                              </div>

                              {publicMenu === 'products' && (
                                <>
                                  <PublicNavRailItem
                                    item={PUBLIC_NAV_META_PREVIEWS['/products']}
                                    active={navPreviewHref === '/products'}
                                    onHover={setNavPreviewHref}
                                    onNavigate={closePublicMenu}
                                  />
                                  {productMenuGroups.map((group) => (
                                    <div key={group.label}>
                                      <div className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                        {group.label}
                                      </div>
                                      {group.items.map((child) => (
                                        <PublicNavRailItem
                                          key={child.href}
                                          item={{ href: child.href, label: child.label, faIcon: child.icon }}
                                          active={navPreviewHref === child.href}
                                          onHover={setNavPreviewHref}
                                          onNavigate={closePublicMenu}
                                        />
                                      ))}
                                    </div>
                                  ))}
                                </>
                              )}

                              {publicMenu === 'industries' && (
                                <>
                                  <PublicNavRailItem
                                    item={PUBLIC_NAV_META_PREVIEWS['/products/industries']}
                                    active={navPreviewHref === '/products/industries'}
                                    onHover={setNavPreviewHref}
                                    onNavigate={closePublicMenu}
                                  />
                                  {industryNavItems.map((item) => (
                                    <PublicNavRailItem
                                      key={item.href}
                                      item={item}
                                      active={navPreviewHref === item.href}
                                      onHover={setNavPreviewHref}
                                      onNavigate={closePublicMenu}
                                    />
                                  ))}
                                  <div className="mt-2 border-t border-slate-100 pt-2 dark:border-slate-800">
                                    <PublicNavRailItem
                                      item={PUBLIC_NAV_META_PREVIEWS['/contact?subject=custom-industry']}
                                      active={navPreviewHref === '/contact?subject=custom-industry'}
                                      onHover={setNavPreviewHref}
                                      onNavigate={closePublicMenu}
                                    />
                                  </div>
                                </>
                              )}

                              {publicMenu === 'resources' && (
                                <>
                                  {resourceNavItems.map((item) => (
                                    <PublicNavRailItem
                                      key={item.href}
                                      item={item}
                                      active={navPreviewHref === item.href}
                                      onHover={setNavPreviewHref}
                                      onNavigate={closePublicMenu}
                                    />
                                  ))}
                                </>
                              )}

                              {publicMenu === 'get-started' && (
                                <>
                                  {GET_STARTED_NAV_ITEMS.map((item) => (
                                    <PublicNavRailItem
                                      key={item.href}
                                      item={item}
                                      active={navPreviewHref === item.href}
                                      onHover={setNavPreviewHref}
                                      onNavigate={closePublicMenu}
                                    />
                                  ))}
                                </>
                              )}
                            </div>

                            {/* Right pane — preview card for the hovered rail item */}
                            <div className="min-w-0 flex-1 p-8">
                              <div className="max-w-2xl">
                                {activeNavPreview ? (
                                  <PublicNavPreview item={activeNavPreview} onNavigate={closePublicMenu} />
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>,
                        headerElementRef.current
                      )}
                    </div>
                  </>
                ) : null}
              </nav>
              )}

              {/* Auth Section */}
              <div className="hidden md:flex items-center space-x-4">
                {mounted && isAuthenticated ? (
                  <div className="flex items-center space-x-2">
                    {/* Desktop header quick actions — My Corner icons inline */}
                    {desktopHeaderQuickActions.map((action) => (
                      <button
                        key={action.key}
                        onClick={action.onClick}
                        className="relative p-1.5 transition-opacity hover:opacity-90"
                        title={action.label}
                        aria-label={action.label}
                      >
                        <span className="flex items-center justify-center">{action.icon}</span>
                        {action.badge > 0 && (
                          <span className={`absolute -top-1 -right-1 ${HEADER_QUICK_ACTION_BADGE_CLASS} text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1`}>
                            {action.badge > 99 ? '99+' : action.badge}
                          </span>
                        )}
                      </button>
                    ))}

                    {/* Notification bell — merged inbox: mentions, approvals, tasks due + notification feed */}
                    {isMdDesktopViewport && user?.role !== 'guest' && hasValidClient && (
                      <NotificationBell
                        buttonClassName="p-1.5"
                        iconClassName={`h-6 w-6 ${HEADER_QUICK_ACTION_ICON_CLASS}`}
                        onNavigate={(link) => router.push(link)}
                        onOpenThread={(threadId) => {
                          setPendingMessagesThreadId(threadId);
                          openModal('messages');
                          setTimeout(() => setPendingMessagesThreadId(null), 2000);
                        }}
                        onOpenManagedTasks={() => openManagedTasksTab('tasks')}
                      />
                    )}

                    <div
                      className="relative"
                      ref={userDropdownRef}
                    >
                      <button
                        type="button"
                        onClick={toggleUserMenu}
                        className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                        aria-haspopup="menu"
                        aria-expanded={isUserMenuOpen}
                        data-tour-id="user-dropdown-trigger"
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <i className="fa fa-user text-sm shrink-0"></i>
                          <span className="truncate min-w-0 text-[15px]">{getUserDisplayName()}</span>
                        </span>
                        <svg
                          className={`h-4 w-4 shrink-0 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {isUserMenuOpen && headerElementRef.current &&
                        createPortal(
                        <div
                          className={HEADER_PUBLIC_MEGA_PANEL_CLASS}
                          role="menu"
                          data-header-mega-panel="true"
                          onMouseEnter={cancelPublicMenuClose}
                        >
                          <div className="mx-auto flex h-[min(72vh,32rem)] max-w-7xl px-4 lg:px-8">
                            <div className="ml-auto flex max-h-[min(72vh,32rem)]">
                              {renderUserMegaInner()}
                            </div>
                          </div>
                        </div>,
                        headerElementRef.current
                      )}
                    </div>

                  </div>
                ) : (
                  <div className="flex items-center space-x-4">
                    {/* Theme/auth CTAs are handled inside the auth shell on auth pages */}
                    {!isAuthPage && <ThemeToggle />}

                    {/* Get Started and Sign In buttons - Hidden on auth pages, desktop mode, and whitelabel */}
                    {!isAuthPage && !isDesktopApp && !isWhiteLabel && (
                      <>
                        <div className="flex items-center gap-3" ref={publicAuthRef}>
                          <Link
                            href="/auth/login"
                            className={HEADER_PUBLIC_NAV_ITEM_CLASS}
                            onMouseEnter={closePublicMenu}
                          >
                            <i className="fa fa-sign-in-alt text-[10px]"></i>
                            <span>Sign In</span>
                          </Link>

                          <button
                            type="button"
                            className={HEADER_PUBLIC_PRIMARY_AUTH_BUTTON_CLASS}
                            aria-haspopup="menu"
                            aria-expanded={publicMenu === 'get-started'}
                            onClick={() => togglePublicMenu('get-started')}
                            onMouseEnter={() => openPublicMenu('get-started')}
                            onMouseLeave={schedulePublicMenuClose}
                          >
                            <i className="fa fa-rocket text-[10px]"></i>
                            <span>Get Started</span>
                          </button>
                        </div>
                      </>
                    )}

                    {/* About Arrow Button - Only show when authenticated */}
                    {mounted && isAuthenticated && (
                      <Link
                        href="/about"
                        className="btn-theme-secondary"
                        title="Go to About"
                      >
                        <i className="fa fa-chevron-right"></i>
                      </Link>
                    )}
                  </div>
                )}
              </div>

              {/* Mobile menu button - Hide on auth pages and developer portal domains */}
              {!isAuthPage && !isDeveloperPortalDomain && (
                <div className="md:hidden">
                  <button
                    onClick={() => {
                      void triggerHeaderHaptic('menuToggle');
                      setIsMenuOpen(!isMenuOpen);
                    }}
                    className="theme-text-primary hover:opacity-80"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      {isMenuOpen ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16"
                        />
                      )}
                    </svg>
                  </button>
                </div>
              )}

              {/* Theme toggle on auth pages (mobile only) */}
              {mounted && isAuthPage && showAppLikeMenuLayout && (
                <div className="md:hidden ml-auto">
                  <ThemeToggle />
                </div>
              )}
            </div>

            {/* Mobile Navigation - Single scrollable list */}
            {isMenuOpen && (
              <div
                ref={mobileMenuRef}
                className="ta-theme-scope md:hidden border-t theme-border overflow-y-auto pb-safe overscroll-contain"
                style={{
                  maxHeight: showAppLikeMenuLayout ? 'calc(100vh - 70px - env(safe-area-inset-bottom, 0px))' : 'calc(100vh - 70px)',
                  minHeight: showAppLikeMenuLayout ? 'calc(100vh - 70px - env(safe-area-inset-bottom, 0px))' : 'auto',
                  height: showAppLikeMenuLayout ? 'calc(100vh - 70px - env(safe-area-inset-bottom, 0px))' : 'auto',
                  paddingBottom: showAppLikeMenuLayout ? 'calc(80px + env(safe-area-inset-bottom, 20px))' : '24px',
                  backgroundColor: 'var(--theme-surface, #1a1a2e)',
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-y'
                }}
              >
                <div className="flex flex-col py-2 px-3">

                  {/* Top Row: Shortcuts for authenticated users, theme for guests */}
                  <div className="flex items-center justify-between py-2 border-b theme-border mb-2">
                    <span className="theme-text-secondary text-sm">{isAuthenticated ? 'Shortcuts' : 'Theme'}</span>
                    <div className="flex items-center gap-3">
                      {/* Search Button */}
                      {isAuthenticated && !isTrainingModeMenuRestricted && (
                        <button
                          onClick={() => {
                            closeMobileMenuWithHaptic();
                            setIsSearchOpen(true);
                          }}
                          className="p-1.5 transition-colors theme-text-secondary hover:theme-primary"
                          title="Search"
                        >
                          <i className="fa fa-search text-lg"></i>
                        </button>
                      )}

                      {/* Theme Toggle for unauthenticated sessions */}
                      {!isAuthenticated && <ThemeToggle />}
                    </div>
                  </div>

                  {mounted && isAuthenticated && isInClientZone && (
                    <>
                      {isTrainingModeMenuRestricted ? (
                        <div className="mb-2 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                          <div className="flex items-start gap-3">
                            <span className="text-xl leading-none">🎯</span>
                            <div>
                              <div className="text-sm font-semibold">Training mode menu</div>
                              <p className="mt-1 text-xs leading-5 text-amber-800/90 dark:text-amber-100/90">
                                Module navigation is hidden on mobile while training mode is active. Exit training mode to return to the full menu.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="px-1 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] theme-text-secondary opacity-70">
                            Navigation
                          </div>

                          {/* Dashboard */}
                          <Link
                            href="/client-zone"
                            className="theme-text-secondary flex items-center py-3"
                            onClick={closeMobileMenuWithHaptic}
                          >
                            <span className="mr-3">🏠</span>
                            Dashboard
                          </Link>

                          {renderMobileModulesAccordion()}
                        </>
                      )}

                      {renderMyCornerSection('mobile')}
                      {renderMyWorkspaceSection('mobile')}
                      {renderSessionAndAppSection('mobile')}

                      {/* Home Link - Hidden on app-like mobile */}
                      {!showAppLikeMenuLayout && (
                        <Link
                          href="/"
                          className="theme-text-secondary flex items-center py-3"
                          onClick={closeMobileMenuWithHaptic}
                        >
                          <span className="mr-3">🌐</span>
                          Go to Website
                        </Link>
                      )}
                    </>
                  )}

                  {mounted && isAuthenticated && !isInClientZone && (
                    <>
                      <div className="px-1 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] theme-text-secondary opacity-70">
                        Navigation
                      </div>

                      <Link href="/client-zone" className="theme-text-secondary flex items-center py-3" onClick={closeMobileMenuWithHaptic}>
                        <span className="mr-3">🏠</span> Dashboard
                      </Link>
                      {renderMobileModulesAccordion()}
                      {renderMyCornerSection('mobile')}
                      {renderMyWorkspaceSection('mobile')}
                      {renderSessionAndAppSection('mobile')}
                    </>
                  )}

                  {mounted && !isAuthenticated && !isWhiteLabel && (
                    <>
                      <Link href="/" className="theme-text-secondary flex items-center py-3" onClick={closeMobileMenuWithHaptic}>
                        <span className="mr-3">🏠</span> Home
                      </Link>
                      <Link href="/about" className="theme-text-secondary flex items-center py-3" onClick={closeMobileMenuWithHaptic}>
                        <span className="mr-3">ℹ️</span> About Us
                      </Link>
                      {/* Products — expandable accordion with children */}
                      <button
                        type="button"
                        onClick={() => setIsMobileProductsOpen((prev) => !prev)}
                        className="theme-text-secondary flex items-center justify-between py-3 w-full text-left"
                        aria-expanded={isMobileProductsOpen}
                      >
                        <span className="flex items-center">
                          <span className="mr-3">📦</span> Products
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isMobileProductsOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isMobileProductsOpen && (
                        <div className="ml-6 mb-1 flex flex-col gap-0.5 border-l border-slate-200 pl-3 dark:border-slate-700">
                          <Link
                            href="/products"
                            className="theme-text-secondary flex items-center py-2 text-sm"
                            onClick={closeMobileMenuWithHaptic}
                          >
                            <span className="mr-3">🗂️</span> All Products
                          </Link>
                          {productMenuGroups.map((group) => (
                            <div key={group.label}>
                              <div className="pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                {group.label}
                              </div>
                              {group.items.map((child) => (
                                <Link
                                  key={child.href}
                                  href={child.href}
                                  className="theme-text-secondary flex items-center py-2 text-sm"
                                  onClick={closeMobileMenuWithHaptic}
                                >
                                  <i className={`fa ${child.icon} mr-3 text-[12px] text-[#047cde] dark:text-sky-300`}></i>
                                  {child.label}
                                </Link>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Industries — expandable accordion fed by the public solutions catalog */}
                      <button
                        type="button"
                        onClick={() => setIsMobileIndustriesOpen((prev) => !prev)}
                        className="theme-text-secondary flex items-center justify-between py-3 w-full text-left"
                        aria-expanded={isMobileIndustriesOpen}
                      >
                        <span className="flex items-center">
                          <span className="mr-3">🏭</span> Industries
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isMobileIndustriesOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isMobileIndustriesOpen && (
                        <div className="ml-6 mb-1 flex flex-col gap-0.5 border-l border-slate-200 pl-3 dark:border-slate-700">
                          <Link
                            href="/products/industries"
                            className="theme-text-secondary flex items-center py-2 text-sm"
                            onClick={closeMobileMenuWithHaptic}
                          >
                            <i className={`fa fa-industry mr-3 text-[12px] text-[#047cde] dark:text-sky-300`}></i>
                            All Industries
                          </Link>
                          {industryMenuEntries.slice(0, INDUSTRY_MENU_MAX_ITEMS).map((child) => (
                            <Link
                              key={child.href}
                              href={child.href}
                              className="theme-text-secondary flex items-center py-2 text-sm"
                              onClick={closeMobileMenuWithHaptic}
                            >
                              {renderIndustryGlyph(child.icon, 'mr-3 h-3.5 w-3.5 text-[#047cde] dark:text-sky-300')}
                              {child.label}
                            </Link>
                          ))}
                          {industryMenuEntries.length > INDUSTRY_MENU_MAX_ITEMS && (
                            <Link
                              href="/products/industries"
                              className="theme-text-secondary flex items-center py-2 text-sm"
                              onClick={closeMobileMenuWithHaptic}
                            >
                              <i className={`fa fa-arrow-right mr-3 text-[12px] text-[#047cde] dark:text-sky-300`}></i>
                              View all {industryMenuEntries.length} industries
                            </Link>
                          )}
                        </div>
                      )}
                      <Link href="/contact" className="theme-text-secondary flex items-center py-3" onClick={closeMobileMenuWithHaptic}>
                        <span className="mr-3">✉️</span> Contact Us
                      </Link>
                      <Link href="/pricing" className="theme-text-secondary flex items-center py-3" onClick={closeMobileMenuWithHaptic}>
                        <span className="mr-3">🏷️</span> Pricing
                      </Link>
                      {/* Resources — expandable accordion */}
                      <button
                        type="button"
                        onClick={() => setIsMobileResourcesOpen((prev) => !prev)}
                        className="theme-text-secondary flex items-center justify-between py-3 w-full text-left"
                        aria-expanded={isMobileResourcesOpen}
                      >
                        <span className="flex items-center">
                          <span className="mr-3">📚</span> Resources
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isMobileResourcesOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isMobileResourcesOpen && (
                        <div className="ml-6 mb-1 flex flex-col gap-0.5 border-l border-slate-200 pl-3 dark:border-slate-700">
                          <Link
                            href="/resources"
                            className="theme-text-secondary flex items-center py-2 text-sm"
                            onClick={closeMobileMenuWithHaptic}
                          >
                            <i className={`fa fa-book-open mr-3 text-[12px] text-[#047cde] dark:text-sky-300`}></i>
                            Resources
                          </Link>
                          <Link
                            href="/case-studies"
                            className="theme-text-secondary flex items-center py-2 text-sm"
                            onClick={closeMobileMenuWithHaptic}
                          >
                            <i className={`fa fa-briefcase mr-3 text-[12px] text-[#047cde] dark:text-sky-300`}></i>
                            Case Studies
                          </Link>
                          <Link
                            href="/support"
                            className="theme-text-secondary flex items-center py-2 text-sm"
                            onClick={closeMobileMenuWithHaptic}
                          >
                            <i className={`fa fa-life-ring mr-3 text-[12px] text-[#047cde] dark:text-sky-300`}></i>
                            Support
                          </Link>
                          <Link
                            href="/legal"
                            className="theme-text-secondary flex items-center py-2 text-sm"
                            onClick={closeMobileMenuWithHaptic}
                          >
                            <i className={`fa fa-balance-scale mr-3 text-[12px] text-[#047cde] dark:text-sky-300`}></i>
                            Legal
                          </Link>
                          {otherSitePageLinks.map((page) => (
                            <Link
                              key={page.page_key}
                              href={page.route}
                              className="theme-text-secondary flex items-center py-2 text-sm"
                              onClick={closeMobileMenuWithHaptic}
                            >
                              <i className={`fa fa-file-alt mr-3 text-[12px] text-[#047cde] dark:text-sky-300`}></i>
                              {page.page_name}
                            </Link>
                          ))}
                          <a
                            href={process.env.NODE_ENV === 'production' ? 'https://developer.totalaccess.co.za/' : 'https://dev-developer.totalaccess.co.za'}
                            className="theme-text-secondary flex items-center py-2 text-sm"
                            onClick={closeMobileMenuWithHaptic}
                          >
                            <i className={`fa fa-code mr-3 text-[12px] text-[#047cde] dark:text-sky-300`}></i>
                            Developer Docs
                          </a>
                        </div>
                      )}
                      <div className="border-b theme-border my-2"></div>
                      <Link
                        href="/auth/signup-options"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-transparent bg-gradient-to-r from-[#047cde] to-sky-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:opacity-90 hover:shadow-md dark:from-sky-500 dark:to-[#047cde]"
                        onClick={closeMobileMenuWithHaptic}
                      >
                        <i className="fa fa-rocket mr-2 text-[10px]"></i> Get Started
                      </Link>
                      <Link
                        href="/auth/login"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#047cde]/45 bg-white px-4 py-2.5 text-sm font-semibold text-[#047cde] shadow-sm transition-colors hover:bg-[#047cde] hover:text-white dark:border-sky-400/35 dark:bg-slate-950/85 dark:text-sky-300 dark:hover:bg-[#047cde] dark:hover:text-white"
                        onClick={closeMobileMenuWithHaptic}
                      >
                        <i className="fa fa-sign-in-alt mr-2 text-[10px]"></i> Sign In
                      </Link>
                    </>
                  )}

                </div>
              </div>
            )}
          </div>

          {desktopHeaderToggleEnabled && !publicMenu && !isModulesOpen && !isUserMenuOpen && (
            <>
              {(['left-3', 'right-3'] as const).map((positionClass) => (
                <button
                  key={positionClass}
                  type="button"
                  onClick={() => setShowHeader(false)}
                  className={[
                    'hidden lg:flex absolute bottom-0 z-[81] translate-y-[45%]',
                    positionClass,
                    SHELL_TOGGLE_BUTTON_CLASS,
                  ].join(' ')}
                  title="Hide header"
                  aria-label="Hide header"
                >
                  <ChevronUp className={SHELL_TOGGLE_ICON_CLASS} />
                </button>
              ))}
            </>
          )}
        </header>
      )}

      {/* Favourites Bar - Bookmark-style bar below header */}
      {!hideHeaderForCapacitorLogin && !desktopHeaderHidden && !(showAppLikeMenuLayout && immersedClientZoneModule) && mounted && isAuthenticated && showHeader && (
        <FavouritesBar />
      )}

      {!hideHeaderForCapacitorLogin && desktopHeaderToggleEnabled && !showHeader && (
        <>
          {(['left-3', 'right-3'] as const).map((positionClass) => (
            <button
              key={positionClass}
              type="button"
              onClick={() => setShowHeader(true)}
              className={[
                'hidden lg:flex fixed top-0 z-[81]',
                positionClass,
                SHELL_TOGGLE_REVEAL_BUTTON_CLASS,
              ].join(' ')}
              title="Show header"
              aria-label="Show header"
            >
              <ChevronDown className={SHELL_TOGGLE_ICON_CLASS} />
            </button>
          ))}
        </>
      )}

      {demoActionLoading && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/70 px-6 backdrop-blur-sm">
          <BrandedLoadingSpinner
            size="lg"
            className="w-full max-w-md"
            primaryColor={demoActionLoading === 'enter' ? '#60a5fa' : '#34d399'}
            message={demoActionLoading === 'enter' ? 'Starting Training Mode' : 'Leaving Training Mode'}
            subMessage={demoActionLoading === 'enter'
              ? trainingWorkspaceOwnerName
                ? `Setting up a dedicated sandbox training workspace for ${trainingWorkspaceOwnerName}.`
                : 'Setting up a dedicated sandbox training workspace for you.'
              : 'Restoring your regular workspace and settings.'}
            progress={demoActionLoading === 'enter' ? (demoLoadingProgressActive ? 100 : 0) : null}
            progressTransitionDurationMs={TRAINING_MODE_ENTER_MIN_LOADING_MS}
            showProgressBar={demoActionLoading === 'enter'}
          />
        </div>
      )}

      {/* Global Search Bar - Only appears when user is in a module */}
      {mounted && isSearchOpen && (isWhiteLabel || isAuthenticated || isDesktopApp || showAppLikeMenuLayout) && (
        <div ref={searchBarRef} className="theme-surface shadow-md border-b-4" style={{
          position: 'relative',
          zIndex: 1000,
          borderBottomColor: whitelabelHeaderColor || 'var(--theme-primary)'
        }}>
          <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
            {/* Module Context Badge + Scope Toggle */}
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              {currentModule && (
                <>
                  <span className="text-xs theme-text-secondary">Searching in:</span>
                  <button
                    onClick={() => setSearchScope(searchScope === 'module' ? 'all' : 'module')}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      searchScope === 'module'
                        ? 'bg-blue-500/10 text-blue-500 dark:bg-blue-400/10 dark:text-blue-400'
                        : 'bg-teal-500/10 text-teal-600 dark:bg-teal-400/10 dark:text-teal-400'
                    }`}
                  >
                    <i className={`fa ${searchScope === 'module' ? 'fa-filter' : 'fa-globe'} text-[10px]`}></i>
                    {searchScope === 'module' ? currentModule.label : 'All Modules'}
                  </button>
                </>
              )}
              {!currentModule && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-500/10 text-teal-600 dark:bg-teal-400/10 dark:text-teal-400">
                  <i className="fa fa-globe text-[10px]"></i>
                  All Modules
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={currentModule && searchScope === 'module' ? `Search in ${currentModule.label}...` : 'Search documents, suppliers, transactions, tasks...'}
                  className="w-full px-4 py-3 md:pl-12 pr-4 rounded-lg theme-surface theme-border border focus:outline-none focus:ring-2 focus:ring-blue-500/50 theme-text-primary text-base"
                  autoFocus
                />
                <i className={`fa ${isSearching ? 'fa-spinner fa-spin' : 'fa-search'} hidden md:block absolute left-4 top-1/2 transform -translate-y-1/2 theme-text-secondary text-lg pointer-events-none`}></i>
              </div>
              <button
                onClick={() => {
                  setIsSearchOpen(false);
                  setSearchQuery('');
                  setSearchResults([]);
                  setSearchError(null);
                  setSearchTypeFilter(null);
                }}
                className="flex-shrink-0 px-4 py-3 rounded-lg theme-text-secondary hover:theme-primary transition-colors"
                title="Close search"
              >
                <i className="fa fa-times text-xl"></i>
              </button>
            </div>

            {/* Search Results */}
            {searchQuery.trim().length >= 2 && (
              <div className="mt-3">
                {/* Type Filter Chips */}
                {searchResults.length > 0 && (() => {
                  const availableTypes = [...new Set(searchResults.map(r => r.type))];
                  if (availableTypes.length <= 1) return null;
                  return (
                    <div className="mb-2 flex items-center gap-1.5 overflow-x-auto pb-1">
                      <button
                        type="button"
                        onClick={() => setSearchTypeFilter(null)}
                        className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                          searchTypeFilter === null
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                        }`}
                      >
                        All ({searchResults.length})
                      </button>
                      {availableTypes.map((type) => {
                        const count = searchResults.filter(r => r.type === type).length;
                        const active = searchTypeFilter === type;
                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setSearchTypeFilter(active ? null : type)}
                            className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                              active
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                            }`}
                          >
                            {getResultTypeLabel(type)} ({count})
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
                {searchError ? (
                  <div className="p-3 rounded-lg theme-surface-dark">
                    <p className="theme-text-secondary text-xs">
                      <i className="fa fa-exclamation-circle mr-2"></i>
                      {searchError}
                    </p>
                  </div>
                ) : (searchTypeFilter ? searchResults.filter(r => r.type === searchTypeFilter) : searchResults).length > 0 ? (
                  <div className="rounded-lg theme-surface-dark max-h-[70vh] overflow-y-auto">
                    <div className="p-1.5">
                      <p className="text-[10px] uppercase tracking-wide theme-text-secondary px-2 py-1.5 font-semibold">
                        {(searchTypeFilter ? searchResults.filter(r => r.type === searchTypeFilter) : searchResults).length} result{(searchTypeFilter ? searchResults.filter(r => r.type === searchTypeFilter) : searchResults).length !== 1 ? 's' : ''}{currentModule && searchScope === 'module' ? ` in ${currentModule.label}` : ''}
                      </p>
                      {(searchTypeFilter ? searchResults.filter(r => r.type === searchTypeFilter) : searchResults).map((result, index) => (
                        <button
                          key={`${result.type}-${result.id}-${index}`}
                          onClick={() => handleResultClick(result)}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all group border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
                        >
                          <div className="flex items-start gap-3">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                              <i className={`fa ${getResultIcon(result)} text-blue-600 dark:text-blue-400 text-sm`}></i>
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              {/* Title and type badge */}
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold theme-text-primary text-sm truncate">
                                  {result.title}
                                </h4>
                                <span className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${getModuleBadgeColor(result.module)}`}>
                                  {result.module.replace('total-', '')}
                                </span>
                              </div>

                              {/* Description with details */}
                              <div className="flex items-center gap-2 text-xs theme-text-secondary flex-wrap">
                                {result.subtitle && (
                                  <span className="font-medium">{result.subtitle}</span>
                                )}
                                {result.description && (
                                  <span className="text-xs theme-text-secondary">{result.description}</span>
                                )}
                              </div>

                              {/* Additional metadata */}
                              {result.metadata && (
                                <div className="flex items-center gap-2 mt-1 text-[11px] theme-text-tertiary">
                                  {result.metadata.status != null && (
                                    <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 capitalize">
                                      {String(result.metadata.status)}
                                    </span>
                                  )}
                                  {result.metadata.item_type != null && (
                                    <span className="capitalize">{String(result.metadata.item_type)}</span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Arrow */}
                            <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity mt-1">
                              <i className="fa fa-chevron-right theme-text-secondary text-xs"></i>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : isSearching ? (
                  <div className="p-4 rounded-lg theme-surface-dark text-center">
                    <i className="fa fa-spinner fa-spin theme-text-secondary text-2xl mb-2"></i>
                    <p className="theme-text-secondary text-sm">Searching...</p>
                  </div>
                ) : null}
              </div>
            )}

            {/* Search Tips */}
            {searchQuery.trim().length < 2 && searchQuery.trim().length > 0 && (
              <div className="mt-3 p-3 rounded-lg theme-surface-dark">
                <p className="theme-text-secondary text-xs">
                  <i className="fa fa-info-circle mr-2"></i>
                  Type at least 2 characters to search
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Platform feedback modal — mounted outside user/mobile menus so opening it is not lost on menu unmount */}
      {mounted && isAuthenticated && (
        <SupportModal ref={supportModalRef} variant="portal-only" />
      )}

      <CapacitorOTAUpdateDialog
        isOpen={showAppVersionDialog}
        updateInfo={updateInfo}
        progress={progress}
        currentVersion={currentVersion}
        isChecking={isChecking}
        onDownload={downloadAndInstall}
        onSkip={handleSkipAppVersion}
        onDismiss={() => setShowAppVersionDialog(false)}
        onCheckAgain={() => {
          void openAppVersionDialog(true);
        }}
      />

      {/* Client Selector Modal */}
      <ClientSelector
        isOpen={isClientSelectorOpen}
        onClose={() => setIsClientSelectorOpen(false)}
        onClientSwitch={handleClientSwitch}
      />

      {/* Managed Tasks Modal */}
      <ManagedTasksModal
        isOpen={activeModal === 'tasks'}
        onClose={handleManagedTasksModalClose}
        hasTotalConnect={hasConnectAccess}
        initialMainTab={managedTasksInitialTab}
        onTabChange={(tab) => {
          if (tab === 'tasks') return;
          toggleModal('tasks');
          setTimeout(() => {
            if (tab === 'calls') {
              setIsCallModalOpen(true);
            } else if (tab === 'dashboard') {
              router.push('/client-zone/dashboard');
            } else if (tab === 'email') {
              openEmailExperience();
            } else {
              toggleModal(tab);
            }
          }, 50);
        }}
      />

      {/* My Shell modal (root only — gated inside MyShellModal too) */}
      <MyShellModal
        isOpen={activeModal === 'myshell'}
        onClose={() => toggleModal('myshell')}
      />

      {/* Messages Modal */}
      <MessagesModal
        isOpen={activeModal === 'messages'}
        onClose={closeMessagesModal}
        onShowDashboard={() => returnToDashboardFromConnect('messages')}
        isFullPage={isCapacitor()}
        initialThreadId={pendingMessagesThreadId || undefined}
        initialDirectUserId={pendingMessagesDirectUserId || undefined}
        pendingSharedContent={pendingSharedContent}
        onSharedContentHandled={() => setPendingSharedContent(null)}
      />

      {/* Email — unified: mobile/Capacitor modal + desktop md+ side slider */}
      <EmailMobileModal
        isOpen={
          isDesktopEmailSlideOpen ||
          (activeModal === 'email' && emailExperienceUsesMobileModal)
        }
        layoutVariant={isDesktopEmailSlideOpen ? 'desktop-slide' : 'mobile-modal'}
        onClose={closeEmailModal}
        onShowDashboard={() => returnToDashboardFromConnect('email')}
        initialEmailId={pendingEmailId || undefined}
      />

      {/* Calendar Modal */}
      <CalendarModal
        isOpen={activeModal === 'calendar'}
        onClose={closeCalendarModal}
        onShowDashboard={() => returnToDashboardFromConnect('calendar')}
      />

      {/* Unified Assistant Modal */}
      <UnifiedAssistantModal
        isOpen={activeModal === 'assistant'}
        onClose={() => toggleModal('assistant')}
        demoSession={demoSession?.is_demo_mode ? demoSession : null}
      />

      <MasterSyncModal
        controller={masterSyncModal}
        accentColor="#047cde"
        title="Prepare Offline"
        errorSubtitle="Some offline components still need attention"
      />

      <DiagnosticModal
        isOpen={showDiagnosticModal}
        onClose={() => setShowDiagnosticModal(false)}
      />

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        initialTab={profileInitialTab}
      />

      <IamConsoleModal
        isOpen={showIamModal}
        onClose={() => setShowIamModal(false)}
        navRequest={iamNavRequest}
      />

      <EmployeeSelfServiceModal
        isOpen={showEssModal}
        onClose={() => setShowEssModal(false)}
        initialTab={essInitialTab}
        initialSection={essInitialSection}
      />

      <ClearCacheModal
        isOpen={showClearCacheModal}
        onClose={() => setShowClearCacheModal(false)}
      />

      <MyDevicesModal
        isOpen={showMyDevicesModal}
        onClose={() => setShowMyDevicesModal(false)}
        onOpenPlayStore={() => { }}
        onOpenPwaInstall={() => { }}
        onOpenAppGallery={() => { window.location.href = '/appgallery'; }}
        isStandalonePwaInstalled={installedPwa}
        userAppId={user?.app_id}
        loginHint={user?.email}
        installContext={installContext}
      />



      {/* TotalConnectRTC - Voice/Video calling with lifecycle integration
          - Always render/mount so WebSocket can receive incoming calls
          - Any user can RECEIVE calls even without hasOperationsAccess.
          - Omitted in the standalone Total Connect desktop window — that page mounts one instance. */}
      {!isTotalConnectStandaloneWindow ? (
        <TotalConnectRTC
          isOpen={isCallModalOpen}
          onClose={closeCallsModal}
          onShowDashboard={() => returnToDashboardFromConnect('calls')}
          incomingCall={incomingOperationsCall}
          pendingCallTarget={pendingCallTarget}
          pendingGroupCall={pendingGroupCall}
          onPendingCallHandled={() => setPendingCallTarget(null)}
          onPendingGroupCallHandled={() => setPendingGroupCall(null)}
          onMissedCallsChange={setMissedCallsCount}
          onIncomingCall={(call) => {
            console.log('[Header] Incoming call from WebSocket, opening modal:', call);
            setIncomingOperationsCall(call);
            setIsCallModalOpen(true);
          }}
          onClearIncomingCall={() => {
            console.log('[Header] Clearing incoming call state');
            setIncomingOperationsCall(null);
          }}
          onEnsureCallModalOpen={ensureCallModalOpenForColdStart}
        />
      ) : null}
    </>
  );
}
